import m from 'mithril';
import { createLanguage, alt, string, optWhitespace, regexp, seqMap, seq, whitespace } from 'parsimmon';

/**
 * Copyright 2013-2019  GenieACS Inc.
 *
 * This file is part of GenieACS.
 *
 * GenieACS is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * GenieACS is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with GenieACS.  If not, see <http://www.gnu.org/licenses/>.
 */
const component = () => {
    return {
        view: vnode => {
            const active = { [vnode.attrs["page"]]: "active" };
            const tabs = [];
            if (window.authorizer.hasAccess("devices", 1)) {
                tabs.push(m("li", { class: active["overview"] }, m("a", { href: "#!/overview" }, "Overview")));
            }
            if (window.authorizer.hasAccess("devices", 2)) {
                tabs.push(m("li", { class: active["devices"] }, m("a", { href: "#!/devices" }, "Devices")));
            }
            if (window.authorizer.hasAccess("faults", 2)) {
                tabs.push(m("li", { class: active["faults"] }, m("a", { href: "#!/faults" }, "Faults")));
            }
            if (window.authorizer.hasAccess("map", 2)) {
                tabs.push(m("li", { class: active["map"] }, m("a", { href: "#!/map" }, "Map")));
            }
            const adminPages = [
                "presets",
                "provisions",
                "virtualParameters",
                "files"
            ];
            for (const page of adminPages) {
                if (window.authorizer.hasAccess(page, 2)) {
                    tabs.push(m("li", { class: active["admin"] }, m("a", { href: "#!/admin" }, "Admin")));
                    break;
                }
            }
            return m("nav", m("ul", tabs));
        }
    };
};

/**
 * Copyright 2013-2019  GenieACS Inc.
 *
 * This file is part of GenieACS.
 *
 * GenieACS is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * GenieACS is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with GenieACS.  If not, see <http://www.gnu.org/licenses/>.
 */
// Turn escaped characters into real ones (e.g. "\\n" becomes "\n").
function interpretEscapes(str) {
    const escapes = {
        b: "\b",
        f: "\f",
        n: "\n",
        r: "\r",
        t: "\t"
    };
    return str.replace(/\\(u[0-9a-fA-F]{4}|[^u])/, (_, escape) => {
        const type = escape.charAt(0);
        const hex = escape.slice(1);
        if (type === "u")
            return String.fromCharCode(parseInt(hex, 16));
        if (escapes.hasOwnProperty(type))
            return escapes[type];
        return type;
    });
}
function map(exp, callback) {
    if (!Array.isArray(exp))
        return callback(exp);
    let clone;
    for (let i = 1; i < exp.length; ++i) {
        const sub = map(exp[i], callback);
        if (sub !== exp[i]) {
            clone = clone || exp.slice();
            clone[i] = sub;
        }
    }
    return callback(clone || exp);
}
function binaryLeft(operatorsParser, nextParser) {
    return seqMap(nextParser, seq(operatorsParser, nextParser).many(), (first, rest) => rest.reduce((acc, ch) => {
        const [op, another] = ch;
        if (Array.isArray(acc) && op === acc[0])
            return acc.concat([another]);
        if (Array.isArray(another) && op === another[0])
            return [op, acc].concat(another.slice(1));
        return [op, acc, another];
    }, first));
}
const lang = createLanguage({
    ComparisonOperator: function () {
        return alt(string(">="), string("<>"), string("<="), string("="), string(">"), string("<"))
            .skip(optWhitespace);
    },
    LikeOperator: function () {
        return alt(regexp(/like/i)
            .result("LIKE")
            .desc("LIKE"), regexp(/not\s+like/i)
            .result("NOT LIKE")
            .desc("NOT LIKE"))
            .notFollowedBy(regexp(/[a-zA-Z0-9_]/))
            .skip(optWhitespace);
    },
    IsNullOperator: function () {
        return alt(regexp(/is\s+null/i)
            .result("IS NULL")
            .desc("IS NULL"), regexp(/is\s+not\s+null/i)
            .result("IS NOT NULL")
            .desc("IS NOT NULL"))
            .notFollowedBy(regexp(/[a-zA-Z0-9_]/))
            .skip(optWhitespace);
    },
    NotOperator: function () {
        return regexp(/not/i)
            .result("NOT")
            .notFollowedBy(regexp(/[a-zA-Z0-9_]/))
            .skip(optWhitespace)
            .desc("NOT");
    },
    AndOperator: function () {
        return regexp(/and/i)
            .result("AND")
            .notFollowedBy(regexp(/[a-zA-Z0-9_]/))
            .skip(optWhitespace)
            .desc("AND");
    },
    OrOperator: function () {
        return regexp(/or/i)
            .result("OR")
            .notFollowedBy(regexp(/[a-zA-Z0-9_]/))
            .skip(optWhitespace)
            .desc("OR");
    },
    Parameter: function (r) {
        return alt(regexp(/[a-zA-Z0-9_.*-]+/), r.Expression.wrap(string("{").skip(optWhitespace), string("}")))
            .atLeast(1)
            .map(x => ["PARAM", x.length > 1 ? ["||"].concat(x) : x[0]])
            .skip(optWhitespace)
            .desc("parameter");
    },
    StringValueSql: function () {
        return regexp(/'([^']*)'/, 1)
            .atLeast(1)
            .skip(optWhitespace)
            .map(s => s.join("'"))
            .desc("string");
    },
    StringValueJs: function () {
        return regexp(/"((?:\\.|.)*?)"/, 1)
            .skip(optWhitespace)
            .map(interpretEscapes)
            .desc("string");
    },
    NumberValue: function () {
        return regexp(/-?(0|[1-9][0-9]*)([.][0-9]+)?([eE][+-]?[0-9]+)?/)
            .skip(optWhitespace)
            .map(Number)
            .desc("number");
    },
    BooleanValue: function () {
        return alt(regexp(/true/i)
            .result(true)
            .desc("TRUE"), regexp(/false/i)
            .result(false)
            .desc("FALSE"))
            .notFollowedBy(regexp(/[a-zA-Z0-9_]/))
            .skip(optWhitespace);
    },
    NullValue: function () {
        return regexp(/null/i)
            .notFollowedBy(regexp(/[a-zA-Z0-9_]/))
            .skip(optWhitespace)
            .result(null)
            .desc("NULL");
    },
    FuncValue: function (r) {
        return seqMap(regexp(/([a-zA-Z0-9_]+)/, 1)
            .skip(optWhitespace)
            .desc("function"), r.ValueExpression.sepBy(string(",").skip(optWhitespace)).wrap(string("(").skip(optWhitespace), string(")").skip(optWhitespace)), (f, args) => ["FUNC", f.toUpperCase()].concat(args));
    },
    Value: function (r) {
        return alt(r.NullValue, r.BooleanValue, r.NumberValue, r.StringValueSql, r.StringValueJs, r.FuncValue);
    },
    ValueExpression: function (r) {
        return binaryLeft(string("||").skip(optWhitespace), binaryLeft(alt(string("+"), string("-"))
            .skip(optWhitespace), binaryLeft(alt(string("*"), string("/"))
            .skip(optWhitespace), alt(r.Value, r.Parameter, r.Expression.wrap(string("(").skip(optWhitespace), string(")").skip(optWhitespace))))));
    },
    Comparison: function (r) {
        return alt(seqMap(r.ValueExpression, r.IsNullOperator, (p, o) => [o, p]), seqMap(r.ValueExpression, r.ComparisonOperator, r.ValueExpression, (p, o, v) => [o, p, v]), seqMap(r.ValueExpression, r.LikeOperator, r.ValueExpression.skip(regexp(/escape/i)
            .result("ESCAPE")
            .skip(whitespace)
            .desc("ESCAPE")), r.ValueExpression, (a, b, c, d) => [b, a, c, d]), seqMap(r.ValueExpression, r.LikeOperator, r.ValueExpression, (a, b, c) => [b, a, c]));
    },
    Expression: function (r) {
        function unary(operatorsParser, nextParser) {
            return seq(operatorsParser, nextParser).or(nextParser);
        }
        return binaryLeft(r.OrOperator, binaryLeft(r.AndOperator, unary(r.NotOperator, r.Comparison.or(r.ValueExpression)))).trim(optWhitespace);
    }
});
function parse(str) {
    if (!str)
        return null;
    return lang.Expression.tryParse(str);
}
function stringify(exp, level = 0) {
    if (!Array.isArray(exp))
        return JSON.stringify(exp);
    const opLevels = {
        OR: 10,
        AND: 11,
        NOT: 12,
        "=": 20,
        "<>": 20,
        ">": 20,
        ">=": 20,
        "<": 20,
        "<=": 20,
        LIKE: 20,
        "NOT LIKE": 20,
        "IS NULL": 20,
        "IS NOT NULL": 20,
        "||": 30,
        "+": 31,
        "-": 31,
        "*": 32,
        "/": 32
    };
    const op = exp[0].toUpperCase();
    function wrap(e) {
        if (opLevels[op] <= level)
            return `(${e})`;
        else
            return e;
    }
    if (op === "FUNC") {
        return wrap(`${exp[1]}(${exp
            .slice(2)
            .map(e => stringify(e))
            .join(", ")})`);
    }
    else if (op === "PARAM") {
        if (typeof exp[1] === "string") {
            return wrap(exp[1]);
        }
        else if (Array.isArray(exp[1]) && exp[1][0] === "||") {
            return wrap(exp[1]
                .slice(1)
                .map(p => {
                if (typeof p === "string")
                    return p;
                else
                    return `{${stringify(p)}}`;
            })
                .join(""));
        }
        else {
            return wrap(`{${stringify(exp[1])}}`);
        }
    }
    else if (op === "IS NULL" || op === "IS NOT NULL") {
        return wrap(`${stringify(exp[1], opLevels[op])} ${op}`);
    }
    else if (op === "LIKE" || op === "NOT LIKE") {
        if (exp[3]) {
            return wrap(`${stringify(exp[1], opLevels[op])} ${op} ${stringify(exp[2], opLevels[op])} ESCAPE ${stringify(exp[3], opLevels[op])}`);
        }
        else {
            return wrap(`${stringify(exp[1], opLevels[op])} ${op} ${stringify(exp[2], opLevels[op])}`);
        }
    }
    else if (op in opLevels) {
        const parts = exp.slice(1).map((e, i) => {
            return stringify(e, opLevels[exp[0]] + Math.min(i - 1, 0));
        });
        if (op === "NOT")
            return wrap(`${op} ${parts[0]}`);
        else
            return wrap(parts.join(` ${op} `));
    }
    else {
        throw new Error(`Unrecognized operator ${exp[0]}`);
    }
}
function parseLikePattern(pat, esc) {
    const chars = pat.split("");
    for (let i = 0; i < chars.length; ++i) {
        const c = chars[i];
        if (c === esc) {
            chars[i] = chars[i + 1] || "";
            chars[i + 1] = "";
        }
        else if (c === "_") {
            chars[i] = "\\_";
        }
        else if (c === "%") {
            chars[i] = "\\%";
            while (chars[i + 1] === "%")
                chars[++i] = "";
        }
    }
    return chars.filter(c => c);
}

/**
 * Copyright 2013-2019  GenieACS Inc.
 *
 * This file is part of GenieACS.
 *
 * GenieACS is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * GenieACS is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with GenieACS.  If not, see <http://www.gnu.org/licenses/>.
 */
function or(cnf1, cnf2) {
    if (cnf1.length === 0)
        return cnf2;
    else if (cnf2.length === 0)
        return cnf1;
    if (cnf1.length > cnf2.length) {
        const c = cnf1;
        cnf1 = cnf2;
        cnf2 = c;
    }
    const res = [];
    for (let i = 0; i < cnf1.length; ++i)
        for (let j = i; j < cnf2.length; ++j)
            res.push(cnf1[i].concat(cnf2[j]));
    return res;
}
function likePatternIncludes(pat1, pat2, idx1 = 0, idx2 = 0) {
    while (idx1 < pat1.length && idx2 < pat2.length) {
        if (pat1[idx1] === "\\%") {
            for (let i = idx2; i <= pat2.length; ++i)
                if (likePatternIncludes(pat1, pat2, idx1 + 1, i))
                    return true;
            return false;
        }
        else if (pat2[idx2] === "\\%") {
            if (pat1[idx1] !== "\\%")
                return false;
        }
        else if (pat1[idx1] === "\\_") ;
        else if (pat2[idx2] === "\\_") {
            return false;
        }
        else if (pat2[idx2] !== pat1[idx1]) {
            return false;
        }
        ++idx1;
        ++idx2;
    }
    if (idx1 === pat1.length && idx2 === pat2.length)
        return true;
    return false;
}
function booleanCnf(exp) {
    const ranges = new Map();
    const likePatterns = new Map();
    map(exp, e => {
        if (!Array.isArray(e))
            return e;
        if (e[0] === "LIKE" || e[0] === "NOT LIKE") {
            if (typeof e[2] === "string" &&
                (e.length >= 4 || (e[3] != null && !Array.isArray(e[3])))) {
                const key = JSON.stringify(e[1]);
                let s = likePatterns.get(key);
                if (!s)
                    likePatterns.set(key, (s = new Map()));
                s.set(`${e[3] || ""}:${e[2]}`, parseLikePattern(e[2], e[3]));
            }
        }
        else if ([">", ">=", "<", "<=", "=", "<>"].includes(e[0])) {
            if (!Array.isArray(e[2])) {
                const key = `${JSON.stringify(e[1])}:${typeof e[2]}`;
                let s = ranges.get(key);
                if (!s)
                    ranges.set(key, (s = new Set()));
                s.add(e[2]);
            }
            else if (!Array.isArray(e[1])) {
                const key = `${JSON.stringify(e[2])}:${typeof e[1]}`;
                let s = ranges.get(key);
                if (!s)
                    ranges.set(key, (s = new Set()));
                s.add(e[2]);
            }
        }
        return e;
    });
    for (const [k, v] of ranges) {
        if (k.endsWith(":number")) {
            ranges.set(k, Array.from(v).sort((a, b) => a - b));
        }
        else {
            ranges.set(k, Array.from(v).sort());
        }
    }
    const expressions = new Map();
    const mutuallyExclusive = new Map();
    function getVariable(lhs, op, rhs = "") {
        const key = `${lhs}:${op}:${rhs}`;
        let f = expressions.get(key);
        if (!f) {
            expressions.set(key, (f = expressions.size + 1));
            let m = mutuallyExclusive.get(lhs);
            if (!m)
                mutuallyExclusive.set(lhs, (m = new Set()));
            m.add(f);
            if (rhs || op !== "null")
                m.add(getVariable(lhs, "null"));
        }
        return f;
    }
    function getComparisonVariables(lhs, from, fromInclusive, to, toInclusive) {
        const type = from != null ? typeof from : typeof to;
        const range = ranges.get(`${lhs}:${type}`);
        const vars = [];
        if (from == null)
            vars.push(`${lhs}:--${range[0]}`);
        for (let i = 0; i < range.length; ++i) {
            if (i > 0 &&
                (from == null || range[i] > from) &&
                (to == null || range[i] <= to))
                vars.push(`${lhs}:${range[i - 1]}--${range[i]}`);
            if ((range[i] > from && range[i] < to) ||
                (range[i] === from && fromInclusive) ||
                (range[i] === to && toInclusive))
                vars.push(`${lhs}:${range[i]}`);
        }
        if (to == null)
            vars.push(`${lhs}:${range[range.length - 1]}--`);
        return vars.map(key => {
            let f = expressions.get(key);
            if (!f) {
                expressions.set(key, (f = expressions.size + 1));
                let m = mutuallyExclusive.get(lhs);
                if (!m)
                    mutuallyExclusive.set(lhs, (m = new Set()));
                m.add(f);
            }
            return f;
        });
    }
    function getLikeVariables(lhs, pat, esc) {
        const vars = [];
        const pats = likePatterns.get(lhs);
        pat = pats.get(`${esc || ""}:${pat}`);
        for (const p of pats.values()) {
            if (pat === p || likePatternIncludes(pat, p))
                vars.push(`${lhs}:like:${JSON.stringify(p)}`);
        }
        return vars.map(key => {
            let f = expressions.get(key);
            if (!f)
                expressions.set(key, (f = expressions.size + 1));
            return f;
        });
    }
    const comparisonOperators = {
        "=": v => [v, true, v, true],
        ">": v => [v, false, null, null],
        ">=": v => [v, true, null, null],
        "<": v => [null, null, v, false],
        "<=": v => [null, null, v, true]
    };
    function recursive(clause, negate) {
        let op = clause[0];
        if ((op === "AND" && !negate) || (op === "OR" && negate)) {
            let res = [];
            for (let i = 1; i < clause.length; ++i)
                res = res.concat(recursive(clause[i], negate));
            return res;
        }
        else if ((op === "OR" && !negate) || (op === "AND" && negate)) {
            let res = [];
            for (let i = 1; i < clause.length; ++i)
                res = or(res, recursive(clause[i], negate));
            return res;
        }
        else if (op === "NOT") {
            return recursive(clause[1], !negate);
        }
        else if (op === "<>") {
            const vars = getComparisonVariables(JSON.stringify(clause[1]), ...comparisonOperators["="](clause[2]));
            if (!negate)
                return vars.map(x => [0 - x]);
            else
                return [vars];
        }
        else if (op === "IS NULL") {
            const v = getVariable(JSON.stringify(clause[1]), "null");
            if (negate)
                return [[0 - v]];
            else
                return [[v]];
        }
        else if (op === "IS NOT NULL") {
            const v = getVariable(JSON.stringify(clause[1]), "null");
            if (!negate)
                return [[0 - v]];
            else
                return [[v]];
        }
        else if (op in comparisonOperators) {
            if (!Array.isArray(clause[2])) {
                const vars = getComparisonVariables(JSON.stringify(clause[1]), ...comparisonOperators[op](clause[2]));
                if (negate)
                    return vars.map(x => [0 - x]);
                else
                    return [vars];
            }
            else if (!Array.isArray(clause[1])) {
                op = { ">": "<", ">=": "<=", "<": ">", "<=": ">=" }[op] || op;
                const vars = getComparisonVariables(JSON.stringify(clause[2]), ...comparisonOperators[op](clause[1]));
                if (negate)
                    return vars.map(x => [0 - x]);
                else
                    return [vars];
            }
            else {
                let lhs = JSON.stringify(clause[1]);
                let rhs = JSON.stringify(clause[2]);
                // For consistency
                if (rhs > lhs) {
                    const t = rhs;
                    rhs = lhs;
                    lhs = t;
                    op = { ">": "<", ">=": "<=", "<": ">", "<=": ">=" }[op] || op;
                }
                const v = getVariable(lhs, op, rhs);
                if (negate)
                    return [[0 - v]];
                else
                    return [[v]];
            }
        }
        else if ((op === "LIKE" || op === "NOT LIKE") &&
            !Array.isArray(clause[2]) &&
            clause[2] != null &&
            (clause.length >= 4 || clause[3] != null)) {
            if (op === "NOT LIKE")
                negate = !negate;
            const vars = getLikeVariables(JSON.stringify(clause[1]), clause[2], clause[3]);
            if (negate)
                return vars.map(x => [0 - x]);
            else
                return [vars];
        }
        else {
            const v = getVariable(JSON.stringify(clause), "");
            if (negate)
                return [[0 - v]];
            else
                return [[v]];
        }
    }
    const cnf = recursive(exp, false);
    for (const m of mutuallyExclusive.values()) {
        const ar = Array.from(m);
        for (let i = 0; i < ar.length; ++i)
            for (let j = i + 1; j < ar.length; ++j)
                cnf.push([0 - ar[i], 0 - ar[j]]);
    }
    return { vars: expressions.size, clauses: cnf };
}

/**
 * Naive DPLL solver for propositional logic adapted from
 * https://github.com/tammet/logictools
 *
 * Origianl code by Tanel Tammet released under the MIT license.
 *
 * Check whether a clause set is satisfiable or contradictory, using a naive
 * implementation of the dpll (Davis-Putnam-Logemann-Loveland) procedure
 * combining truth table search with the resolution-like unit propagation.
 *
 * The standard pure-literal rule is not implemented in this naive version,
 * since it is not really crucial for the algorithm.
 *
 * The code is very similar to the pure table solver, except we use a
 * unit-propagating, derived-varlist-returing satisfiableAt.
 */
/* eslint-disable @typescript-eslint/no-use-before-define */
function satisfiableAt(clauses, varVals, varnr, val) {
    if (varnr !== 0)
        varVals[varnr] = val;
    const propRes = unitPropagate(clauses, varVals);
    if (propRes === 1) {
        varVals[varnr] = 0;
        return true;
    }
    // if result false (-1) unitPropagate restores the varVals state itself
    if (propRes === -1) {
        varVals[varnr] = 0;
        return false;
    }
    // find next unassigned var to split
    let nextVar = 0;
    for (let i = 1; i < varVals.length; i++) {
        if (varVals[i] === 0) {
            nextVar = i;
            break;
        }
    }
    if (nextVar === 0)
        throw new Error("Error in satisfiableAt");
    if (satisfiableAt(clauses, varVals, nextVar, 1) ||
        satisfiableAt(clauses, varVals, nextVar, -1))
        // not necessary to restore split var: search is halted anyway
        return true;
    // restore split var
    varVals[nextVar] = 0;
    // restore derived vars
    for (let i = 0; i < propRes.length; i++)
        varVals[propRes[i]] = 0;
    return false;
}
/**
 * Check whether clauses are satisfiable under the varVals assignment, without
 * search.
 *
 * Iterate through all clauses and check whether
 *
 *  - all literals in the clause are assigned false: undo derived units (set
 *    values in varVals to 0) and return false immediately
 *
 *  - a clause contains a literal assigned true: skip further actions on this
 *    clause
 *
 *  - a single literal in the clause is unassigned, all the rest are assigned
 *    false: derive this literal as a unit clause.
 *
 * In case at least one literal is derived during the process above, iterate
 * the whole process until no more new literals are derived.
 *
 * Returns:
 *
 *   1: all clauses were assigned true, hence the clause set is satisfiable.
 *   The whole search will terminate after that.
 *
 *   -1: clause set is contradictory. Derived units have been restored.
 *
 *   a list of derived variables:  the status of the clause set is
 *   undetermined. The values of variables on the list will be restored (set to
 *   0) during backtracking.
 */
function unitPropagate(clauses, varVals) {
    // all vars with a value derived in the forthcoming main loop
    const derivedVars = [];
    // the main loop is run, deriving new unit clauses, until either:
    // - the clause set is detected to be unsatisfiable
    // - the clause set is detected to be satisfiable
    // - there are no more derived unit clauses
    for (;;) {
        let allClausesFound = true; // set to false if we find an undetermined clause
        const derivedQueue = []; // derived units (literals) during one iteration
        for (let i = 0; i < clauses.length; i++) {
            const clause = clauses[i];
            let clauseVal = 0;
            let unassignedCount = 0; // count unassigned vars in clause
            let unassignedLit = 0; // 0 means none found for this clause
            for (let j = 0; j < clause.length; j++) {
                const lit = clause[j];
                let nr, polarity;
                if (lit < 0) {
                    nr = 0 - lit;
                    polarity = -1;
                }
                else {
                    nr = lit;
                    polarity = 1;
                }
                if (varVals[nr] === polarity) {
                    clauseVal = 1;
                    break;
                }
                else if (varVals[nr] === 0) {
                    unassignedCount++;
                    unassignedLit = lit;
                }
            }
            if (clauseVal === 1)
                continue; // clause is subsumed
            // clause is not subsumed by varVals
            if (unassignedCount === 0) {
                for (let j = 0; j < derivedVars.length; j++)
                    varVals[derivedVars[j]] = 0; // restore derived
                return -1;
            }
            else if (unassignedCount === 1) {
                // unassignedLit is a derived literal
                derivedQueue.push(unassignedLit);
            }
            if (unassignedCount !== 0)
                allClausesFound = false;
        }
        // if all clauses were subsumed or all the variables in a clause had a value
        // no need to restore varVals: whole search finished
        if (allClausesFound)
            return 1;
        // in case there are any derived units present, store to varVals and
        // iterate the main loop
        if (derivedQueue.length === 0)
            break;
        // some units were derived: iterate the main loop
        for (let j = 0; j < derivedQueue.length; j++) {
            const lit = derivedQueue[j];
            let nr, polarity;
            if (lit < 0) {
                nr = 0 - lit;
                polarity = -1;
            }
            else {
                nr = lit;
                polarity = 1;
            }
            varVals[nr] = polarity;
            derivedVars.push(nr);
        }
    }
    return derivedVars;
}
function naiveDpll(clauses, maxvarnr) {
    // variable values are 0 if not set, 1 if positive, -1 if negative
    const varVals = new Int32Array(maxvarnr + 1);
    return satisfiableAt(clauses, varVals, 0, 0);
}

/**
 * Copyright 2013-2019  GenieACS Inc.
 *
 * This file is part of GenieACS.
 *
 * GenieACS is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * GenieACS is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with GenieACS.  If not, see <http://www.gnu.org/licenses/>.
 */
const isArray = Array.isArray;
const regExpCache = new WeakMap();
const REDUCE_SKIP = {};
function reduce(exp, callback) {
    let loop = true;
    while (loop) {
        loop = false;
        for (let i = 2; i < exp.length; ++i) {
            const res = callback(exp[i - 1], exp[i], i - 2);
            if (res !== REDUCE_SKIP) {
                loop = true;
                exp = exp.slice();
                exp.splice(i - 1, 2, res);
            }
        }
    }
    if (exp.length === 2)
        return exp[1];
    return exp;
}
function likePatternToRegExp(pat, esc = "", flags = "") {
    const convChars = {
        "-": "\\-",
        "/": "\\/",
        "\\": "\\/",
        "^": "\\^",
        $: "\\$",
        "*": "\\*",
        "+": "\\+",
        "?": "\\?",
        ".": "\\.",
        "(": "\\(",
        ")": "\\)",
        "|": "\\|",
        "[": "\\[",
        "]": "\\]",
        "{": "\\{",
        "}": "\\}",
        "\\%": ".*",
        "\\_": "."
    };
    let chars = parseLikePattern(pat, esc);
    if (!chars.length)
        return new RegExp("^$", flags);
    chars = chars.map(c => convChars[c] || c);
    chars[0] = chars[0] === ".*" ? "" : "^" + chars[0];
    const l = chars.length - 1;
    chars[l] = [".*", ""].includes(chars[l]) ? "" : chars[l] + "$";
    return new RegExp(chars.join(""), flags);
}
function evalExp(e) {
    if (e[0] === "AND") {
        return reduce(e, (a, b) => {
            if (!isArray(a))
                return a ? b : a;
            if (!isArray(b))
                return b ? a : b;
            return REDUCE_SKIP;
        });
    }
    else if (e[0] === "OR") {
        return reduce(e, (a, b) => {
            if (!isArray(a))
                return a ? a : b;
            if (!isArray(b))
                return b ? b : a;
            return REDUCE_SKIP;
        });
    }
    else if (e[0] === "NOT") {
        if (!isArray(e[1]))
            return !e[1];
        else if (e[1][0] === "NOT")
            return e[1][1];
    }
    else if (e[0] === "IS NULL") {
        if (isArray(e[1]))
            return e;
        else if (e[1] == null)
            return true;
        else
            return null;
    }
    else if (e[0] === "IS NOT NULL") {
        if (isArray(e[1]))
            return e;
        else if (e[1] != null)
            return true;
        else
            return null;
    }
    else if (e[0] === "LIKE") {
        if (isArray(e[1]) || isArray(e[2]) || isArray(e[3]))
            return e;
        else if (e[1] == null ||
            e[2] == null ||
            (e.length >= 4 && e[3] == null))
            return null;
        let r = regExpCache.get(e);
        if (!r) {
            r = likePatternToRegExp(e[2], e[3]);
            regExpCache.set(e, r);
        }
        return r.test(e[1]);
    }
    else if (e[0] === "NOT LIKE") {
        if (isArray(e[1]) || isArray(e[2]) || isArray(e[3]))
            return e;
        else if (e[1] == null ||
            e[2] == null ||
            (e.length >= 4 && e[3] == null))
            return null;
        let r = regExpCache.get(e);
        if (!r) {
            r = likePatternToRegExp(e[2], e[3]);
            regExpCache.set(e, r);
        }
        return !r.test(e[1]);
    }
    else if (e[0] === "=") {
        if (isArray(e[1]) || isArray(e[2]))
            return e;
        if (e[1] == null || e[2] == null)
            return null;
        return e[1] === e[2];
    }
    else if (e[0] === "<>") {
        if (isArray(e[1]) || isArray(e[2]))
            return e;
        if (e[1] == null || e[2] == null)
            return null;
        return e[1] !== e[2];
    }
    else if (e[0] === ">") {
        if (isArray(e[1]) || isArray(e[2]))
            return e;
        if (e[1] == null || e[2] == null)
            return null;
        return e[1] > e[2];
    }
    else if (e[0] === ">=") {
        if (isArray(e[1]) || isArray(e[2]))
            return e;
        if (e[1] == null || e[2] == null)
            return null;
        return e[1] >= e[2];
    }
    else if (e[0] === "<") {
        if (isArray(e[1]) || isArray(e[2]))
            return e;
        if (e[1] == null || e[2] == null)
            return null;
        return e[1] < e[2];
    }
    else if (e[0] === "<=") {
        if (isArray(e[1]) || isArray(e[2]))
            return e;
        if (e[1] == null || e[2] == null)
            return null;
        return e[1] <= e[2];
    }
    else if (e[0] === "*") {
        return reduce(e, (a, b) => {
            if (!isArray(a) && !isArray(b)) {
                if (a == null || b == null)
                    return null;
                return a * b;
            }
            return REDUCE_SKIP;
        });
    }
    else if (e[0] === "/") {
        return reduce(e, (a, b, i) => {
            if (!isArray(a) && !isArray(b)) {
                if (a == null || b == null)
                    return null;
                return i === 0 ? a / b : a * b;
            }
            return REDUCE_SKIP;
        });
    }
    else if (e[0] === "+") {
        return reduce(e, (a, b) => {
            if (!isArray(a) && !isArray(b)) {
                if (a == null || b == null)
                    return null;
                return a + b;
            }
            return REDUCE_SKIP;
        });
    }
    else if (e[0] === "-") {
        return reduce(e, (a, b, i) => {
            if (!isArray(a) && !isArray(b)) {
                if (a == null || b == null)
                    return null;
                return i === 0 ? a - b : a + b;
            }
            return REDUCE_SKIP;
        });
    }
    else if (e[0] === "||") {
        return reduce(e, (a, b) => {
            if (!isArray(a) && !isArray(b)) {
                if (a == null || b == null)
                    return null;
                return `${a}${b}`;
            }
            return REDUCE_SKIP;
        });
    }
    return e;
}
function evaluate(exp, obj, now, cb) {
    return map(exp, e => {
        if (cb)
            e = cb(e);
        if (!isArray(e))
            return e;
        if (e[0] === "FUNC") {
            if (e[1] === "NOW") {
                if (now)
                    return now;
            }
            else if (e[1] === "UPPER") {
                if (e[2] == null)
                    return null;
                if (!isArray(e[2]))
                    return `${e[2]}`.toUpperCase();
            }
            else if (e[1] === "LOWER") {
                if (e[2] == null)
                    return null;
                if (!isArray(e[2]))
                    return `${e[2]}`.toLowerCase();
            }
        }
        else if (e[0] === "PARAM") {
            if (e[1] == null)
                return null;
            if (obj && !isArray(e[1])) {
                let v;
                if (typeof obj === "function")
                    v = obj(e[1]);
                else
                    v = obj[e[1]];
                if (v == null)
                    return null;
                if (typeof v === "object")
                    v = v.value ? v.value[0] : null;
                return v;
            }
        }
        return evalExp(e);
    });
}
function and(exp1, exp2) {
    if (!isArray(exp1))
        return exp1 ? exp2 : exp1;
    if (!isArray(exp2))
        return exp2 ? exp1 : exp2;
    let res = ["AND"];
    if (exp1[0] === "AND")
        res = res.concat(exp1.slice(1));
    else
        res.push(exp1);
    if (exp2[0] === "AND")
        res = res.concat(exp2.slice(1));
    else
        res.push(exp2);
    return res;
}
function or$1(exp1, exp2) {
    if (!isArray(exp1))
        return exp1 ? exp1 : exp2;
    if (!isArray(exp2))
        return exp2 ? exp2 : exp1;
    let res = ["OR"];
    if (exp1[0] === "OR")
        res = res.concat(exp1.slice(1));
    else
        res.push(exp1);
    if (exp2[0] === "OR")
        res = res.concat(exp2.slice(1));
    else
        res.push(exp2);
    return res;
}
function not(exp) {
    if (isArray(exp) && exp[0] === "NOT")
        return exp[1];
    return ["NOT", exp];
}
function subset(exp1, exp2) {
    const e = evaluate(["NOT", ["OR", ["NOT", exp1], exp2]]);
    if (!isArray(e))
        return !e;
    const { vars, clauses } = booleanCnf(e);
    return !naiveDpll(clauses, vars);
}
function extractParams(exp) {
    const params = new Set();
    map(exp, e => {
        if (isArray(e) && e[0] === "PARAM")
            params.add(e[1]);
        return e;
    });
    return Array.from(params);
}

/**
 * Copyright 2013-2019  GenieACS Inc.
 *
 * This file is part of GenieACS.
 *
 * GenieACS is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * GenieACS is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with GenieACS.  If not, see <http://www.gnu.org/licenses/>.
 */
let cache1 = new Map();
let cache2 = new Map();
const keys = new WeakMap();
function getKey(obj) {
    if (obj === null)
        return "null";
    else if (obj === undefined)
        return "undefined";
    const t = typeof obj;
    if (t === "number" || t === "boolean" || t === "string")
        return `${t}:${obj}`;
    if (t !== "function" && t !== "object")
        throw new Error(`Cannot memoize ${t} arguments`);
    let k = keys.get(obj);
    if (!k) {
        const rnd = Math.trunc(Math.random() * Number.MAX_SAFE_INTEGER);
        k = `${t}:${rnd.toString(36)}`;
        keys.set(obj, k);
    }
    return k;
}
function memoize(func) {
    const funcKey = getKey(func);
    return ((...args) => {
        const key = JSON.stringify(args.map(getKey)) + funcKey;
        if (cache1.has(key))
            return cache1.get(key);
        let r;
        if (cache2.has(key))
            r = cache2.get(key);
        else
            r = func(...args);
        cache1.set(key, r);
        return r;
    });
}
const interval = setInterval(() => {
    cache2 = cache1;
    cache1 = new Map();
}, 120000);
// Don't hold Node.js process
if (interval.unref)
    interval.unref();

/**
 * Copyright 2013-2019  GenieACS Inc.
 *
 * This file is part of GenieACS.
 *
 * GenieACS is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * GenieACS is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with GenieACS.  If not, see <http://www.gnu.org/licenses/>.
 */
const notifications = new Set();
function push(type, message, actions) {
    const n = {
        type: type,
        message: message,
        timestamp: Date.now(),
        actions: actions
    };
    notifications.add(n);
    m.redraw();
    if (!actions) {
        setTimeout(() => {
            dismiss(n);
        }, 4000);
    }
    return n;
}
function dismiss(n) {
    notifications.delete(n);
    m.redraw();
}
function getNotifications() {
    return notifications;
}

/**
 * Copyright 2013-2019  GenieACS Inc.
 *
 * This file is part of GenieACS.
 *
 * GenieACS is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * GenieACS is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with GenieACS.  If not, see <http://www.gnu.org/licenses/>.
 */
const config = window.clientConfig;
const configSnapshot = window.configSnapshot;

/**
 * Copyright 2013-2019  GenieACS Inc.
 *
 * This file is part of GenieACS.
 *
 * GenieACS is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * GenieACS is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with GenieACS.  If not, see <http://www.gnu.org/licenses/>.
 */
const memoizedStringify = memoize(stringify);
const memoizedEvaluate = memoize(evaluate);
let fulfillTimestamp = 0;
let refreshNotification;
const queries = {
    filter: new WeakMap(),
    bookmark: new WeakMap(),
    limit: new WeakMap(),
    sort: new WeakMap(),
    fulfilled: new WeakMap(),
    fulfilling: new WeakSet(),
    accessed: new WeakMap(),
    value: new WeakMap()
};
const resources = {};
for (const r of [
    "devices",
    "faults",
    "files",
    "presets",
    "provisions",
    "virtualParameters",
    "files",
    "config",
    "users",
    "permissions"
]) {
    resources[r] = {
        objects: new Map(),
        count: new Map(),
        fetch: new Map(),
        combinedFilter: null
    };
}
class QueryResponse {
    get fulfilled() {
        queries.accessed.set(this, Date.now());
        return !!queries.fulfilled.get(this);
    }
    get fulfilling() {
        queries.accessed.set(this, Date.now());
        return queries.fulfilling.has(this);
    }
    get value() {
        queries.accessed.set(this, Date.now());
        return queries.value.get(this);
    }
}
async function xhrRequest(options) {
    const extract = options.extract;
    const deserialize = options.deserialize;
    options.extract = (xhr, _options) => {
        if (!refreshNotification &&
            configSnapshot !== xhr.getResponseHeader("x-config-snapshot")) {
            refreshNotification = push("warning", "Configuration has been modified, please reload the page", {
                Reload: () => {
                    window.location.reload();
                }
            });
        }
        if (typeof extract === "function")
            return extract(xhr, _options);
        let response;
        if (typeof deserialize === "function") {
            response = deserialize(xhr.responseText);
        }
        else {
            try {
                response = xhr.responseText ? JSON.parse(xhr.responseText) : null;
            }
            catch (err) {
                throw new Error("Invalid JSON: " + response);
            }
        }
        // https://mithril.js.org/request.html#error-handling
        if (xhr.status !== 304 && Math.floor(xhr.status / 100) !== 2) {
            const err = new Error();
            err["message"] = xhr.responseText;
            err["code"] = xhr.status;
            err["response"] = response;
            throw err;
        }
        return response;
    };
    return m.request(options);
}
function unpackExpression(exp) {
    if (!Array.isArray(exp))
        return exp;
    const e = memoizedEvaluate(exp, null, fulfillTimestamp);
    return e;
}
function count(resourceType, filter) {
    const filterStr = memoizedStringify(filter);
    let queryResponse = resources[resourceType].count.get(filterStr);
    if (queryResponse)
        return queryResponse;
    queryResponse = new QueryResponse();
    resources[resourceType].count.set(filterStr, queryResponse);
    queries.filter.set(queryResponse, filter);
    return queryResponse;
}
function limitFilter(filter, sort, bookmark) {
    const sortSort = (a, b) => Math.abs(b[1]) - Math.abs(a[1]);
    const arr = Object.entries(sort)
        .sort(sortSort)
        .reverse();
    return and(filter, arr.reduce((cur, kv) => {
        const [param, asc] = kv;
        if (asc <= 0) {
            if (bookmark[param] == null) {
                return or$1(["IS NOT NULL", ["PARAM", param]], and(["IS NULL", ["PARAM", param]], cur));
            }
            let f = null;
            if (typeof bookmark[param] !== "string")
                f = or$1(f, [">=", ["PARAM", param], ""]);
            f = or$1(f, [">", ["PARAM", param], bookmark[param]]);
            return or$1(f, and(["=", ["PARAM", param], bookmark[param]], cur));
        }
        else {
            let f = ["IS NULL", ["PARAM", param]];
            if (bookmark[param] == null)
                return and(f, cur);
            if (typeof bookmark[param] !== "number") {
                f = or$1(f, [">=", ["PARAM", param], 0]);
                f = or$1(f, ["<", ["PARAM", param], 0]);
            }
            f = or$1(f, ["<", ["PARAM", param], bookmark[param]]);
            return or$1(f, and(["=", ["PARAM", param], bookmark[param]], cur));
        }
    }, true));
}
function compareFunction(sort) {
    const sortEntries = Object.entries(sort).sort((a, b) => Math.abs(b[1]) - Math.abs(a[1]));
    return (a, b) => {
        for (const [param, asc] of sortEntries) {
            let v1 = a[param];
            let v2 = b[param];
            if (v1 != null && typeof v1 === "object") {
                if (v1.value)
                    v1 = v1.value[0];
                else
                    v1 = null;
            }
            if (v2 != null && typeof v2 === "object") {
                if (v2.value)
                    v2 = v2.value[0];
                else
                    v2 = null;
            }
            if (v1 > v2) {
                return asc;
            }
            else if (v1 < v2) {
                return asc * -1;
            }
            else if (v1 !== v2) {
                const w = {
                    null: 1,
                    number: 2,
                    string: 3
                };
                const w1 = w[v1 == null ? "null" : typeof v1] || 4;
                const w2 = w[v2 == null ? "null" : typeof v2] || 4;
                return Math.max(-1, Math.min(1, w1 - w2)) * asc;
            }
        }
        return 0;
    };
}
function findMatches(resourceType, filter, sort, limit) {
    // Handle "tag =" and "tag <>" special cases
    if (resourceType === "devices") {
        filter = map(filter, e => {
            if (Array.isArray(e) &&
                Array.isArray(e[1]) &&
                e[1][0] === "PARAM" &&
                e[1][1] === "tag") {
                if (e[0] === "=")
                    return ["IS NOT NULL", ["PARAM", `Tags.${e[2]}`]];
                else if (e[0] === "<>")
                    return ["IS NULL", ["PARAM", `Tags.${e[2]}`]];
            }
            return e;
        });
    }
    let value = [];
    for (const obj of resources[resourceType].objects.values())
        if (evaluate(filter, obj, fulfillTimestamp))
            value.push(obj);
    value = value.sort(compareFunction(sort));
    if (limit)
        value = value.slice(0, limit);
    return value;
}
function inferQuery(resourceType, queryResponse) {
    const limit = queries.limit.get(queryResponse);
    let filter = queries.filter.get(queryResponse);
    filter = unpackExpression(filter);
    const bookmark = queries.bookmark.get(queryResponse);
    const sort = queries.sort.get(queryResponse);
    if (bookmark || !limit) {
        if (bookmark)
            filter = limitFilter(filter, sort, bookmark);
        if (resources[resourceType].combinedFilter &&
            subset(filter, resources[resourceType].combinedFilter))
            queries.fulfilled.set(queryResponse, fulfillTimestamp);
    }
    queries.value.set(queryResponse, findMatches(resourceType, filter, sort, limit));
}
function fetch(resourceType, filter, options = {}) {
    const filterStr = memoizedStringify(filter);
    const sort = Object.assign({}, options.sort);
    for (const [k, v] of Object.entries(sort))
        sort[k] += Math.sign(v);
    const limit = options.limit || 0;
    if (resourceType === "devices")
        sort["DeviceID.ID"] = sort["DeviceID.ID"] || 1;
    else
        sort["_id"] = sort["_id"] || 1;
    const key = `${filterStr}:${limit}:${JSON.stringify(sort)}`;
    let queryResponse = resources[resourceType].fetch.get(key);
    if (queryResponse)
        return queryResponse;
    queryResponse = new QueryResponse();
    resources[resourceType].fetch.set(key, queryResponse);
    queries.filter.set(queryResponse, filter);
    queries.limit.set(queryResponse, limit);
    queries.sort.set(queryResponse, sort);
    inferQuery(resourceType, queryResponse);
    return queryResponse;
}
function fulfill(accessTimestamp, _fulfillTimestamp) {
    let updated = false;
    if (_fulfillTimestamp > fulfillTimestamp) {
        for (const resource of Object.values(resources))
            resource.combinedFilter = null;
        fulfillTimestamp = _fulfillTimestamp;
    }
    const allPromises = [];
    for (const [resourceType, resource] of Object.entries(resources)) {
        for (const [queryResponseKey, queryResponse] of resource.count) {
            if (!(queries.accessed.get(queryResponse) >= accessTimestamp)) {
                resource.count.delete(queryResponseKey);
                continue;
            }
            if (queries.fulfilling.has(queryResponse))
                continue;
            if (!(fulfillTimestamp <= queries.fulfilled.get(queryResponse))) {
                queries.fulfilling.add(queryResponse);
                allPromises.push(new Promise((resolve, reject) => {
                    updated = true;
                    let filter = queries.filter.get(queryResponse);
                    filter = unpackExpression(filter);
                    xhrRequest({
                        method: "HEAD",
                        url: `/api/${resourceType}/?` +
                            m.buildQueryString({
                                filter: memoizedStringify(filter)
                            }),
                        extract: xhr => +xhr.getResponseHeader("x-total-count"),
                        background: true
                    })
                        .then(c => {
                        queries.value.set(queryResponse, c);
                        queries.fulfilled.set(queryResponse, fulfillTimestamp);
                        queries.fulfilling.delete(queryResponse);
                        resolve();
                    })
                        .catch(err => reject(err));
                }));
            }
        }
    }
    const toFetchAll = {};
    for (const [resourceType, resource] of Object.entries(resources)) {
        for (const [queryResponseKey, queryResponse] of resource.fetch) {
            if (!(queries.accessed.get(queryResponse) >= accessTimestamp)) {
                resource.fetch.delete(queryResponseKey);
                continue;
            }
            if (queries.fulfilling.has(queryResponse))
                continue;
            if (!(fulfillTimestamp <= queries.fulfilled.get(queryResponse))) {
                queries.fulfilling.add(queryResponse);
                toFetchAll[resourceType] = toFetchAll[resourceType] || [];
                toFetchAll[resourceType].push(queryResponse);
                const limit = queries.limit.get(queryResponse);
                const sort = queries.sort.get(queryResponse);
                if (limit) {
                    allPromises.push(new Promise((resolve, reject) => {
                        updated = true;
                        let filter = queries.filter.get(queryResponse);
                        filter = unpackExpression(filter);
                        xhrRequest({
                            method: "GET",
                            url: `/api/${resourceType}/?` +
                                m.buildQueryString({
                                    filter: memoizedStringify(filter),
                                    limit: 1,
                                    skip: limit - 1,
                                    sort: JSON.stringify(sort),
                                    projection: Object.keys(sort).join(",")
                                })
                        })
                            .then(res => {
                            if (res.length) {
                                // Generate bookmark object
                                const bm = Object.keys(sort).reduce((b, k) => {
                                    if (res[0][k] != null) {
                                        if (typeof res[0][k] === "object") {
                                            if (res[0][k].value != null)
                                                b[k] = res[0][k].value[0];
                                        }
                                        else {
                                            b[k] = res[0][k];
                                        }
                                    }
                                    return b;
                                }, {});
                                queries.bookmark.set(queryResponse, bm);
                            }
                            else {
                                queries.bookmark.delete(queryResponse);
                            }
                            resolve();
                        })
                            .catch(reject);
                    }));
                }
            }
        }
    }
    return new Promise((resolve, reject) => {
        Promise.all(allPromises)
            .then(() => {
            const allPromises2 = [];
            for (let [resourceType, toFetch] of Object.entries(toFetchAll)) {
                let combinedFilter = null;
                toFetch = toFetch.filter(queryResponse => {
                    let filter = queries.filter.get(queryResponse);
                    filter = unpackExpression(filter);
                    const limit = queries.limit.get(queryResponse);
                    const bookmark = queries.bookmark.get(queryResponse);
                    const sort = queries.sort.get(queryResponse);
                    if (bookmark)
                        filter = limitFilter(filter, sort, bookmark);
                    if (resources[resourceType].combinedFilter &&
                        subset(filter, resources[resourceType].combinedFilter)) {
                        queries.value.set(queryResponse, findMatches(resourceType, filter, sort, limit));
                        queries.fulfilled.set(queryResponse, fulfillTimestamp);
                        queries.fulfilling.delete(queryResponse);
                        return false;
                    }
                    combinedFilter = or$1(combinedFilter, filter);
                    return true;
                });
                if (combinedFilter == null)
                    continue;
                updated = true;
                let deleted = new Set();
                if (!resources[resourceType].combinedFilter)
                    deleted = new Set(resources[resourceType].objects.keys());
                let combinedFilterDiff = combinedFilter;
                if (resources[resourceType].combinedFilter) {
                    combinedFilterDiff = and(combinedFilterDiff, not(resources[resourceType].combinedFilter));
                }
                resources[resourceType].combinedFilter = or$1(combinedFilter, resources[resourceType].combinedFilter);
                allPromises2.push(new Promise((resolve2, reject2) => {
                    xhrRequest({
                        method: "GET",
                        url: `/api/${resourceType}/?` +
                            m.buildQueryString({
                                filter: memoizedStringify(combinedFilterDiff)
                            })
                    })
                        .then(res => {
                        for (const r of res) {
                            const id = resourceType === "devices"
                                ? r["DeviceID.ID"].value[0]
                                : r["_id"];
                            resources[resourceType].objects.set(id, r);
                            deleted.delete(id);
                        }
                        for (const d of deleted) {
                            const obj = resources[resourceType].objects.get(d);
                            if (evaluate(combinedFilterDiff, obj, fulfillTimestamp))
                                resources[resourceType].objects.delete(d);
                        }
                        for (const queryResponse of toFetch) {
                            let filter = queries.filter.get(queryResponse);
                            filter = unpackExpression(filter);
                            const limit = queries.limit.get(queryResponse);
                            const bookmark = queries.bookmark.get(queryResponse);
                            const sort = queries.sort.get(queryResponse);
                            if (bookmark)
                                filter = limitFilter(filter, sort, bookmark);
                            queries.value.set(queryResponse, findMatches(resourceType, filter, sort, limit));
                            queries.fulfilled.set(queryResponse, fulfillTimestamp);
                            queries.fulfilling.delete(queryResponse);
                        }
                        resolve2();
                    })
                        .catch(reject2);
                }));
            }
            Promise.all(allPromises2)
                .then(() => resolve(updated))
                .catch(reject);
        })
            .catch(reject);
    });
}
function getTimestamp() {
    return fulfillTimestamp;
}
function postTasks(deviceId, tasks) {
    for (const t of tasks) {
        t.status = "pending";
        t.device = deviceId;
    }
    return xhrRequest({
        method: "POST",
        url: `/api/devices/${encodeURIComponent(deviceId)}/tasks`,
        data: tasks,
        extract: xhr => {
            if (xhr.status !== 200)
                throw new Error(xhr.response);
            const connectionRequestStatus = xhr.getResponseHeader("Connection-Request");
            const st = JSON.parse(xhr.response);
            for (const [i, t] of st.entries()) {
                tasks[i]._id = t._id;
                tasks[i].status = t.status;
                tasks[i].fault = t.fault;
            }
            return connectionRequestStatus;
        }
    });
}
function updateTags(deviceId, tags) {
    return xhrRequest({
        method: "POST",
        url: `/api/devices/${encodeURIComponent(deviceId)}/tags`,
        data: tags
    });
}
function deleteResource(resourceType, id) {
    return xhrRequest({
        method: "DELETE",
        url: `/api/${resourceType}/${encodeURIComponent(id)}`
    });
}
function putResource(resourceType, id, object) {
    for (const k in object)
        if (object[k] === undefined)
            object[k] = null;
    return xhrRequest({
        method: "PUT",
        url: `/api/${resourceType}/${encodeURIComponent(id)}`,
        data: object
    });
}
function queryConfig(pattern = "%") {
    const filter = stringify(["LIKE", ["PARAM", "_id"], pattern]);
    return xhrRequest({
        method: "GET",
        url: `api/config/?${m.buildQueryString({ filter: filter })}`,
        background: true
    });
}
function resourceExists(resource, id) {
    const param = resource === "devices" ? "DeviceID.ID" : "_id";
    const filter = ["=", ["PARAM", param], id];
    return xhrRequest({
        method: "HEAD",
        url: `/api/${resource}/?` +
            m.buildQueryString({
                filter: memoizedStringify(filter)
            }),
        extract: xhr => +xhr.getResponseHeader("x-total-count"),
        background: true
    });
}
function evaluateExpression(exp, obj) {
    if (!Array.isArray(exp))
        return exp;
    return memoizedEvaluate(exp, obj, fulfillTimestamp);
}
function changePassword(username, newPassword, authPassword) {
    const data = { newPassword };
    if (authPassword)
        data["authPassword"] = authPassword;
    return xhrRequest({
        method: "PUT",
        url: `/api/users/${username}/password`,
        background: true,
        data
    });
}
function logIn(username, password) {
    return xhrRequest({
        method: "POST",
        url: "/login",
        background: true,
        data: { username, password }
    });
}
function logOut() {
    return xhrRequest({
        method: "POST",
        url: "/logout"
    });
}
function ping(host) {
    return xhrRequest({
        url: `/api/ping/${encodeURIComponent(host)}`
    });
}

/**
 * Copyright 2013-2019  GenieACS Inc.
 *
 * This file is part of GenieACS.
 *
 * GenieACS is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * GenieACS is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with GenieACS.  If not, see <http://www.gnu.org/licenses/>.
 */
const queue = new Set();
const staging = new Set();
function queueTask(task) {
    staging.delete(task);
    task.status = "queued";
    queue.add(task);
}
function deleteTask(task) {
    staging.delete(task);
    queue.delete(task);
}
function getQueue() {
    return queue;
}
function clear() {
    queue.clear();
}
function getStaging() {
    return staging;
}
function stageSpv(task) {
    staging.add(task);
}
function stageDownload(task) {
    staging.add(task);
}
function commit(tasks, callback) {
    const devices = {};
    for (const t of tasks) {
        devices[t.device] = devices[t.device] || [];
        devices[t.device].push(t);
        queueTask(t);
    }
    return new Promise(resolve => {
        let counter = 1;
        for (const [deviceId, tasks2] of Object.entries(devices)) {
            ++counter;
            postTasks(deviceId, tasks2)
                .then(connectionRequestStatus => {
                for (const t of tasks2) {
                    if (t.status === "pending")
                        t.status = "stale";
                    else if (t.status === "done")
                        queue.delete(t);
                }
                callback(deviceId, null, connectionRequestStatus, tasks2);
                if (--counter === 0)
                    resolve();
            })
                .catch(err => {
                for (const t of tasks2)
                    t.status = "stale";
                callback(deviceId, err, null, tasks2);
                if (--counter === 0)
                    resolve();
            });
        }
        if (--counter === 0)
            resolve();
    });
}

/**
 * Copyright 2013-2019  GenieACS Inc.
 *
 * This file is part of GenieACS.
 *
 * GenieACS is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * GenieACS is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with GenieACS.  If not, see <http://www.gnu.org/licenses/>.
 */
function mparam(param) {
    return m("span.parameter", { title: param }, `${param}`);
}
function mval(val) {
    return m("span.value", { title: val }, `${val}`);
}
function renderStagingSpv(task, queueFunc, cancelFunc) {
    function keydown(e) {
        if (e.keyCode === 13)
            queueFunc();
        else if (e.keyCode === 27)
            cancelFunc();
        else
            e.redraw = false;
    }
    let input;
    if (task.parameterValues[0][2] === "xsd:boolean") {
        input = m("select", {
            value: task.parameterValues[0][1].toString(),
            onchange: e => {
                e.redraw = false;
                task.parameterValues[0][1] = input.dom.value;
            },
            onkeydown: keydown,
            oncreate: vnode => {
                vnode.dom.focus();
            }
        }, [
            m("option", { value: "true" }, "true"),
            m("option", { value: "false" }, "false")
        ]);
    }
    else {
        input = m("input", {
            type: ["xsd:dateTime", "xsd:int", "xsd:unsignedInt"].includes(task.parameterValues[0][2])
                ? "number"
                : "text",
            value: task.parameterValues[0][1],
            oninput: e => {
                e.redraw = false;
                task.parameterValues[0][1] = input.dom.value;
            },
            onkeydown: keydown,
            oncreate: vnode => {
                vnode.dom.focus();
                vnode.dom.select();
                // Need to prevent scrolling on focus because
                // we're animating height and using overflow: hidden
                vnode.dom.parentNode.parentNode.scrollTop = 0;
            }
        });
    }
    return [m("span", "Editing ", mparam(task.parameterValues[0][0])), input];
}
function renderStagingDownload(task) {
    task.invalid = !task.fileName || !task.fileType;
    const files = fetch("files", true);
    const idParts = task.device.split("-");
    const oui = decodeURIComponent(idParts[0]);
    const productClass = idParts.length === 3 ? decodeURIComponent(idParts[1]) : "";
    const typesList = [
        "",
        "1 Firmware Upgrade Image",
        "2 Web Content",
        "3 Vendor Configuration File",
        "4 Tone File",
        "5 Ringer File"
    ].map(t => m("option", { disabled: !t, value: t, selected: (task.fileType || "") === t }, t));
    const filesList = [""]
        .concat(files.value
        .filter(f => (!f["metadata.oui"] || f["metadata.oui"] === oui) &&
        (!f["metadata.productClass"] ||
            f["metadata.productClass"] === productClass))
        .map(f => f._id))
        .map(f => m("option", { disabled: !f, value: f, selected: (task.fileName || "") === f }, f));
    return [
        "Push ",
        m("select", {
            onchange: e => {
                const f = e.target.value;
                task.fileName = f;
                task.fileType = "";
                for (const file of files.value)
                    if (file._id === f)
                        task.fileType = file["metadata.fileType"];
            },
            disabled: files.fulfilling,
            style: "width: 350px"
        }, filesList),
        " as ",
        m("select", {
            onchange: e => {
                task.fileType = e.target.value;
            }
        }, typesList)
    ];
}
function renderStaging(staging) {
    const elements = [];
    for (const s of staging) {
        const queueFunc = () => {
            staging.delete(s);
            queueTask(s);
        };
        const cancelFunc = () => {
            staging.delete(s);
        };
        let elms;
        if (s.name === "setParameterValues")
            elms = renderStagingSpv(s, queueFunc, cancelFunc);
        else if (s.name === "download")
            elms = renderStagingDownload(s);
        const queue = m("button.primary", { title: "Queue task", onclick: queueFunc, disabled: s.invalid }, "Queue");
        const cancel = m("button", { title: "Cancel edit", onclick: cancelFunc }, "Cancel");
        elements.push(m(".staging", elms, m("div.actions", queue, cancel)));
    }
    return elements;
}
function renderQueue(queue) {
    const details = [];
    const devices = {};
    for (const t of queue) {
        devices[t.device] = devices[t.device] || [];
        devices[t.device].push(t);
    }
    for (const [k, v] of Object.entries(devices)) {
        details.push(m("strong", k));
        for (const t of v) {
            const actions = [];
            if (t.status === "fault" || t.status === "stale") {
                actions.push(m("button", {
                    title: "Retry this task",
                    onclick: () => {
                        queueTask(t);
                    }
                }, "↺"));
            }
            actions.push(m("button", {
                title: "Remove this task",
                onclick: () => {
                    deleteTask(t);
                }
            }, "✕"));
            if (t.name === "setParameterValues") {
                details.push(m(`div.${t.status}`, m("span", "Set ", mparam(t.parameterValues[0][0]), " to '", mval(t.parameterValues[0][1]), "'"), m(".actions", actions)));
            }
            else if (t.name === "refreshObject") {
                details.push(m(`div.${t.status}`, m("span", "Refresh ", mparam(t.parameterName)), m(".actions", actions)));
            }
            else if (t.name === "reboot") {
                details.push(m(`div.${t.status}`, "Reboot", m(".actions", actions)));
            }
            else if (t.name === "factoryReset") {
                details.push(m(`div.${t.status}`, "Factory reset", m(".actions", actions)));
            }
            else if (t.name === "addObject") {
                details.push(m(`div.${t.status}`, m("span", "Add ", mparam(t.objectName)), m(".actions", actions)));
            }
            else if (t.name === "deleteObject") {
                details.push(m(`div.${t.status}`, m("span", "Delete ", mparam(t.objectName)), m(".actions", actions)));
            }
            else if (t.name === "getParameterValues") {
                details.push(m(`div.${t.status}`, `Refresh ${t.parameterNames.length} parameters`, m(".actions", actions)));
            }
            else if (t.name === "download") {
                details.push(m(`div.${t.status}`, `Push file: ${t.fileName} (${t.fileType})`, m(".actions", actions)));
            }
            else {
                details.push(m(`div.${t.status}`, t.name, m(".actions", actions)));
            }
        }
    }
    return details;
}
function renderNotifications(notifs) {
    const notificationElements = [];
    for (const n of notifs) {
        let buttons;
        if (n.actions) {
            const btns = Object.entries(n.actions).map(([label, onclick]) => m("button.primary", { onclick: onclick }, label));
            if (btns.length)
                buttons = m("div", { style: "float: right" }, btns);
        }
        notificationElements.push(m("div.notification", {
            class: n.type,
            style: "position: absolute;opacity: 0",
            oncreate: vnode => {
                vnode.dom.style.opacity = "1";
            },
            onbeforeremove: vnode => {
                vnode.dom.style.opacity = "0";
                return new Promise(resolve => {
                    setTimeout(() => {
                        resolve();
                    }, 500);
                });
            },
            key: n.timestamp
        }, m("div", buttons, n.message)));
    }
    return notificationElements;
}
const component$1 = () => {
    return {
        view: vnode => {
            const queue = getQueue();
            const staging = getStaging();
            const notifs = getNotifications();
            let drawerElement, statusElement;
            const notificationElements = renderNotifications(notifs);
            const stagingElements = renderStaging(staging);
            const queueElements = renderQueue(queue);
            function repositionNotifications() {
                let top = 10;
                for (const c of notificationElements) {
                    c.dom.style.top = `${top}`;
                    top += c.dom.offsetHeight + 10;
                }
            }
            function resizeDrawer() {
                let height = statusElement.dom.offsetTop + statusElement.dom.offsetHeight;
                if (stagingElements.length) {
                    for (const s of stagingElements) {
                        height = Math.max(height, s.dom.offsetTop +
                            s.dom.offsetHeight);
                    }
                }
                else if (vnode.state["mouseIn"]) {
                    for (const c of drawerElement.children)
                        height = Math.max(height, c.dom.offsetTop + c.dom.offsetHeight);
                }
                drawerElement.dom.style.height = height;
            }
            if (stagingElements.length + queueElements.length) {
                const statusCount = { queued: 0, pending: 0, fault: 0, stale: 0 };
                for (const t of queue)
                    statusCount[t["status"]] += 1;
                const actions = m(".actions", m("button.primary", {
                    title: "Commit queued tasks",
                    disabled: !statusCount.queued,
                    onclick: () => {
                        const tasks = Array.from(getQueue()).filter(t => t["status"] === "queued");
                        commit(tasks, (deviceId, err, connectionRequestStatus, tasks2) => {
                            if (err) {
                                push("error", `${deviceId}: ${err.message}`);
                                return;
                            }
                            if (connectionRequestStatus !== "OK") {
                                push("error", `${deviceId}: ${connectionRequestStatus}`);
                                return;
                            }
                            for (const t of tasks2) {
                                if (t.status === "stale") {
                                    push("error", `${deviceId}: No contact from device`);
                                    return;
                                }
                                else if (t.status === "fault") {
                                    push("error", `${deviceId}: Task(s) faulted`);
                                    return;
                                }
                            }
                            push("success", `${deviceId}: Task(s) committed`);
                        })
                            .then(() => {
                            fulfill(0, Date.now());
                        });
                    }
                }, "Commit"), m("button", {
                    title: "Clear tasks",
                    onclick: clear,
                    disabled: !queueElements.length
                }, "Clear"));
                statusElement = m(".status", m("span.queued", { class: statusCount.queued ? "active" : "" }, `Queued: ${statusCount.queued}`), m("span.pending", { class: statusCount.pending ? "active" : "" }, `Pending: ${statusCount.pending}`), m("span.fault", { class: statusCount.fault ? "active" : "" }, `Fault: ${statusCount.fault}`), m("span.stale", { class: statusCount.stale ? "active" : "" }, `Stale: ${statusCount.stale}`), actions);
                drawerElement = m(".drawer", {
                    key: "drawer",
                    style: "opacity: 0;height: 0;",
                    oncreate: vnode2 => {
                        vnode.state["mouseIn"] = false;
                        vnode2.dom.style.opacity = "1";
                        resizeDrawer();
                    },
                    onmouseover: e => {
                        vnode.state["mouseIn"] = true;
                        resizeDrawer();
                        e.redraw = false;
                    },
                    onmouseleave: e => {
                        vnode.state["mouseIn"] = false;
                        resizeDrawer();
                        e.redraw = false;
                    },
                    onupdate: resizeDrawer,
                    onbeforeremove: vnode2 => {
                        vnode2.dom.onmouseover = null;
                        vnode2.dom.onmouseleave = null;
                        vnode2.dom.style.opacity = "0";
                        vnode2.dom.style.height = "0";
                        return new Promise(resolve => {
                            setTimeout(resolve, 500);
                        });
                    }
                }, statusElement, stagingElements.length ? stagingElements : m(".queue", queueElements));
            }
            return m("div.drawer-wrapper", drawerElement, m("div.notifications-wrapper", {
                key: "notifications",
                style: "position: relative;",
                onupdate: repositionNotifications,
                oncreate: repositionNotifications
            }, notificationElements));
        }
    };
};

/**
 * Copyright 2013-2019  GenieACS Inc.
 *
 * This file is part of GenieACS.
 *
 * GenieACS is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * GenieACS is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with GenieACS.  If not, see <http://www.gnu.org/licenses/>.
 */
const component$2 = () => {
    return {
        view: () => {
            if (window.username) {
                return m("div.user-menu", window.username, m("button", {
                    onclick: e => {
                        e.target.disabled = true;
                        logOut()
                            .then(() => {
                            location.hash = "";
                            location.reload();
                        })
                            .catch(err => {
                            e.target.disabled = false;
                            push("error", err.message);
                        });
                        return false;
                    }
                }, "Log out"));
            }
            else {
                return m("div.user-menu", m("a", {
                    href: "#!/login?" + m.buildQueryString({ continue: m.route.get() })
                }, "Log in"));
            }
        }
    };
};

/**
 * Copyright 2013-2019  GenieACS Inc.
 *
 * This file is part of GenieACS.
 *
 * GenieACS is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * GenieACS is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with GenieACS.  If not, see <http://www.gnu.org/licenses/>.
 */
const component$3 = () => {
    return {
        view: vnode => {
            const active = { [vnode.attrs["page"]]: "active" };
            const tabs = [];
            if (window.authorizer.hasAccess("presets", 1)) {
                tabs.push(m("li", { class: active["presets"] }, m("a", { href: "#!/admin/presets" }, "Presets")));
            }
            if (window.authorizer.hasAccess("provisions", 1)) {
                tabs.push(m("li", { class: active["provisions"] }, m("a", { href: "#!/admin/provisions" }, "Provisions")));
            }
            if (window.authorizer.hasAccess("virtualParameters", 1)) {
                tabs.push(m("li", { class: active["virtualParameters"] }, m("a", { href: "#!/admin/virtualParameters" }, "Virtual Parameters")));
            }
            if (window.authorizer.hasAccess("files", 1)) {
                tabs.push(m("li", { class: active["files"] }, m("a", { href: "#!/admin/files" }, "Files")));
            }
            if (window.authorizer.hasAccess("config", 1)) {
                tabs.push(m("li", { class: active["config"] }, m("a", { href: "#!/admin/config" }, "Config")));
            }
            if (window.authorizer.hasAccess("permissions", 1)) {
                tabs.push(m("li", { class: active["permissions"] }, m("a", { href: "#!/admin/permissions" }, "Permissions")));
            }
            if (window.authorizer.hasAccess("users", 1)) {
                tabs.push(m("li", { class: active["users"] }, m("a", { href: "#!/admin/users" }, "Users")));
            }
            return m("nav#side-menu", m("ul", tabs));
        }
    };
};

/**
 * Copyright 2013-2019  GenieACS Inc.
 *
 * This file is part of GenieACS.
 *
 * GenieACS is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * GenieACS is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with GenieACS.  If not, see <http://www.gnu.org/licenses/>.
 */
let overlayCallback = null;
function open(callback) {
    overlayCallback = callback;
}
function close(callback) {
    if (callback === overlayCallback) {
        overlayCallback = null;
        return true;
    }
    return false;
}
function render() {
    if (overlayCallback) {
        return m(".overlay-wrapper", {
            tabindex: 0,
            onclick: () => {
                close(overlayCallback);
            },
            style: "opacity: 0",
            oncreate: vnode => {
                vnode.dom.focus();
                vnode.dom.style.opacity = "1";
            },
            onbeforeremove: vnode => {
                vnode.dom.style.opacity = "0";
                return new Promise(resolve => {
                    setTimeout(() => {
                        resolve();
                    }, 500);
                });
            }
        }, m(".overlay", {
            onclick: e => {
                e.stopPropagation();
            }
        }, overlayCallback()));
    }
    return null;
}
document.addEventListener("keydown", e => {
    if (overlayCallback && e.keyCode === 27 && close(overlayCallback))
        m.redraw();
});
window.addEventListener("popstate", () => {
    if (close(overlayCallback))
        m.redraw();
});

/**
 * Copyright 2013-2019  GenieACS Inc.
 *
 * This file is part of GenieACS.
 *
 * GenieACS is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * GenieACS is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with GenieACS.  If not, see <http://www.gnu.org/licenses/>.
 */
const adminPages = [
    "presets",
    "provisions",
    "virtualParameters",
    "files",
    "config",
    "users",
    "permissions"
];
const component$4 = () => {
    return {
        view: vnode => {
            let sideMenu, group;
            if (adminPages.includes(vnode.attrs["page"])) {
                group = "admin";
                const attrs = {};
                attrs["page"] = vnode.attrs["page"];
                sideMenu = m(component$3, attrs);
            }
            if ("map" == vnode.attrs["page"]) {
                sideMenu =
                    m("nav", { id: "side-menu" }, m("h1", "Filters"), m("b", "Status"), m("ul", { id: "status-filter" }, m("li", m("input", { type: "checkbox", style: "display:inline", value: "Online" }), "Online"), m("li", m("input", { type: "checkbox", style: "display:inline", value: "24h" }), "In 24 hours"), m("li", m("input", { type: "checkbox", style: "display:inline", value: "Other" }), "Others")), m("b", "Manufacturers"), m("ul", { id: "manufacturer-filter" }), m("b", "Models"), m("ul", { id: "model-filter" }), m("button.primary", { title: "Manage visible devices", id: "manage-btn" }, "Manage"));
            }
            const attrs = {};
            attrs["page"] = group || vnode.attrs["page"];
            return [
                m("#header", [
                    m("img.logo", { src: "logo.svg" }),
                    m(component$2),
                    m(component, attrs),
                    m(component$1)
                ]),
                m("#content-wrapper", sideMenu, m("#content", { class: `page-${vnode.attrs["page"]}` }, [
                    vnode.children
                ])),
                render()
            ];
        }
    };
};

/**
 * Copyright 2013-2019  GenieACS Inc.
 *
 * This file is part of GenieACS.
 *
 * GenieACS is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * GenieACS is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with GenieACS.  If not, see <http://www.gnu.org/licenses/>.
 */
const UNITS = {
    year: 12 * 30 * 24 * 60 * 60 * 1000,
    month: 30 * 24 * 60 * 60 * 1000,
    day: 24 * 60 * 60 * 1000,
    hour: 60 * 60 * 1000,
    minute: 60 * 1000,
    second: 1000
};
function timeAgo(dtime) {
    let res = "";
    let level = 2;
    for (const [u, t] of Object.entries(UNITS)) {
        if (dtime >= t) {
            let n;
            if (level > 1) {
                n = Math.floor(dtime / t);
                dtime -= n * t;
            }
            else {
                n = Math.round(dtime / t);
            }
            if (n > 1)
                res += `${n} ${u}s `;
            else
                res += `${n} ${u} `;
            if (!--level)
                break;
        }
    }
    return res + "ago";
}

/**
 * Copyright 2013-2019  GenieACS Inc.
 *
 * This file is part of GenieACS.
 *
 * GenieACS is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * GenieACS is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with GenieACS.  If not, see <http://www.gnu.org/licenses/>.
 */
const evaluateParam = memoize((exp, obj, now) => {
    let timestamp = now;
    const params = new Set();
    const value = evaluate(exp, obj, now, e => {
        if (Array.isArray(e)) {
            if (e[0] === "PARAM") {
                params.add(e[1]);
                if (!Array.isArray(e[1])) {
                    const p = obj[e[1]];
                    if (p && p.valueTimestamp)
                        timestamp = Math.min(timestamp, p.valueTimestamp);
                }
            }
            if (e[0] === "FUNC" && e[1] === "DATE_STRING" && !Array.isArray(e[2]))
                return new Date(e[2]).toLocaleString();
        }
        return e;
    });
    let parameter = params.size === 1 ? params.values().next().value : null;
    if (Array.isArray(parameter))
        parameter = null;
    return { value, timestamp, parameter };
});
const component$5 = () => {
    return {
        view: vnode => {
            const device = vnode.attrs["device"];
            const { value, timestamp, parameter } = evaluateParam(vnode.attrs["parameter"], vnode.attrs["device"], getTimestamp());
            if (value == null)
                return null;
            let edit;
            if (device[parameter] && device[parameter].writable) {
                edit = M("button", {
                    title: "Edit parameter value",
                    onclick: () => {
                        stageSpv({
                            name: "setParameterValues",
                            device: device["DeviceID.ID"].value[0],
                            parameterValues: [
                                [
                                    parameter,
                                    device[parameter].value[0],
                                    device[parameter].value[1]
                                ]
                            ]
                        });
                    }
                }, "✎");
            }
            const el = M("long-text", { text: `${value}` });
            return M("span", {
                class: "parameter-value",
                onmouseover: e => {
                    e.redraw = false;
                    // Don't update any child element
                    if (e.target === el.dom) {
                        const now = Date.now();
                        const localeString = new Date(timestamp).toLocaleString();
                        e.target.title = `${localeString} (${timeAgo(now - timestamp)})`;
                    }
                }
            }, el, edit);
        }
    };
};

/**
 * Copyright 2013-2019  GenieACS Inc.
 *
 * This file is part of GenieACS.
 *
 * GenieACS is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * GenieACS is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with GenieACS.  If not, see <http://www.gnu.org/licenses/>.
 */
const component$6 = () => {
    return {
        view: vnode => {
            const device = vnode.attrs["device"];
            const rows = Object.values(vnode.attrs["parameters"]).map(parameter => {
                const p = M.context({
                    device: device,
                    parameter: parameter["parameter"]
                }, parameter["type"] || "parameter", parameter);
                return M("tr", {
                    onupdate: vn => {
                        vn.dom.style.display = p.dom
                            ? ""
                            : "none";
                    }
                }, M("th", parameter["label"]), M("td", p));
            });
            return M("table.parameter-list", rows);
        }
    };
};

/**
 * Copyright 2013-2019  GenieACS Inc.
 *
 * This file is part of GenieACS.
 *
 * GenieACS is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * GenieACS is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with GenieACS.  If not, see <http://www.gnu.org/licenses/>.
 */
const component$7 = () => {
    return {
        oninit: vnode => {
            const obj = vnode.attrs["parameter"];
            if (!Array.isArray(obj) || obj[0] !== "PARAM")
                throw new Error("Object must be a parameter path");
            vnode.state["object"] = obj[1];
            vnode.state["parameters"] = Object.values(vnode.attrs["childParameters"]);
        },
        view: vnode => {
            const device = vnode.attrs["device"];
            const object = evaluateExpression(vnode.state["object"], device);
            const parameters = vnode.state["parameters"];
            if (!device[object])
                return null;
            const instances = new Set();
            const prefix = `${object}.`;
            for (const p in device) {
                if (p.startsWith(prefix)) {
                    const i = p.indexOf(".", prefix.length);
                    if (i === -1)
                        instances.add(p);
                    else
                        instances.add(p.slice(0, i));
                }
            }
            const headers = Object.values(parameters).map(p => M("th", p["label"]));
            const thead = M("thead", M("tr", headers));
            const rows = [];
            for (const i of instances) {
                let filter = vnode.attrs["filter"] != null ? vnode.attrs["filter"] : true;
                filter = map(filter, e => {
                    if (Array.isArray(e) && e[0] === "PARAM")
                        return ["PARAM", ["||", i, ".", e[1]]];
                    return e;
                });
                if (!evaluateExpression(filter, device))
                    continue;
                const row = parameters.map(p => {
                    const param = map(p.parameter, e => {
                        if (Array.isArray(e) && e[0] === "PARAM")
                            return ["PARAM", ["||", i, ".", e[1]]];
                        return e;
                    });
                    return M("td", M.context({
                        device: device,
                        parameter: param
                    }, p.type || "parameter", Object.assign({}, p, {
                        device: device,
                        parameter: param
                    })));
                });
                if (device[i].writable === true) {
                    row.push(M("td", M("button", {
                        title: "Delete this instance",
                        onclick: () => {
                            queueTask({
                                name: "deleteObject",
                                device: device["DeviceID.ID"].value[0],
                                objectName: i
                            });
                        }
                    }, "✕")));
                }
                rows.push(M("tr", row));
            }
            if (!rows.length) {
                rows.push(M("tr.empty", M("td", { colspan: headers.length }, "No instances")));
            }
            if (device[object].writable === true) {
                rows.push(M("tr", M("td", { colspan: headers.length }), M("td", M("button", {
                    title: "Create a new instance",
                    onclick: () => {
                        queueTask({
                            name: "addObject",
                            device: device["DeviceID.ID"].value[0],
                            objectName: object
                        });
                    }
                }, "🞢"))));
            }
            let label;
            if (vnode.attrs["label"])
                label = M("h2", vnode.attrs["label"]);
            return [label, M("table.table", thead, M("tbody", rows))];
        }
    };
};

/**
 * Copyright 2013-2019  GenieACS Inc.
 *
 * This file is part of GenieACS.
 *
 * GenieACS is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * GenieACS is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with GenieACS.  If not, see <http://www.gnu.org/licenses/>.
 */
const CHARTS = config.ui.overview.charts;
const component$8 = () => {
    return {
        view: vnode => {
            const device = vnode.attrs["device"];
            const chart = CHARTS[vnode.attrs["chart"]];
            if (!chart)
                return null;
            for (const slice of Object.values(chart.slices)) {
                const filter = slice["filter"];
                if (evaluateExpression(filter, device)) {
                    const dot = M("svg", {
                        width: "1em",
                        height: "1em",
                        xmlns: "http://www.w3.org/2000/svg",
                        "xmlns:xlink": "http://www.w3.org/1999/xlink"
                    }, M("circle", {
                        cx: "0.5em",
                        cy: "0.5em",
                        r: "0.4em",
                        fill: slice["color"]
                    }));
                    return M("span.overview-dot", dot, `${slice["label"]}`);
                }
            }
            return null;
        }
    };
};

/**
 * Copyright 2013-2019  GenieACS Inc.
 *
 * This file is part of GenieACS.
 *
 * GenieACS is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * GenieACS is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with GenieACS.  If not, see <http://www.gnu.org/licenses/>.
 */
const component$9 = () => {
    return {
        view: vnode => {
            if (vnode.attrs["filter"]) {
                if (!evaluateExpression(vnode.attrs["filter"], vnode.attrs["device"]))
                    return null;
            }
            const children = Object.values(vnode.attrs["components"]).map(c => {
                if (typeof c !== "object")
                    return `${c}`;
                return M(c["type"], c);
            });
            if (vnode.attrs["element"])
                return M(vnode.attrs["element"], children);
            else
                return children;
        }
    };
};

/**
 * Copyright 2013-2019  GenieACS Inc.
 *
 * This file is part of GenieACS.
 *
 * GenieACS is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * GenieACS is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with GenieACS.  If not, see <http://www.gnu.org/licenses/>.
 */
const component$a = () => {
    return {
        view: vnode => {
            const device = vnode.attrs["device"];
            return M("button.primary", {
                title: "Initiate session and refresh basic parameters",
                onclick: e => {
                    e.target.disabled = true;
                    const params = Object.values(vnode.attrs["parameters"])
                        .map(exp => {
                        if (!Array.isArray(exp) || exp[0] !== "PARAM")
                            return null;
                        return evaluateExpression(exp[1], device);
                    })
                        .filter(exp => !!exp);
                    const task = {
                        name: "getParameterValues",
                        parameterNames: params,
                        device: device["DeviceID.ID"].value[0]
                    };
                    commit([task], (deviceId, err, connectionRequestStatus, tasks2) => {
                        if (err) {
                            push("error", `${deviceId}: ${err.message}`);
                            return;
                        }
                        for (const t of tasks2)
                            if (t.status === "stale")
                                deleteTask(t);
                        if (connectionRequestStatus !== "OK") {
                            push("error", `${deviceId}: ${connectionRequestStatus}`);
                        }
                        else if (tasks2[0].status === "stale") {
                            push("error", `${deviceId}: No contact from device`);
                        }
                        else if (tasks2[0].status === "fault") {
                            push("error", `${deviceId}: Refresh faulted`);
                        }
                        else {
                            push("success", `${deviceId}: Summoned`);
                        }
                    })
                        .then(() => {
                        e.target.disabled = false;
                        fulfill(0, Date.now());
                    });
                }
            }, "Summon");
        }
    };
};

/**
 * Copyright 2013-2019  GenieACS Inc.
 *
 * This file is part of GenieACS.
 *
 * GenieACS is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * GenieACS is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with GenieACS.  If not, see <http://www.gnu.org/licenses/>.
 */
const component$b = () => {
    return {
        view: vnode => {
            const device = vnode.attrs["device"];
            const deviceId = device["DeviceID.ID"].value[0];
            const faults = fetch("faults", [
                "AND",
                [">", ["PARAM", "_id"], `${deviceId}:`],
                ["<", ["PARAM", "_id"], `${deviceId}:zzzz`]
            ]);
            const headers = [
                "Channel",
                "Code",
                "Message",
                "Retries",
                "Timestamp"
            ].map(l => M("th", l));
            const thead = M("thead", M("tr", headers));
            const rows = [];
            for (const f of faults.value) {
                rows.push([
                    M("td", f["channel"]),
                    M("td", f["code"]),
                    M("td", f["message"]),
                    M("td", f["retries"]),
                    M("td", new Date(f["timestamp"]).toLocaleString()),
                    M("td", M("button", {
                        title: "Delete fault",
                        onclick: e => {
                            e.redraw = false;
                            deleteResource("faults", f["_id"])
                                .then(() => {
                                push("success", "Fault deleted");
                                fulfill(Date.now(), Date.now());
                                M.redraw();
                            })
                                .catch(err => {
                                push("error", err.message);
                                fulfill(Date.now(), Date.now());
                            });
                        }
                    }, "✕"))
                ]);
            }
            let tbody;
            if (rows.length) {
                tbody = M("tbody", rows.map(r => M("tr", r)));
            }
            else {
                tbody = M("tbody", M("tr.empty", M("td", { colspan: headers.length }, "No faults")));
            }
            return M("table.table", thead, tbody);
        }
    };
};

/**
 * Copyright 2013-2019  GenieACS Inc.
 *
 * This file is part of GenieACS.
 *
 * GenieACS is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * GenieACS is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with GenieACS.  If not, see <http://www.gnu.org/licenses/>.
 */
const memoizedParse = memoize(parse);
function escapeRegExp(str) {
    return str.replace(/[-[\]/{}()*+?.\\^$|]/g, "\\$&");
}
const component$c = () => {
    return {
        view: vnode => {
            const device = vnode.attrs["device"];
            const search = M("input", {
                type: "text",
                placeholder: "Search parameters",
                oninput: e => {
                    vnode.state["searchString"] = e.target.value;
                    e.redraw = false;
                    clearTimeout(vnode.state["timeout"]);
                    vnode.state["timeout"] = setTimeout(M.redraw, 500);
                }
            });
            const instanceRegex = /\.[0-9]+$/;
            let re;
            if (vnode.state["searchString"]) {
                const keywords = vnode.state["searchString"].split(" ").filter(s => s);
                if (keywords.length)
                    re = new RegExp(keywords.map(s => escapeRegExp(s)).join(".*"), "i");
            }
            const rows = Object.keys(device)
                .sort()
                .map(k => {
                const p = device[k];
                const val = [];
                const attrs = {};
                if (re) {
                    const str = p.value && p.value[0] ? `${k} ${p.value[0]}` : k;
                    if (!re.test(str))
                        attrs["style"] = "display: none;";
                }
                if (p.object === false) {
                    val.push(M("parameter", Object.assign({ device: device, parameter: memoizedParse(k) })));
                }
                else if (p.object && p.writable) {
                    if (instanceRegex.test(k)) {
                        val.push(M("button", {
                            title: "Delete this instance",
                            onclick: () => {
                                queueTask({
                                    name: "deleteObject",
                                    device: device["DeviceID.ID"].value[0],
                                    objectName: k
                                });
                            }
                        }, "✕"));
                    }
                    else {
                        val.push(M("button", {
                            title: "Create a new instance",
                            onclick: () => {
                                queueTask({
                                    name: "addObject",
                                    device: device["DeviceID.ID"].value[0],
                                    objectName: k
                                });
                            }
                        }, "🞢"));
                    }
                }
                val.push(M("button", {
                    title: "Refresh tree",
                    onclick: () => {
                        queueTask({
                            name: "getParameterValues",
                            device: device["DeviceID.ID"].value[0],
                            parameterNames: [k]
                        });
                    }
                }, "↺"));
                return M("tr", attrs, M("td.left", M("long-text", { text: k })), M("td.right", val));
            });
            return M(".all-parameters", M("a.download-csv", {
                href: `/api/devices/${device["DeviceID.ID"].value[0]}.csv`,
                download: "",
                style: "float: right;"
            }, "Download"), search, M(".parameter-list", M("table", M("tbody", rows))));
        }
    };
};

/**
 * Copyright 2013-2019  GenieACS Inc.
 *
 * This file is part of GenieACS.
 *
 * GenieACS is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * GenieACS is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with GenieACS.  If not, see <http://www.gnu.org/licenses/>.
 */
const component$d = () => {
    return {
        view: vnode => {
            const device = vnode.attrs["device"];
            const buttons = [];
            buttons.push(M("button.primary", {
                title: "Reboot device",
                onclick: () => {
                    queueTask({
                        name: "reboot",
                        device: device["DeviceID.ID"].value[0]
                    });
                }
            }, "Reboot"));
            buttons.push(M("button.critical", {
                title: "Factory reset device",
                onclick: () => {
                    queueTask({
                        name: "factoryReset",
                        device: device["DeviceID.ID"].value[0]
                    });
                }
            }, "Reset"));
            buttons.push(M("button.critical", {
                title: "Push a firmware or a config file",
                onclick: () => {
                    stageDownload({
                        name: "download",
                        device: device["DeviceID.ID"].value[0]
                    });
                }
            }, "Push file"));
            buttons.push(M("button.primary", {
                title: "Delete device",
                onclick: () => {
                    if (!confirm("Deleting this device. Are you sure?"))
                        return;
                    const deviceId = device["DeviceID.ID"].value[0];
                    deleteResource("devices", deviceId)
                        .then(() => {
                        push("success", `${deviceId}: Device deleted`);
                        M.route.set("/devices");
                    })
                        .catch(err => {
                        push("error", err.message);
                    });
                }
            }, "Delete"));
            return M(".actions-bar", buttons);
        }
    };
};

/**
 * Copyright 2013-2019  GenieACS Inc.
 *
 * This file is part of GenieACS.
 *
 * GenieACS is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * GenieACS is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with GenieACS.  If not, see <http://www.gnu.org/licenses/>.
 */
const component$e = () => {
    return {
        view: vnode => {
            const device = vnode.attrs["device"];
            let writable = true;
            if (vnode.attrs["writable"] != null)
                writable = vnode.attrs["writable"];
            const tags = [];
            for (const p of Object.keys(device))
                if (p.startsWith("Tags."))
                    tags.push(p.slice(5));
            tags.sort();
            if (!writable)
                return M(".tags", tags.map(t => M("span.tag", t)));
            return M(".tags", tags.map(tag => M("span.tag", tag, M("button", {
                onclick: e => {
                    e.target.disabled = true;
                    const deviceId = device["DeviceID.ID"].value[0];
                    updateTags(deviceId, { [tag]: false })
                        .then(() => {
                        e.target.disabled = false;
                        push("success", `${deviceId}: Tags updated`);
                        fulfill(0, Date.now());
                    })
                        .catch(err => {
                        e.target.disabled = false;
                        push("error", `${deviceId}: ${err.message}`);
                    });
                }
            }, "✕"))), M("span.tag.writable", M("button", {
                onclick: e => {
                    e.target.disabled = true;
                    const deviceId = device["DeviceID.ID"].value[0];
                    const tag = prompt(`Enter tag to assign to device:`);
                    if (!tag) {
                        e.target.disabled = false;
                        return;
                    }
                    updateTags(deviceId, { [tag]: true })
                        .then(() => {
                        e.target.disabled = false;
                        push("success", `${deviceId}: Tags updated`);
                        fulfill(0, Date.now());
                    })
                        .catch(err => {
                        e.target.disabled = false;
                        push("error", `${deviceId}: ${err.message}`);
                    });
                }
            }, "🞢")));
        }
    };
};

/**
 * Copyright 2013-2019  GenieACS Inc.
 *
 * This file is part of GenieACS.
 *
 * GenieACS is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * GenieACS is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with GenieACS.  If not, see <http://www.gnu.org/licenses/>.
 */
const REFRESH_INTERVAL = 3000;
const component$f = () => {
    return {
        oninit: vnode => {
            vnode.state["timestamp"] = 0;
            vnode.state["ping"] = null;
            vnode.state["host"] = null;
            vnode.state["timeout"] = null;
        },
        view: vnode => {
            const refresh = () => {
                const device = vnode.attrs["device"];
                let param = device["InternetGatewayDevice.ManagementServer.ConnectionRequestURL"];
                if (!param)
                    param = device["Device.ManagementServer.ConnectionRequestURL"];
                if (!param || !param.value)
                    return;
                const url = new URL(param.value[0]);
                vnode.state["host"] = url.hostname;
                vnode.state["timestamp"] = Date.now();
                ping(vnode.state["host"])
                    .then(res => {
                    vnode.state["ping"] = res;
                })
                    .catch(() => {
                    // Do nothing
                });
            };
            const t = vnode.state["timestamp"] + REFRESH_INTERVAL - Date.now();
            clearTimeout(vnode.state["timeout"]);
            if (t <= 0)
                refresh();
            else
                vnode.state["timeout"] = setTimeout(refresh, t);
            if (vnode.state["host"]) {
                let status = "";
                if (vnode.state["ping"]) {
                    if (vnode.state["ping"].avg != null)
                        status = `${Math.trunc(vnode.state["ping"].avg)} ms`;
                    else
                        status = "Unreachable";
                }
                return M("div", ['Pinging ',
                    M("a", { href: `http://${vnode.state["host"]}` }, vnode.state["host"]),
                    ':', status]);
            }
            return null;
        }
    };
};

/**
 * Copyright 2013-2019  GenieACS Inc.
 *
 * This file is part of GenieACS.
 *
 * GenieACS is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * GenieACS is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with GenieACS.  If not, see <http://www.gnu.org/licenses/>.
 */
const component$g = () => {
    return {
        view: vnode => {
            let deviceId;
            if (vnode.attrs["device"])
                deviceId = vnode.attrs["device"]["DeviceID.ID"].value[0];
            const children = Object.values(vnode.attrs["components"]).map(c => {
                if (typeof c !== "object")
                    return `${c}`;
                const attrs = Object.assign({}, vnode.attrs, c);
                return M(attrs["type"], attrs);
            });
            if (deviceId) {
                return M("a", { href: `#!/devices/${encodeURIComponent(deviceId)}` }, children);
            }
            else {
                return children;
            }
        }
    };
};

/**
 * Copyright 2013-2019  GenieACS Inc.
 *
 * This file is part of GenieACS.
 *
 * GenieACS is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * GenieACS is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with GenieACS.  If not, see <http://www.gnu.org/licenses/>.
 */
const component$h = () => {
    return {
        view: vnode => {
            const text = vnode.attrs["text"];
            const element = vnode.attrs["element"] || "span";
            function overflowed(_vnode) {
                if (_vnode.dom.classList.contains("long-text-overflowed"))
                    return;
                _vnode.dom.classList.add("long-text-overflowed");
                _vnode.dom.onclick = e => {
                    open(() => {
                        return m("textarea.long-text", {
                            value: text,
                            cols: 80,
                            rows: 24,
                            readonly: "",
                            oncreate: vnode2 => {
                                vnode2.dom.focus();
                                vnode2.dom.select();
                            }
                        });
                    });
                    // prevent index page selection
                    e.stopPropagation();
                    m.redraw();
                };
            }
            return m(element, {
                oncreate: vnode2 => {
                    if (vnode2.dom.clientWidth !== vnode2.dom.scrollWidth)
                        overflowed(vnode2);
                },
                onupdate: vnode2 => {
                    if (vnode2.dom.clientWidth === vnode2.dom.scrollWidth) {
                        vnode2.dom.classList.remove("long-text-overflowed");
                        vnode2.dom.onclick = null;
                    }
                    else {
                        overflowed(vnode2);
                    }
                },
                class: "long-text",
                title: text
            }, text);
        }
    };
};

/**
 * Copyright 2013-2019  GenieACS Inc.
 *
 * This file is part of GenieACS.
 *
 * GenieACS is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * GenieACS is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with GenieACS.  If not, see <http://www.gnu.org/licenses/>.
 */
const comps = {
    parameter: component$5,
    "parameter-list": component$6,
    "parameter-table": component$7,
    "overview-dot": component$8,
    container: component$9,
    "summon-button": component$a,
    "device-faults": component$b,
    "all-parameters": component$c,
    "device-actions": component$d,
    tags: component$e,
    ping: component$f,
    "device-link": component$g,
    "long-text": component$h
};
const contextifiedComponents = new WeakMap();
const vnodeContext = new WeakMap();
const M = new Proxy(m, {
    apply: (target, thisArg, argumentsList) => {
        const c = argumentsList[0];
        if (typeof c !== "string")
            argumentsList[0] = contextifyComponent(c);
        else if (comps[c])
            argumentsList[0] = contextifyComponent(comps[c]);
        return Reflect.apply(target, undefined, argumentsList);
    },
    get: (target, prop) => {
        if (prop === "context")
            return contextFn;
        else
            return Reflect.get(target, prop);
    }
});
function contextFn(context, ...argumentsList) {
    const vnode = Reflect.apply(M, undefined, argumentsList);
    vnodeContext.set(vnode, context);
    return vnode;
}
function applyContext(vnode, parentContext) {
    if (Array.isArray(vnode)) {
        for (const c of vnode)
            applyContext(c, parentContext);
    }
    else if (vnode && typeof vnode === "object" && vnode.tag) {
        const vc = Object.assign({}, parentContext, vnodeContext.get(vnode));
        vnodeContext.set(vnode, vc);
        vnode.attrs = Object.assign({}, vc, vnode.attrs);
        if (vnode.children && vnode.children.length)
            for (const c of vnode.children)
                applyContext(c, vc);
    }
}
function contextifyComponent(component) {
    let c = contextifiedComponents.get(component);
    if (!c) {
        if (typeof component !== "function") {
            c = Object.assign({}, component);
            const view = component.view;
            c.view = function (vnode) {
                const context = vnodeContext.get(vnode) || {};
                const res = Reflect.apply(view, this, [vnode]);
                applyContext(res, context);
                return res;
            };
        }
        else if (!component.prototype || !component.prototype.view) {
            c = initialNode => {
                const state = component(initialNode);
                const view = state.view;
                state.view = function (vnode) {
                    const context = vnodeContext.get(vnode) || {};
                    const res = Reflect.apply(view, this, [vnode]);
                    applyContext(res, context);
                    return res;
                };
                return state;
            };
        }
        else {
            // TODO support class components
            throw new Error("Class components not supported");
        }
        contextifiedComponents.set(component, c);
    }
    return c;
}

/**
 * Copyright 2013-2019  GenieACS Inc.
 *
 * This file is part of GenieACS.
 *
 * GenieACS is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * GenieACS is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with GenieACS.  If not, see <http://www.gnu.org/licenses/>.
 */
const component$i = () => {
    return {
        view: vnode => {
            const onPasswordChange = vnode.attrs["onPasswordChange"];
            const enforceAuth = !vnode.attrs["noAuth"];
            const username = vnode.attrs["username"];
            if (username)
                vnode.state["username"] = username;
            const form = [
                M("p", M("label", { for: "username" }, "Username"), M("input", {
                    name: "username",
                    type: "text",
                    value: vnode.state["username"],
                    disabled: !!username,
                    oninput: M.withAttr("value", v => (vnode.state["username"] = v)),
                    oncreate: _vnode => {
                        _vnode.dom.focus();
                    }
                }))
            ];
            let fields = {
                newPassword: "New password",
                confirmPassword: "Confirm password"
            };
            if (enforceAuth)
                fields = Object.assign({ authPassword: "Your password" }, fields);
            for (const [f, l] of Object.entries(fields)) {
                form.push(M("p", M("label", { for: f }, l), M("input", {
                    name: f,
                    type: "password",
                    value: vnode.state[f],
                    oninput: M.withAttr("value", v => (vnode.state[f] = v))
                })));
            }
            const submit = M("button.primary", {
                type: "submit"
            }, "Change password");
            form.push(M(".actions-bar", submit));
            const children = [
                M("h1", "Change password"),
                M("form", {
                    onsubmit: e => {
                        e.redraw = false;
                        e.preventDefault();
                        if (!vnode.state["username"] ||
                            !vnode.state["newPassword"] ||
                            (enforceAuth && !vnode.state["authPassword"])) {
                            push("error", "Please fill all fields");
                        }
                        else if (vnode.state["newPassword"] !== vnode.state["confirmPassword"]) {
                            push("error", "Password confirm doesn't match new password");
                        }
                        else {
                            submit.dom.disabled = true;
                            changePassword(vnode.state["username"], vnode.state["newPassword"], vnode.state["authPassword"])
                                .then(() => {
                                push("success", "Password updated successfully");
                                if (onPasswordChange)
                                    onPasswordChange();
                                submit.dom.disabled = false;
                            })
                                .catch(err => {
                                push("error", err.message);
                                submit.dom.disabled = false;
                            });
                        }
                    }
                }, form)
            ];
            return M("div.put-form", children);
        }
    };
};

/**
 * Copyright 2013-2019  GenieACS Inc.
 *
 * This file is part of GenieACS.
 *
 * GenieACS is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * GenieACS is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with GenieACS.  If not, see <http://www.gnu.org/licenses/>.
 */
function init(args) {
    return Promise.resolve(args);
}
const component$j = () => {
    return {
        view: vnode => {
            if (window.username)
                M.route.set(vnode.attrs["continue"] || "/");
            document.title = "Login - GenieACS";
            return [
                M("h1", "Log in"),
                M("form", M("p", M("label", { for: "username" }, "Username"), M("input", {
                    name: "username",
                    type: "text",
                    value: vnode.state["username"],
                    oncreate: vnode2 => {
                        vnode2.dom.focus();
                    },
                    oninput: M.withAttr("value", v => (vnode.state["username"] = v))
                })), M("p", M("label", { for: "password" }, "Password"), M("input", {
                    name: "password",
                    type: "password",
                    value: vnode.state["password"],
                    oninput: M.withAttr("value", v => (vnode.state["password"] = v))
                })), M("p", M("button.primary", {
                    type: "submit",
                    onclick: e => {
                        e.target.disabled = true;
                        logIn(vnode.state["username"], vnode.state["password"])
                            .then(() => {
                            location.reload();
                        })
                            .catch(err => {
                            push("error", err.message);
                            e.target.disabled = false;
                        });
                        return false;
                    }
                }, "Login"))),
                M("a", {
                    onclick: () => {
                        const cb = () => {
                            const attrs = {
                                onPasswordChange: () => {
                                    close(cb);
                                    M.redraw();
                                }
                            };
                            return M(component$i, attrs);
                        };
                        open(cb);
                    }
                }, "Change password")
            ];
        }
    };
};

const loginPage = /*#__PURE__*/Object.freeze({
  init: init,
  component: component$j
});

/**
 * Copyright 2013-2019  GenieACS Inc.
 *
 * This file is part of GenieACS.
 *
 * GenieACS is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * GenieACS is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with GenieACS.  If not, see <http://www.gnu.org/licenses/>.
 */
const memoizedStringify$1 = memoize(stringify);
function drawChart(chartData) {
    const slices = chartData.slices;
    const total = Array.from(Object.values(chartData.slices)).reduce((a, s) => a + (s["count"]["value"] || 0), 0);
    const legend = [];
    const paths = [];
    const links = [];
    let currentProgressPercentage = 0;
    let startX = Math.cos(2 * Math.PI * currentProgressPercentage) * 100;
    let startY = Math.sin(2 * Math.PI * currentProgressPercentage) * 100;
    let endX, endY;
    for (const slice of Object.values(slices)) {
        const percent = total > 0 ? (slice["count"]["value"] || 0) / total : 0;
        legend.push(M(".legend-line", [
            M("span.color", {
                style: `background-color: ${slice["color"]} !important;`
            }),
            `${slice["label"]}: `,
            M("a", {
                href: `#!/devices/?${M.buildQueryString({
                    filter: memoizedStringify$1(slice["filter"])
                })}`
            }, slice["count"]["value"] || 0),
            ` (${(percent * 100).toFixed(2)}%)`
        ]));
        if (percent > 0) {
            currentProgressPercentage += percent;
            endX = Math.cos(2 * Math.PI * currentProgressPercentage) * 100;
            endY = Math.sin(2 * Math.PI * currentProgressPercentage) * 100;
            const isBigArc = percent > 0.5 ? 1 : 0;
            const sketch = `M ${startX} ${startY} ` + // Move to the starting point
                `A 100 100 0 ${isBigArc} 1 ${endX} ${endY} ` + // Draw an Arc from starting point to ending point
                `L 0 0 z`; // complete the shape by drawing a line to the center of circle
            startX = endX;
            startY = endY;
            paths.push(M("path", {
                d: sketch,
                fill: slice["color"]
            }));
            const percentageX = Math.cos(2 * Math.PI * (currentProgressPercentage - percent / 2)) * 50;
            const percentageY = Math.sin(2 * Math.PI * (currentProgressPercentage - percent / 2)) * 50;
            links.push(M("a", {
                "xlink:href": `#!/devices/?${M.buildQueryString({
                    filter: memoizedStringify$1(slice["filter"])
                })}`
            }, [
                M("path", {
                    d: sketch,
                    "fill-opacity": 0
                }),
                M("text", {
                    x: percentageX,
                    y: percentageY,
                    "dominant-baseline": "middle",
                    "text-anchor": "middle"
                }, `${(percent * 100).toFixed(2)}%`)
            ]));
        }
    }
    legend.push(M("span.legend-total", `Total: ${total}`));
    return M("div", { class: "pie-chart" }, [
        M("svg", {
            // Adding 2 as padding; strokes must not be more than 2
            viewBox: "-102 -102 204 204",
            width: "204px",
            height: "204px",
            xmlns: "http://www.w3.org/2000/svg",
            "xmlns:xlink": "http://www.w3.org/1999/xlink"
        }, paths.concat(links)),
        M(".legend", legend)
    ]);
}
const component$k = () => {
    return {
        view: vnode => {
            return drawChart(vnode.attrs["chart"]);
        }
    };
};

/**
 * Copyright 2013-2019  GenieACS Inc.
 *
 * This file is part of GenieACS.
 *
 * GenieACS is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * GenieACS is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with GenieACS.  If not, see <http://www.gnu.org/licenses/>.
 */
const GROUPS = config.ui.overview.groups;
const CHARTS$1 = {};
for (const group of Object.values(GROUPS)) {
    for (const chartName of Object.values(group["charts"]))
        CHARTS$1[chartName] = config.ui.overview.charts[chartName];
}
function queryCharts(charts) {
    charts = Object.assign({}, charts);
    for (let [chartName, chart] of Object.entries(charts)) {
        charts[chartName] = chart = Object.assign({}, chart);
        chart["slices"] = Object.assign({}, chart["slices"]);
        for (let [sliceName, slice] of Object.entries(chart["slices"])) {
            const filter = slice["filter"];
            chart["slices"][sliceName] = slice = Object.assign({}, slice);
            slice["count"] = count("devices", filter);
        }
    }
    return charts;
}
function init$1() {
    if (!window.authorizer.hasAccess("devices", 1)) {
        return Promise.reject(new Error("You are not authorized to view this page"));
    }
    return Promise.resolve({ charts: queryCharts(CHARTS$1) });
}
const component$l = () => {
    return {
        view: vnode => {
            document.title = "Overview - GenieACS";
            const children = [];
            for (const group of Object.values(GROUPS)) {
                if (group["label"])
                    children.push(M("h1", group["label"]));
                const groupChildren = [];
                for (const chartName of Object.values(group["charts"])) {
                    const chart = vnode.attrs["charts"][chartName];
                    const chartChildren = [];
                    if (chart.label)
                        chartChildren.push(M("h2", chart.label));
                    const attrs = {};
                    attrs["chart"] = chart;
                    chartChildren.push(M(component$k, attrs));
                    groupChildren.push(M(".overview-chart", chartChildren));
                }
                children.push(M(".overview-chart-group", groupChildren));
            }
            return children;
        }
    };
};

const overviewPage = /*#__PURE__*/Object.freeze({
  init: init$1,
  component: component$l
});

/**
 * Copyright 2013-2019  GenieACS Inc.
 *
 * This file is part of GenieACS.
 *
 * GenieACS is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * GenieACS is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with GenieACS.  If not, see <http://www.gnu.org/licenses/>.
 */
function renderTable(attributes, data, total, showMoreCallback, selected, sortAttributes, onSortChange, downloadUrl, valueCallback, actionsCallback, recordActionsCallback) {
    const records = data || [];
    const selectAll = M("input", {
        type: "checkbox",
        checked: records.length && selected.size === records.length,
        onchange: e => {
            for (const record of records) {
                const id = record["_id"] || record["DeviceID.ID"].value[0];
                if (e.target.checked)
                    selected.add(id);
                else
                    selected.delete(id);
            }
        },
        disabled: !total
    });
    // Table header
    const labels = [M("th", selectAll)];
    for (let i = 0; i < attributes.length; i++) {
        const attr = attributes[i];
        const label = attr.label;
        if (!sortAttributes.hasOwnProperty(i)) {
            labels.push(M("th", label));
            continue;
        }
        let direction = 1;
        let symbol = "\u2981";
        if (sortAttributes[i] > 0)
            symbol = "\u2bc6";
        else if (sortAttributes[i] < 0)
            symbol = "\u2bc5";
        const sortable = M("button", {
            onclick: () => {
                if (sortAttributes[i] > 0)
                    direction *= -1;
                return onSortChange({ [i]: direction });
            }
        }, symbol);
        labels.push(M("th", [label, sortable]));
    }
    // Table rows
    const rows = [];
    for (const record of records) {
        const id = record["_id"] || record["DeviceID.ID"].value[0];
        const checkbox = M("input", {
            type: "checkbox",
            checked: selected.has(id),
            onchange: e => {
                if (e.target.checked)
                    selected.add(id);
                else
                    selected.delete(id);
            },
            onclick: e => {
                e.stopPropagation();
                e.redraw = false;
            }
        });
        const tds = [M("td", checkbox)];
        for (const attr of attributes) {
            const attrs = {};
            let valueComponent;
            if (typeof valueCallback === "function") {
                valueComponent = valueCallback(attr, record);
            }
            else if (attr.type === "code") {
                const firstLines = record[attr.id].split("\n", 11);
                if (firstLines.length > 10)
                    firstLines[10] = ["\ufe19"];
                if (attrs["title"] == null)
                    attrs["title"] = firstLines.join("\n");
                valueComponent = firstLines[0] || "";
            }
            else {
                valueComponent = record[attr.id];
            }
            // TODO automatically add long text component on long values
            tds.push(M("td", attrs, valueComponent));
        }
        let recordButtons = [];
        if (typeof recordActionsCallback === "function") {
            recordButtons = recordActionsCallback(record);
            if (!Array.isArray(recordButtons))
                recordButtons = [recordButtons];
        }
        else if (Array.isArray(recordActionsCallback)) {
            recordButtons = recordActionsCallback;
        }
        for (const button of recordButtons)
            tds.push(M("td.table-row-links", button));
        rows.push(M("tr", {
            onclick: e => {
                if (["INPUT", "BUTTON", "A"].includes(e.target.nodeName)) {
                    e.redraw = false;
                    return;
                }
                if (!selected.delete(id))
                    selected.add(id);
            }
        }, tds));
    }
    if (!rows.length)
        rows.push(M("tr.empty", M("td", { colspan: labels.length }, "No records")));
    // Table footer
    const footerElements = [];
    if (total != null)
        footerElements.push(`${records.length}/${total}`);
    else
        footerElements.push(`${records.length}`);
    footerElements.push(M("button", {
        title: "Show more records",
        onclick: showMoreCallback,
        disabled: !data.length || records.length >= total
    }, "More"));
    if (downloadUrl) {
        footerElements.push(M("a.download-csv", { href: downloadUrl, download: "" }, "Download"));
    }
    const tfoot = M("tfoot", M("tr", M("td", { colspan: labels.length }, footerElements)));
    // Actions bar
    let buttons = [];
    if (typeof actionsCallback === "function") {
        buttons = actionsCallback(selected);
        if (!Array.isArray(buttons))
            buttons = [buttons];
    }
    else if (Array.isArray(actionsCallback)) {
        buttons = actionsCallback;
    }
    const children = [
        M("table.table.highlight", M("thead", M("tr", labels)), M("tbody", rows), tfoot)
    ];
    if (buttons.length)
        children.push(M("div.actions-bar", buttons));
    return children;
}
const component$m = () => {
    let selected = new Set();
    return {
        view: vnode => {
            const attributes = vnode.attrs["attributes"];
            const data = vnode.attrs["data"];
            const valueCallback = vnode.attrs["valueCallback"];
            const total = vnode.attrs["total"];
            const showMoreCallback = vnode.attrs["showMoreCallback"];
            const sortAttributes = vnode.attrs["sortAttributes"];
            const onSortChange = vnode.attrs["onSortChange"];
            const downloadUrl = vnode.attrs["downloadUrl"];
            const actionsCallback = vnode.attrs["actionsCallback"];
            const recordActionsCallback = vnode.attrs["recordActionsCallback"];
            const _selected = new Set();
            for (const record of data) {
                const id = record["_id"] || record["DeviceID.ID"].value[0];
                if (selected.has(id))
                    _selected.add(id);
            }
            selected = _selected;
            return renderTable(attributes, data, total, showMoreCallback, selected, sortAttributes, onSortChange, downloadUrl, valueCallback, actionsCallback, recordActionsCallback);
        }
    };
};

/**
 * Copyright 2013-2019  GenieACS Inc.
 *
 * This file is part of GenieACS.
 *
 * GenieACS is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * GenieACS is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with GenieACS.  If not, see <http://www.gnu.org/licenses/>.
 */
class Autocomplete {
    constructor(className, callback) {
        this.callback = callback;
        this.element = null;
        this.hideTimeout = null;
        this.visible = false;
        this.default = null;
        this.selection = null;
        this.container = document.createElement("div");
        this.container.style.position = "absolute";
        this.container.style.display = "block";
        this.container.style.opacity = "0";
        this.container.className = className;
    }
    attach(el) {
        el.setAttribute("autocomplete", "off");
        el.addEventListener("focus", () => {
            this.element = el;
            const domRect = el.getBoundingClientRect();
            this.container.style.left = `${domRect.left + window.pageXOffset}px`;
            this.container.style.width = `${domRect.width}px`;
            this.container.style.top = `${domRect.bottom + window.pageYOffset}px`;
            this.update();
        });
        el.addEventListener("blur", () => {
            if (this.element !== el)
                return;
            if (!this.visible)
                return;
            this.hide();
        });
        el.addEventListener("keydown", e => {
            if (this.element !== el)
                return;
            if (e.key === "Escape") {
                if (this.visible)
                    this.hide();
            }
            else if (e.key === "Enter") {
                if (this.default != null) {
                    el.value = this.default;
                    e.preventDefault();
                    this.update();
                }
            }
            else if (e.key === "ArrowDown") {
                e.preventDefault();
                if (this.selection == null)
                    this.selection = 0;
                else
                    ++this.selection;
                this.update();
            }
            else if (e.key === "ArrowUp") {
                e.preventDefault();
                --this.selection;
                this.update();
            }
        });
        el.addEventListener("input", () => {
            if (this.element !== el)
                return;
            this.selection = null;
            this.update();
        });
    }
    hide() {
        this.container.style.opacity = "0";
        this.visible = false;
        this.default = null;
        this.selection = null;
        clearTimeout(this.hideTimeout);
        this.hideTimeout = setTimeout(() => {
            this.hideTimeout = null;
            while (this.container.firstChild)
                this.container.removeChild(this.container.firstChild);
            document.body.removeChild(this.container);
        }, 500);
    }
    update() {
        const el = this.element;
        this.callback(el.value, suggestions => {
            if (this.element !== el)
                return;
            this.default = null;
            if (!suggestions.length) {
                if (this.visible)
                    this.hide();
                return;
            }
            while (this.container.firstChild)
                this.container.removeChild(this.container.firstChild);
            if (!this.visible) {
                if (!this.hideTimeout) {
                    document.body.appendChild(this.container);
                    window.getComputedStyle(this.container).opacity;
                }
                else {
                    clearTimeout(this.hideTimeout);
                    this.hideTimeout = null;
                }
                this.container.style.opacity = "1";
                this.visible = true;
            }
            if (this.selection != null) {
                this.selection =
                    ((this.selection % suggestions.length) + suggestions.length) %
                        suggestions.length;
                this.default = suggestions[this.selection];
            }
            else {
                this.default = suggestions[0];
            }
            let selectedElement;
            for (const [idx, suggestion] of suggestions.entries()) {
                const e = document.createElement("div");
                e.classList.add("suggestion");
                if (idx === this.selection) {
                    e.classList.add("selected");
                    selectedElement = e;
                }
                const t = document.createTextNode(suggestion);
                e.appendChild(t);
                e.addEventListener("mousedown", ev => {
                    ev.preventDefault();
                    el.value = suggestion;
                    if (this.element === el)
                        this.update();
                });
                this.container.appendChild(e);
            }
            // Ensure selected element is in view
            if (selectedElement) {
                this.container.scrollTop = Math.min(this.container.scrollTop, selectedElement.offsetTop);
                this.container.scrollTop = Math.max(this.container.scrollTop, selectedElement.offsetTop +
                    selectedElement.scrollHeight -
                    this.container.clientHeight);
            }
        });
    }
}

/**
 * Copyright 2013-2019  GenieACS Inc.
 *
 * This file is part of GenieACS.
 *
 * GenieACS is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * GenieACS is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with GenieACS.  If not, see <http://www.gnu.org/licenses/>.
 */
const resources$1 = {
    devices: {},
    faults: {
        Device: { parameter: ["PARAM", "device"], type: "string" },
        Channel: { parameter: ["PARAM", "channel"], type: "string" },
        Code: { parameter: ["PARAM", "code"], type: "string" },
        Retries: { parameter: ["PARAM", "retries"], type: "number" },
        Timestamp: { parameter: ["PARAM", "timestamp"], type: "timestamp" }
    },
    presets: {
        ID: { parameter: ["PARAM", "_id"], type: "string" },
        Channel: { parameter: ["PARAM", "channel"], type: "string" },
        Weight: { parameter: ["PARAM", "weight"], type: "number" }
    },
    provisions: {
        ID: { parameter: ["PARAM", "_id"], type: "string" }
    },
    virtualParameters: {
        ID: { parameter: ["PARAM", "_id"], type: "string" }
    },
    files: {
        ID: { parameter: ["PARAM", "_id"], type: "string" },
        Type: { parameter: ["PARAM", "metadata.fileType"], type: "string" },
        OUI: { parameter: ["PARAM", "metadata.oui"], type: "string" },
        "Product class": {
            parameter: ["PARAM", "metadata.productClass"],
            type: "string"
        },
        Version: { parameter: ["PARAM", "metadata.version"], type: "string" }
    },
    permissions: {
        Role: { parameter: ["PARAM", "role"], type: "string" },
        Resource: { parameter: ["PARAM", "resource"], type: "string" },
        Access: { parameter: ["PARAM", "access"], type: "number" }
    },
    users: { Username: { parameter: ["PARAM", "_id"], type: "string" } }
};
for (const v of Object.values(config.ui.filters)) {
    resources$1.devices[v.label] = {
        parameter: v.parameter,
        type: (v.type || "").split(",").map(s => s.trim())
    };
}
function getLabels(resource) {
    if (!resources$1[resource])
        return [];
    return Object.keys(resources$1[resource]);
}
function queryNumber(param, value) {
    let op = "=";
    for (const o of ["<>", "=", "<=", "<", ">=", ">"]) {
        if (value.startsWith(o)) {
            op = o;
            value = value.slice(o.length).trim();
            break;
        }
    }
    const v = parseInt(value);
    if (v !== +value)
        return null;
    return [op, param, v];
}
function queryTimestamp(param, value) {
    let op = "=";
    for (const o of ["<>", "=", "<=", "<", ">=", ">"]) {
        if (value.startsWith(o)) {
            op = o;
            value = value.slice(o.length).trim();
            break;
        }
    }
    let v = parseInt(value);
    if (v !== +value)
        v = Date.parse(value);
    if (isNaN(v))
        return null;
    return [op, param, v];
}
function queryString(param, value) {
    return ["LIKE", ["FUNC", "LOWER", param], value.toLowerCase()];
}
function queryMac(param, value) {
    value = value.replace(/[^a-f0-9]/gi, "").toLowerCase();
    if (!value)
        return null;
    if (value.length === 12) {
        return [
            "LIKE",
            ["FUNC", "LOWER", param],
            value.replace(/(..)(?!$)/g, "$1:")
        ];
    }
    return [
        "OR",
        [
            "LIKE",
            ["FUNC", "LOWER", param],
            `%${value.replace(/(..)(?!$)/g, "$1:")}%`
        ],
        ["LIKE", ["FUNC", "LOWER", param], `%${value.replace(/(.)(.)/g, "$1:$2")}%`]
    ];
}
function unpack(resource, label, value) {
    if (!resources$1[resource])
        return null;
    const type = resources$1[resource][label].type;
    value = value.trim();
    const res = ["OR"];
    if (type.length === 0 || type.includes("number")) {
        const q = queryNumber(resources$1[resource][label].parameter, value);
        if (q)
            res.push(q);
    }
    if (type.length === 0 || type.includes("string")) {
        const q = queryString(resources$1[resource][label].parameter, value);
        if (q)
            res.push(q);
    }
    if (type.length === 0 || type.includes("timestamp")) {
        const q = queryTimestamp(resources$1[resource][label].parameter, value);
        if (q)
            res.push(q);
    }
    if (type.includes("mac")) {
        const q = queryMac(resources$1[resource][label].parameter, value);
        if (q)
            res.push(q);
    }
    if (res.length <= 1)
        return null;
    else if (res.length === 2)
        return res[1];
    else
        return res;
}

/**
 * Copyright 2013-2019  GenieACS Inc.
 *
 * This file is part of GenieACS.
 *
 * GenieACS is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * GenieACS is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with GenieACS.  If not, see <http://www.gnu.org/licenses/>.
 */
const isArray$1 = Array.isArray;
function filterToMongoQuery(exp) {
    const ops = {
        OR: 0,
        AND: 0,
        NOT: 0,
        "=": 1,
        "<>": 1,
        ">": 1,
        ">=": 1,
        "<": 1,
        "<=": 1,
        LIKE: 1,
        "NOT LIKE": 1,
        "IS NULL": 1,
        "IS NOT NULL": 1
    };
    function recursive(filter, negate, res = {}) {
        const op = filter[0];
        if (ops[op] === 0) {
            for (let i = 1; i < filter.length; ++i) {
                if (!isArray$1(filter[i]) || ops[filter[i][0]] == null)
                    throw new Error(`Invalid expression in ${op} clause`);
            }
            if ((!negate && op === "AND") || (negate && op === "OR")) {
                res["$and"] = res["$and"] || [];
                for (let i = 1; i < filter.length; ++i)
                    res["$and"].push(recursive(filter[i], negate));
            }
            else if ((!negate && op === "OR") || (negate && op === "AND")) {
                res["$or"] = res["$or"] || [];
                for (let i = 1; i < filter.length; ++i)
                    res["$or"].push(recursive(filter[i], negate));
            }
            else if (op === "NOT") {
                recursive(filter[1], !negate, res);
            }
        }
        else if (ops[op] === 1) {
            if (isArray$1(filter[2])) {
                throw new Error(`Right operand of ${op} clause is not a primitive`);
            }
            else if (["LIKE", "NOT LIKE"].includes(op) &&
                isArray$1(filter[1]) &&
                filter[1][0] === "FUNC" &&
                ["UPPER", "LOWER"].includes(filter[1][1])) {
                if ((filter[1][1] === "UPPER" && filter[2] !== filter[2].toUpperCase()) ||
                    (filter[1][1] === "LOWER" && filter[2] !== filter[2].toLowerCase())) {
                    throw new Error(`Cannot compare ${filter[1][1]}() against non-${filter[1][1].toLowerCase()}case pattern`);
                }
            }
            else if (!isArray$1(filter[1]) || filter[1][0] !== "PARAM") {
                throw new Error(`Left operand of ${op} clause is not a parameter`);
            }
            if (op === "=") {
                const param = filter[1][1];
                const p = (res[param] = res[param] || {});
                if (negate)
                    p["$ne"] = filter[2];
                else
                    p["$eq"] = filter[2];
            }
            else if (op === "<>") {
                const param = filter[1][1];
                let p = (res[param] = res[param] || {});
                if (negate)
                    p = p["$not"] = p["$not"] || {};
                p["$ne"] = filter[2];
                if (param !== "_tags")
                    p["$exists"] = true;
            }
            else if (op === ">") {
                const param = filter[1][1];
                let p = (res[param] = res[param] || {});
                if (negate)
                    p = p["$not"] = p["$not"] || {};
                p["$gt"] = filter[2];
            }
            else if (op === ">=") {
                const param = filter[1][1];
                let p = (res[param] = res[param] || {});
                if (negate)
                    p = p["$not"] = p["$not"] || {};
                p["$gte"] = filter[2];
            }
            else if (op === "<") {
                const param = filter[1][1];
                let p = (res[param] = res[param] || {});
                if (negate)
                    p = p["$not"] = p["$not"] || {};
                p["$lt"] = filter[2];
            }
            else if (op === "<=") {
                const param = filter[1][1];
                let p = (res[param] = res[param] || {});
                if (negate)
                    p = p["$not"] = p["$not"] || {};
                p["$lte"] = filter[2];
            }
            else if (op === "IS NULL") {
                const param = filter[1][1];
                res[param] = { $exists: negate };
            }
            else if (op === "IS NOT NULL") {
                const param = filter[1][1];
                res[param] = { $exists: !negate };
            }
            else {
                if (op === "NOT LIKE")
                    negate = !negate;
                let param;
                let flags;
                if (filter[1][0] === "FUNC" && filter[1][1] === "UPPER") {
                    param = filter[1][2][1];
                    flags = "i";
                }
                else if (filter[1][0] === "FUNC" && filter[1][1] === "LOWER") {
                    param = filter[1][2][1];
                    flags = "i";
                }
                else {
                    param = filter[1][1];
                    flags = "";
                }
                const r = likePatternToRegExp(filter[2], filter[3], flags);
                if (negate)
                    res[param] = { $not: r };
                else
                    res[param] = r;
            }
        }
        else {
            throw new Error(`Unrecognized operator ${op}`);
        }
        return res;
    }
    const _exp = evaluate(exp, null, Date.now());
    if (!isArray$1(_exp)) {
        if (_exp === true)
            return {};
        throw new Error("Primitives are not valid queries");
    }
    return recursive(_exp, false);
}

/**
 * Copyright 2013-2019  GenieACS Inc.
 *
 * This file is part of GenieACS.
 *
 * GenieACS is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * GenieACS is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with GenieACS.  If not, see <http://www.gnu.org/licenses/>.
 */
const getAutocomplete = memoize(resource => {
    const labels = getLabels(resource);
    const autocomplete = new Autocomplete("autocomplete", (txt, cb) => {
        txt = txt.toLowerCase();
        cb(labels.filter(s => s.toLowerCase().includes(txt)).map(s => `${s}: `));
    });
    return autocomplete;
});
function parseFilter(resource, f) {
    let exp;
    if (/^[\s0-9a-zA-Z]+:/.test(f)) {
        const k = f.split(":", 1)[0];
        const v = f.slice(k.length + 1).trim();
        exp = ["FUNC", "Q", k.trim(), v];
    }
    else {
        exp = parse(f);
    }
    // Throw exception if invalid Mongo query
    filterToMongoQuery(map(exp, e => {
        if (Array.isArray(e) && e[0] === "FUNC" && e[1] === "Q")
            return unpack(resource, e[2], e[3]);
        return e;
    }));
    return exp;
}
function stringifyFilter(f) {
    if (Array.isArray(f) && f[0] === "FUNC" && f[1] === "Q")
        return `${f[2]}: ${f[3]}`;
    return stringify(f);
}
const splitFilter = memoize(filter => {
    if (!filter)
        return [""];
    const list = [];
    const f = parse(filter);
    if (Array.isArray(f) && f[0] === "AND")
        for (const ff of f.slice(1))
            list.push(stringifyFilter(ff));
    else
        list.push(stringifyFilter(f));
    list.push("");
    return list;
});
const component$n = () => {
    return {
        view: vnode => {
            if (!vnode.state["filterList"] ||
                vnode.attrs["filter"] !== vnode.state["filter"]) {
                vnode.state["filterInvalid"] = 0;
                vnode.state["filter"] = vnode.attrs["filter"];
                vnode.state["filterList"] = splitFilter(vnode.attrs["filter"]);
            }
            function onChange() {
                vnode.state["filterInvalid"] = 0;
                vnode.state["filterList"] = vnode.state["filterList"].filter(f => f);
                let filter = vnode.state["filterList"].map((f, idx) => {
                    try {
                        return parseFilter(vnode.attrs["resource"], f);
                    }
                    catch (err) {
                        vnode.state["filterInvalid"] |= 1 << idx;
                    }
                    return null;
                });
                vnode.state["filterList"].push("");
                if (!vnode.state["filterInvalid"]) {
                    delete vnode.state["filter"];
                    if (filter.length === 0) {
                        vnode.attrs["onChange"]("");
                    }
                    else {
                        if (filter.length > 1)
                            filter = ["AND"].concat(filter);
                        else
                            filter = filter[0];
                        vnode.attrs["onChange"](stringify(filter));
                    }
                }
            }
            return m("div.filter", [m("b", "Filter")].concat(vnode.state["filterList"].map((fltr, idx) => {
                return m("input", {
                    type: "text",
                    class: `${(vnode.state["filterInvalid"] >> idx) & 1 ? "error" : ""}`,
                    value: fltr,
                    onchange: e => {
                        vnode.state["filterList"] = vnode.state["filterList"].slice();
                        vnode.state["filterList"][idx] = e.target.value.trim();
                        onChange();
                    },
                    oncreate: vn => {
                        getAutocomplete(vnode.attrs["resource"]).attach(vn.dom);
                    }
                });
            })));
        }
    };
};

/**
 * Copyright 2013-2019  GenieACS Inc.
 *
 * This file is part of GenieACS.
 *
 * GenieACS is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * GenieACS is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with GenieACS.  If not, see <http://www.gnu.org/licenses/>.
 */
const PAGE_SIZE = config.ui.pageSize || 10;
const memoizedParse$1 = memoize(parse);
const memoizedJsonParse = memoize(JSON.parse);
const memoizedGetSortable = memoize(p => {
    const expressionParams = extractParams(p);
    if (expressionParams.length === 1) {
        const param = evaluate(expressionParams[0]);
        if (typeof param === "string")
            return param;
    }
    return null;
});
const getDownloadUrl = memoize((filter, indexParameters) => {
    const columns = {};
    for (const p of indexParameters)
        columns[p.label] = stringify(p.parameter);
    return `/api/devices.csv?${M.buildQueryString({
        filter: stringify(filter),
        columns: JSON.stringify(columns)
    })}`;
});
const unpackSmartQuery = memoize(query => {
    return map(query, e => {
        if (Array.isArray(e) && e[0] === "FUNC" && e[1] === "Q")
            return unpack("devices", e[2], e[3]);
        return e;
    });
});
function init$2(args) {
    return new Promise((resolve, reject) => {
        if (!window.authorizer.hasAccess("devices", 2))
            return void reject(new Error("You are not authorized to view this page"));
        const filter = args.hasOwnProperty("filter") ? "" + args["filter"] : "";
        const sort = args.hasOwnProperty("sort") ? "" + args["sort"] : "";
        const indexParameters = Object.values(config.ui.index);
        resolve({ filter, indexParameters, sort });
    });
}
function renderActions(selected) {
    const buttons = [];
    buttons.push(M("button.primary", {
        title: "Reboot selected devices",
        disabled: !selected.size,
        onclick: () => {
            for (const d of selected) {
                queueTask({
                    name: "reboot",
                    device: d
                });
            }
        }
    }, "Reboot"));
    buttons.push(M("button.critical", {
        title: "Factory reset selected devices",
        disabled: !selected.size,
        onclick: () => {
            for (const d of selected) {
                queueTask({
                    name: "factoryReset",
                    device: d
                });
            }
        }
    }, "Reset"));
    buttons.push(M("button.primary", {
        title: "Delete selected devices",
        disabled: !selected.size,
        onclick: () => {
            const ids = Array.from(selected);
            if (!confirm(`Deleting ${ids.length} devices. Are you sure?`))
                return;
            let counter = 1;
            for (const id of ids) {
                ++counter;
                deleteResource("devices", id)
                    .then(() => {
                    push("success", `${id}: Deleted`);
                    if (--counter === 0)
                        fulfill(0, Date.now());
                })
                    .catch(err => {
                    push("error", `${id}: ${err.message}`);
                    if (--counter === 0)
                        fulfill(0, Date.now());
                });
            }
            if (--counter === 0)
                fulfill(0, Date.now());
        }
    }, "Delete"));
    buttons.push(M("button.primary", {
        title: "Tag selected devices",
        disabled: !selected.size,
        onclick: () => {
            const ids = Array.from(selected);
            const tag = prompt(`Enter tag to assign to ${ids.length} devices:`);
            if (!tag)
                return;
            let counter = 1;
            for (const id of ids) {
                ++counter;
                updateTags(id, { [tag]: true })
                    .then(() => {
                    push("success", `${id}: Tags updated`);
                    if (--counter === 0)
                        fulfill(0, Date.now());
                })
                    .catch(err => {
                    push("error", `${id}: ${err.message}`);
                    if (--counter === 0)
                        fulfill(0, Date.now());
                });
            }
            if (--counter === 0)
                fulfill(0, Date.now());
        }
    }, "Tag"));
    buttons.push(M("button.primary", {
        title: "Untag selected devices",
        disabled: !selected.size,
        onclick: () => {
            const ids = Array.from(selected);
            const tag = prompt(`Enter tag to unassign from ${ids.length} devices:`);
            if (!tag)
                return;
            let counter = 1;
            for (const id of ids) {
                ++counter;
                updateTags(id, { [tag]: false })
                    .then(() => {
                    push("success", `${id}: Tags updated`);
                    if (--counter === 0)
                        fulfill(0, Date.now());
                })
                    .catch(err => {
                    push("error", `${id}: ${err.message}`);
                    if (--counter === 0)
                        fulfill(0, Date.now());
                });
            }
            if (--counter === 0)
                fulfill(0, Date.now());
        }
    }, "Untag"));
    return buttons;
}
const component$o = () => {
    return {
        view: vnode => {
            document.title = "Devices - GenieACS";
            const attributes = vnode.attrs["indexParameters"];
            function showMore() {
                vnode.state["showCount"] =
                    (vnode.state["showCount"] || PAGE_SIZE) + PAGE_SIZE;
                M.redraw();
            }
            function onFilterChanged(filter) {
                const ops = { filter };
                if (vnode.attrs["sort"])
                    ops["sort"] = vnode.attrs["sort"];
                M.route.set("/devices", ops);
            }
            const sort = vnode.attrs["sort"]
                ? memoizedJsonParse(vnode.attrs["sort"])
                : {};
            const sortAttributes = {};
            for (let i = 0; i < attributes.length; i++) {
                const attr = attributes[i];
                if (attr.unsortable)
                    continue;
                const param = memoizedGetSortable(attr.parameter);
                if (param)
                    sortAttributes[i] = sort[param] || 0;
            }
            function onSortChange(sortedAttrs) {
                const _sort = Object.assign({}, sort);
                for (const [index, direction] of Object.entries(sortedAttrs)) {
                    const param = memoizedGetSortable(attributes[index].parameter);
                    if (param) {
                        // Changing the priority of columns
                        delete _sort[param];
                        _sort[param] = direction;
                    }
                }
                const ops = { sort: JSON.stringify(_sort) };
                if (vnode.attrs["filter"])
                    ops["filter"] = vnode.attrs["filter"];
                M.route.set(M.route.get(), ops);
            }
            let filter = vnode.attrs["filter"]
                ? memoizedParse$1(vnode.attrs["filter"])
                : true;
            filter = unpackSmartQuery(filter);
            const devs = fetch("devices", filter, {
                limit: vnode.state["showCount"] || PAGE_SIZE,
                sort: sort
            });
            const count$1 = count("devices", filter);
            const downloadUrl = getDownloadUrl(filter, attributes);
            const valueCallback = (attr, device) => {
                return M.context({ device: device, parameter: attr.parameter }, attr.type || "parameter", attr);
            };
            const attrs = {};
            attrs["attributes"] = attributes;
            attrs["data"] = devs.value;
            attrs["total"] = count$1.value;
            attrs["showMoreCallback"] = showMore;
            attrs["sortAttributes"] = sortAttributes;
            attrs["onSortChange"] = onSortChange;
            attrs["downloadUrl"] = downloadUrl;
            attrs["valueCallback"] = valueCallback;
            attrs["recordActionsCallback"] = (device) => {
                return M("a", {
                    href: `#!/devices/${encodeURIComponent(device["DeviceID.ID"].value[0])}`
                }, "Show");
            };
            if (window.authorizer.hasAccess("devices", 3))
                attrs["actionsCallback"] = renderActions;
            const filterAttrs = {};
            filterAttrs["resource"] = "devices";
            filterAttrs["filter"] = vnode.attrs["filter"];
            filterAttrs["onChange"] = onFilterChanged;
            return [
                M("h1", "Listing devices"),
                M(component$n, filterAttrs),
                M(component$m, attrs)
            ];
        }
    };
};

const devicesPage = /*#__PURE__*/Object.freeze({
  init: init$2,
  component: component$o
});

/**
 * Copyright 2013-2019  GenieACS Inc.
 *
 * This file is part of GenieACS.
 *
 * GenieACS is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * GenieACS is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with GenieACS.  If not, see <http://www.gnu.org/licenses/>.
 */
function init$3(args) {
    if (!window.authorizer.hasAccess("devices", 2)) {
        return Promise.reject(new Error("You are not authorized to view this page"));
    }
    return Promise.resolve({
        deviceId: args.id,
        deviceFilter: ["=", ["PARAM", "DeviceID.ID"], args.id]
    });
}
const component$p = () => {
    return {
        view: vnode => {
            document.title = `${vnode.attrs["deviceId"]} - Devices - GenieACS`;
            const dev = fetch("devices", vnode.attrs["deviceFilter"]).value;
            if (!dev.length)
                return "Loading";
            const conf = config.ui.device;
            const cmps = [];
            for (const c of Object.values(conf))
                cmps.push(M.context({ device: dev[0] }, c["type"], c));
            return [M("h1", vnode.attrs["deviceId"]), cmps];
        }
    };
};

const devicePage = /*#__PURE__*/Object.freeze({
  init: init$3,
  component: component$p
});

/**
 * Copyright 2013-2019  GenieACS Inc.
 *
 * This file is part of GenieACS.
 *
 * GenieACS is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * GenieACS is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with GenieACS.  If not, see <http://www.gnu.org/licenses/>.
 */
const component$q = () => {
    return {
        view: function (vnode) {
            document.title = "Error! - GenieACS";
            return M("p.error", vnode.attrs["error"]);
        }
    };
};

//import * as store from "./store";
//import * as notifications from "./notifications";
//import memoize from "../lib/common/memoize";
//import * as smartQuery from "./smart-query";
//import { map, parse, stringify } from "../lib/common/expression-parser";
//import { loadYaml, yaml } from "./dynamic-loader";
function init$4(args) {
    if (!window.authorizer.hasAccess("map", 2)) {
        return Promise.reject(new Error("You are not authorized to view this page"));
    }
    return Promise.resolve({});
}
const component$r = () => {
    return {
        view: vnode => {
            document.title = "Map - GenieACS";
            return [
                M(".map", { id: "map", style: "height: calc(100vh - 98px);" }, M(".tooltip", { id: "tooltip" })),
                M("script", { src: "/mapview.js" })
            ];
        }
    };
};

const mapPage = /*#__PURE__*/Object.freeze({
  init: init$4,
  component: component$r
});

/**
 * Copyright 2013-2019  GenieACS Inc.
 *
 * This file is part of GenieACS.
 *
 * GenieACS is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * GenieACS is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with GenieACS.  If not, see <http://www.gnu.org/licenses/>.
 */
let codeMirror;
let yaml;
function loadCodeMirror() {
    if (codeMirror)
        return Promise.resolve();
    return new Promise((resolve, reject) => {
        const promises = [
            import(/* webpackChunkName: "codemirror" */ 'codemirror'),
            import(
            /* webpackChunkName: "codemirror" */ 'codemirror/mode/javascript/javascript'),
            import(/* webpackChunkName: "codemirror" */ 'codemirror/mode/yaml/yaml')
        ];
        Promise.all(promises)
            .then(modules => {
            codeMirror = modules[0];
            resolve();
        })
            .catch(reject);
    });
}
function loadYaml() {
    if (yaml)
        return Promise.resolve();
    return new Promise((resolve, reject) => {
        import(/* webpackChunkName: "yaml" */ 'yaml')
            .then(module => {
            yaml = module;
            resolve();
        })
            .catch(reject);
    });
}

/**
 * Copyright 2013-2019  GenieACS Inc.
 *
 * This file is part of GenieACS.
 *
 * GenieACS is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * GenieACS is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with GenieACS.  If not, see <http://www.gnu.org/licenses/>.
 */
const PAGE_SIZE$1 = config.ui.pageSize || 10;
const memoizedParse$2 = memoize(parse);
const memoizedJsonParse$1 = memoize(JSON.parse);
const attributes = [
    { id: "device", label: "Device" },
    { id: "channel", label: "Channel" },
    { id: "code", label: "Code" },
    { id: "message", label: "Message" },
    { id: "detail", label: "Detail" },
    { id: "retries", label: "Retries" },
    { id: "timestamp", label: "Timestamp" }
];
const getDownloadUrl$1 = memoize(filter => {
    const cols = {};
    for (const attr of attributes) {
        cols[attr.label] =
            attr.id === "timestamp" ? `DATE_STRING(${attr.id})` : attr.id;
    }
    return `/api/faults.csv?${M.buildQueryString({
        filter: stringify(filter),
        columns: JSON.stringify(cols)
    })}`;
});
const unpackSmartQuery$1 = memoize(query => {
    return map(query, e => {
        if (Array.isArray(e) && e[0] === "FUNC" && e[1] === "Q")
            return unpack("faults", e[2], e[3]);
        return e;
    });
});
function init$5(args) {
    if (!window.authorizer.hasAccess("faults", 2)) {
        return Promise.reject(new Error("You are not authorized to view this page"));
    }
    const sort = args.hasOwnProperty("sort") ? "" + args["sort"] : "";
    const filter = args.hasOwnProperty("filter") ? "" + args["filter"] : "";
    return new Promise((resolve, reject) => {
        loadYaml()
            .then(() => {
            resolve({ filter, sort });
        })
            .catch(reject);
    });
}
const component$s = () => {
    return {
        view: vnode => {
            document.title = "Faults - GenieACS";
            function showMore() {
                vnode.state["showCount"] =
                    (vnode.state["showCount"] || PAGE_SIZE$1) + PAGE_SIZE$1;
                M.redraw();
            }
            function onFilterChanged(filter) {
                const ops = { filter };
                if (vnode.attrs["sort"])
                    ops["sort"] = vnode.attrs["sort"];
                M.route.set("/faults", ops);
            }
            const sort = vnode.attrs["sort"]
                ? memoizedJsonParse$1(vnode.attrs["sort"])
                : {};
            const sortAttributes = {};
            for (let i = 0; i < attributes.length; i++) {
                const attr = attributes[i];
                if (attr.id !== "detail")
                    sortAttributes[i] = sort[attr.id] || 0;
            }
            function onSortChange(sortAttrs) {
                const _sort = Object.assign({}, sort);
                for (const [index, direction] of Object.entries(sortAttrs)) {
                    // Changing the priority of columns
                    delete _sort[attributes[index].id];
                    _sort[attributes[index].id] = direction;
                }
                const ops = { sort: JSON.stringify(_sort) };
                if (vnode.attrs["filter"])
                    ops["filter"] = vnode.attrs["filter"];
                M.route.set(M.route.get(), ops);
            }
            let filter = vnode.attrs["filter"]
                ? memoizedParse$2(vnode.attrs["filter"])
                : true;
            filter = unpackSmartQuery$1(filter);
            const faults = fetch("faults", filter, {
                limit: vnode.state["showCount"] || PAGE_SIZE$1,
                sort: sort
            });
            const count$1 = count("faults", filter);
            const downloadUrl = getDownloadUrl$1(filter);
            const valueCallback = (attr, fault) => {
                if (attr.id === "device") {
                    const deviceHref = `#!/devices/${encodeURIComponent(fault["device"])}`;
                    return M("a", { href: deviceHref }, fault["device"]);
                }
                if (attr.id === "detail")
                    return M("long-text", { text: yaml.stringify(fault["detail"]) });
                if (attr.id === "timestamp")
                    return new Date(fault["timestamp"]).toLocaleString();
                return fault[attr.id];
            };
            const attrs = {};
            attrs["attributes"] = attributes;
            attrs["data"] = faults.value;
            attrs["valueCallback"] = valueCallback;
            attrs["total"] = count$1.value;
            attrs["showMoreCallback"] = showMore;
            attrs["sortAttributes"] = sortAttributes;
            attrs["onSortChange"] = onSortChange;
            attrs["downloadUrl"] = downloadUrl;
            if (window.authorizer.hasAccess("faults", 3)) {
                attrs["actionsCallback"] = (selected) => {
                    return M("button.primary", {
                        disabled: selected.size === 0,
                        title: "Delete selected faults",
                        onclick: e => {
                            e.redraw = false;
                            e.target.disabled = true;
                            if (!confirm(`Deleting ${selected.size} faults. Are you sure?`))
                                return;
                            Promise.all(Array.from(selected).map(id => deleteResource("faults", id)))
                                .then(res => {
                                push("success", `${res.length} faults deleted`);
                                fulfill(0, Date.now());
                            })
                                .catch(err => {
                                push("error", err.message);
                                fulfill(0, Date.now());
                            });
                        }
                    }, "Delete");
                };
            }
            const filterAttrs = {};
            filterAttrs["resource"] = "faults";
            filterAttrs["filter"] = vnode.attrs["filter"];
            filterAttrs["onChange"] = onFilterChanged;
            return [
                M("h1", "Listing faults"),
                M(component$n, filterAttrs),
                M(component$m, attrs)
            ];
        }
    };
};

const faultsPage = /*#__PURE__*/Object.freeze({
  init: init$5,
  component: component$s
});

/**
 * Copyright 2013-2019  GenieACS Inc.
 *
 * This file is part of GenieACS.
 *
 * GenieACS is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * GenieACS is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with GenieACS.  If not, see <http://www.gnu.org/licenses/>.
 */
const component$t = () => {
    return {
        view: vnode => {
            return M("textarea", {
                name: vnode.attrs["id"],
                value: vnode.attrs["value"],
                oncreate: _vnode => {
                    const editor = codeMirror.fromTextArea(_vnode.dom, {
                        mode: vnode.attrs["mode"],
                        lineNumbers: true,
                        readOnly: vnode.attrs["readOnly"],
                        extraKeys: {
                            "Ctrl-Enter": () => {
                                if (vnode.attrs["onSubmit"])
                                    vnode.attrs["onSubmit"](_vnode.dom);
                            },
                            "Cmd-Enter": () => {
                                if (vnode.attrs["onSubmit"])
                                    vnode.attrs["onSubmit"](_vnode.dom);
                            }
                        }
                    });
                    if (vnode.attrs["onChange"]) {
                        editor.on("change", e => {
                            vnode.attrs["onChange"](e.getValue());
                        });
                    }
                    if (vnode.attrs["focus"])
                        editor.focus();
                    if (vnode.attrs["onReady"])
                        vnode.attrs["onReady"](editor);
                }
            });
        }
    };
};

/**
 * Copyright 2013-2019  GenieACS Inc.
 *
 * This file is part of GenieACS.
 *
 * GenieACS is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * GenieACS is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with GenieACS.  If not, see <http://www.gnu.org/licenses/>.
 */
const singular = {
    presets: "preset",
    provisions: "provision",
    virtualParameters: "virtual parameter",
    files: "file",
    users: "user",
    permissions: "permission"
};
function createField(current, attr, focus) {
    if (attr.type === "combo") {
        const options = [M("option", { value: "" }, "--Please choose--")];
        for (const op of attr.options)
            options.push(M("option", { value: op }, op));
        let selected = "";
        if (attr.options.includes(current.object[attr.id]))
            selected = current.object[attr.id];
        return M("select", {
            name: attr.id,
            value: selected,
            oncreate: focus
                ? _vnode => {
                    _vnode.dom.focus();
                }
                : null,
            onchange: e => {
                current.object[attr.id] = e.target.value;
                e.redraw = false;
            }
        }, options);
    }
    else if (attr.type === "multi") {
        const currentSelected = new Set(current.object[attr.id]);
        const options = attr.options.map(op => {
            const id = `${attr.id}-${op}`;
            const opts = {
                type: "checkbox",
                id: id,
                value: op,
                oncreate: _vnode => {
                    if (focus && !options.length)
                        _vnode.dom.focus();
                    if (currentSelected.has(op))
                        _vnode.dom.checked = true;
                },
                onchange: e => {
                    if (e.target.checked)
                        currentSelected.add(op);
                    else
                        currentSelected.delete(op);
                    current.object[attr.id] = Array.from(currentSelected);
                    e.redraw = false;
                }
            };
            return M("tr", [M("td", M("input", opts)), M("td", op)]);
        });
        return M("table", options);
    }
    else if (attr.type === "code") {
        const attrs = {
            id: attr.id,
            value: current.object[attr.id],
            mode: "javascript",
            onSubmit: dom => {
                dom.form.querySelector("button[type=submit]").click();
            },
            onChange: value => {
                current.object[attr.id] = value;
            }
        };
        return M(component$t, attrs);
    }
    else if (attr.type === "file") {
        return M("input", {
            type: "file",
            name: attr.id,
            oncreate: focus
                ? _vnode => {
                    _vnode.dom.focus();
                }
                : null,
            onchange: e => {
                current.object[attr.id] = e.target.files;
                e.redraw = false;
            }
        });
    }
    else if (attr.type === "textarea") {
        return M("textarea", {
            name: attr.id,
            value: current.object[attr.id],
            readonly: attr.id === "_id" && !current.isNew,
            cols: attr.cols || 80,
            rows: attr.rows || 4,
            style: "resize: none;",
            oncreate: focus
                ? _vnode => {
                    const dom = _vnode.dom;
                    dom.focus();
                    dom.setSelectionRange(dom.value.length, dom.value.length);
                }
                : null,
            oninput: e => {
                current.object[attr.id] = e.target.value;
                e.redraw = false;
            },
            onkeypress: e => {
                e.redraw = false;
                if (e.which === 13 && !e.shiftKey) {
                    const dom = e.target;
                    dom.form.querySelector("button[type=submit]").click();
                    return false;
                }
                return true;
            }
        });
    }
    return M("input", {
        type: attr.type === "password" ? "password" : "text",
        name: attr.id,
        disabled: attr.id === "_id" && !current.isNew,
        value: current.object[attr.id],
        oncreate: focus
            ? _vnode => {
                _vnode.dom.focus();
            }
            : null,
        oninput: e => {
            current.object[attr.id] = e.target.value;
            e.redraw = false;
        }
    });
}
const component$u = () => {
    return {
        view: vnode => {
            const actionHandler = vnode.attrs["actionHandler"];
            const attributes = vnode.attrs["attributes"];
            const resource = vnode.attrs["resource"];
            const base = vnode.attrs["base"] || {};
            if (!vnode.state["current"]) {
                vnode.state["current"] = {
                    isNew: !base["_id"],
                    object: Object.assign({}, base)
                };
            }
            const current = vnode.state["current"];
            const form = [];
            let focused = false;
            for (const attr of attributes) {
                let focus = false;
                if (!focused && (current.isNew || attr.id !== "_id"))
                    focus = focused = true;
                form.push(M("p", M("label", { for: attr.id }, attr.label || attr.id), M("br"), createField(current, attr, focus)));
            }
            const submit = M("button.primary", { type: "submit" }, "Save");
            const buttons = [submit];
            if (!current.isNew) {
                buttons.push(M("button.primary", {
                    type: "button",
                    title: `Delete ${singular[resource] || resource}`,
                    onclick: e => {
                        e.redraw = false;
                        e.target.disabled = true;
                        actionHandler("delete", current.object).then(() => {
                            e.target.disabled = false;
                        });
                    }
                }, "Delete"));
            }
            form.push(M(".actions-bar", buttons));
            const children = [
                M("h1", `${current.isNew ? "New" : "Editing"} ${singular[resource] ||
                    resource}`),
                M("form", {
                    onsubmit: e => {
                        e.redraw = false;
                        // const onsubmit = e.target.onsubmit;
                        e.preventDefault();
                        // e.target.onsubmit = null;
                        submit.dom.disabled = true;
                        // submit.dom.textContent = "Loading ...";
                        actionHandler("save", current.object).then(() => {
                            // submit.dom.textContent = "Save";
                            // e.target.onsubmit = onsubmit;
                            submit.dom.disabled = false;
                        });
                    }
                }, form)
            ];
            return M("div.put-form", children);
        }
    };
};

/**
 * Copyright 2013-2019  GenieACS Inc.
 *
 * This file is part of GenieACS.
 *
 * GenieACS is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * GenieACS is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with GenieACS.  If not, see <http://www.gnu.org/licenses/>.
 */
const PAGE_SIZE$2 = config.ui.pageSize || 10;
const memoizedParse$3 = memoize(parse);
const memoizedJsonParse$2 = memoize(JSON.parse);
const attributes$1 = [
    { id: "_id", label: "Name" },
    { id: "channel", label: "Channel" },
    { id: "weight", label: "Weight" },
    { id: "schedule", label: "Schedule" },
    { id: "events", label: "Events" },
    { id: "precondition", label: "Precondition", type: "textarea" },
    { id: "provision", label: "Provision", type: "combo" },
    { id: "provisionArgs", label: "Arguments" }
];
const unpackSmartQuery$2 = memoize(query => {
    return map(query, e => {
        if (Array.isArray(e) && e[0] === "FUNC" && e[1] === "Q")
            return unpack("presets", e[2], e[3]);
        return e;
    });
});
function putActionHandler(action, _object, isNew) {
    return new Promise((resolve, reject) => {
        const object = Object.assign({}, _object);
        if (action === "save") {
            const id = object["_id"];
            delete object["_id"];
            const errors = {};
            if (!id)
                errors["_id"] = "ID can not be empty";
            if (!object.provision)
                errors["provision"] = "Provision not selected";
            if (Object.keys(errors).length)
                return void resolve(errors);
            if (object.precondition) {
                try {
                    object.precondition = stringify(memoizedParse$3(object.precondition));
                }
                catch (err) {
                    return void resolve({
                        precondition: "Precondition must be valid expression"
                    });
                }
            }
            resourceExists("presets", id)
                .then(exists => {
                if (exists && isNew) {
                    fulfill(0, Date.now());
                    return void resolve({ _id: "Preset already exists" });
                }
                if (!exists && !isNew) {
                    fulfill(0, Date.now());
                    return void resolve({ _id: "Preset does not exist" });
                }
                putResource("presets", id, object)
                    .then(() => {
                    push("success", `Preset ${exists ? "updated" : "created"}`);
                    fulfill(0, Date.now());
                    resolve();
                })
                    .catch(reject);
            })
                .catch(reject);
        }
        else if (action === "delete") {
            deleteResource("presets", object["_id"])
                .then(() => {
                push("success", "Preset deleted");
                fulfill(0, Date.now());
                resolve();
            })
                .catch(err => {
                reject(err);
                fulfill(0, Date.now());
            });
        }
        else {
            reject(new Error("Undefined action"));
        }
    });
}
const formData = {
    resource: "presets",
    attributes: attributes$1
};
const getDownloadUrl$2 = memoize(filter => {
    const cols = {};
    for (const attr of attributes$1)
        cols[attr.label] = attr.id;
    return `/api/presets.csv?${M.buildQueryString({
        filter: stringify(filter),
        columns: JSON.stringify(cols)
    })}`;
});
function init$6(args) {
    if (!window.authorizer.hasAccess("presets", 2)) {
        return Promise.reject(new Error("You are not authorized to view this page"));
    }
    const sort = args.hasOwnProperty("sort") ? "" + args["sort"] : "";
    const filter = args.hasOwnProperty("filter") ? "" + args["filter"] : "";
    return Promise.resolve({ filter, sort });
}
const component$v = () => {
    return {
        view: vnode => {
            document.title = "Presets - GenieACS";
            function showMore() {
                vnode.state["showCount"] =
                    (vnode.state["showCount"] || PAGE_SIZE$2) + PAGE_SIZE$2;
                M.redraw();
            }
            function onFilterChanged(filter) {
                const ops = { filter };
                if (vnode.attrs["sort"])
                    ops["sort"] = vnode.attrs["sort"];
                M.route.set(M.route.get(), ops);
            }
            const sort = vnode.attrs["sort"]
                ? memoizedJsonParse$2(vnode.attrs["sort"])
                : {};
            const sortAttributes = {};
            for (let i = 0; i < attributes$1.length; i++) {
                const attr = attributes$1[i];
                if (!(attr.id === "events" ||
                    attr.id === "precondition" ||
                    attr.id === "provision" ||
                    attr.id === "provisionArgs"))
                    sortAttributes[i] = sort[attr.id] || 0;
            }
            function onSortChange(sortAttrs) {
                const _sort = Object.assign({}, sort);
                for (const [index, direction] of Object.entries(sortAttrs)) {
                    // Changing the priority of columns
                    delete _sort[attributes$1[index].id];
                    _sort[attributes$1[index].id] = direction;
                }
                const ops = { sort: JSON.stringify(_sort) };
                if (vnode.attrs["filter"])
                    ops["filter"] = vnode.attrs["filter"];
                M.route.set(M.route.get(), ops);
            }
            let filter = vnode.attrs["filter"]
                ? memoizedParse$3(vnode.attrs["filter"])
                : true;
            filter = unpackSmartQuery$2(filter);
            const presets = fetch("presets", filter, {
                limit: vnode.state["showCount"] || PAGE_SIZE$2,
                sort: sort
            });
            const count$1 = count("presets", filter);
            const provisions = fetch("provisions", true);
            if (provisions.fulfilled) {
                const provisionAttr = attributes$1.find(attr => {
                    return attr.id === "provision";
                });
                const provisionIds = new Set([
                    "refresh",
                    "value",
                    "tag",
                    "reboot",
                    "reset",
                    "download",
                    "instances"
                ]);
                for (const p of provisions.value)
                    provisionIds.add(p["_id"]);
                provisionAttr["options"] = Array.from(provisionIds);
            }
            const downloadUrl = getDownloadUrl$2(filter);
            const valueCallback = (attr, preset) => {
                if (attr.id !== "precondition")
                    return preset[attr.id];
                let devicesUrl = "#!/devices";
                if (preset["precondition"].length) {
                    devicesUrl += `?${M.buildQueryString({
                        filter: preset["precondition"]
                    })}`;
                }
                return M("a", { href: devicesUrl, title: preset["precondition"] }, preset["precondition"]);
            };
            const attrs = {};
            attrs["attributes"] = attributes$1;
            attrs["data"] = presets.value;
            attrs["total"] = count$1.value;
            attrs["showMoreCallback"] = showMore;
            attrs["sortAttributes"] = sortAttributes;
            attrs["onSortChange"] = onSortChange;
            attrs["downloadUrl"] = downloadUrl;
            attrs["valueCallback"] = valueCallback;
            attrs["recordActionsCallback"] = preset => {
                return [
                    M("a", {
                        onclick: () => {
                            const cb = () => {
                                return M(component$u, Object.assign({
                                    base: preset,
                                    actionHandler: (action, object) => {
                                        return new Promise(resolve => {
                                            putActionHandler(action, object, false)
                                                .then(errors => {
                                                const errorList = errors
                                                    ? Object.values(errors)
                                                    : [];
                                                if (errorList.length) {
                                                    for (const err of errorList)
                                                        push("error", err);
                                                }
                                                else {
                                                    close(cb);
                                                }
                                                resolve();
                                            })
                                                .catch(err => {
                                                push("error", err.message);
                                                resolve();
                                            });
                                        });
                                    }
                                }, formData));
                            };
                            open(cb);
                        }
                    }, "Show")
                ];
            };
            if (window.authorizer.hasAccess("presets", 3)) {
                attrs["actionsCallback"] = (selected) => {
                    return [
                        M("button.primary", {
                            title: "Create new preset",
                            onclick: () => {
                                const cb = () => {
                                    return M(component$u, Object.assign({
                                        actionHandler: (action, object) => {
                                            return new Promise(resolve => {
                                                putActionHandler(action, object, true)
                                                    .then(errors => {
                                                    const errorList = errors
                                                        ? Object.values(errors)
                                                        : [];
                                                    if (errorList.length) {
                                                        for (const err of errorList)
                                                            push("error", err);
                                                    }
                                                    else {
                                                        close(cb);
                                                    }
                                                    resolve();
                                                })
                                                    .catch(err => {
                                                    push("error", err.message);
                                                    resolve();
                                                });
                                            });
                                        }
                                    }, formData));
                                };
                                open(cb);
                            }
                        }, "New"),
                        M("button.primary", {
                            title: "Delete selected presets",
                            disabled: !selected.size,
                            onclick: e => {
                                if (!confirm(`Deleting ${selected.size} presets. Are you sure?`))
                                    return;
                                e.redraw = false;
                                e.target.disabled = true;
                                Promise.all(Array.from(selected).map(id => deleteResource("presets", id)))
                                    .then(res => {
                                    push("success", `${res.length} presets deleted`);
                                    fulfill(0, Date.now());
                                })
                                    .catch(err => {
                                    push("error", err.message);
                                    fulfill(0, Date.now());
                                });
                            }
                        }, "Delete")
                    ];
                };
            }
            const filterAttrs = {};
            filterAttrs["resource"] = "presets";
            filterAttrs["filter"] = vnode.attrs["filter"];
            filterAttrs["onChange"] = onFilterChanged;
            return [
                M("h1", "Listing presets"),
                M(component$n, filterAttrs),
                M(component$m, attrs)
            ];
        }
    };
};

const presetsPage = /*#__PURE__*/Object.freeze({
  init: init$6,
  component: component$v
});

/**
 * Copyright 2013-2019  GenieACS Inc.
 *
 * This file is part of GenieACS.
 *
 * GenieACS is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * GenieACS is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with GenieACS.  If not, see <http://www.gnu.org/licenses/>.
 */
const PAGE_SIZE$3 = config.ui.pageSize || 10;
const memoizedParse$4 = memoize(parse);
const memoizedJsonParse$3 = memoize(JSON.parse);
const attributes$2 = [
    { id: "_id", label: "Name" },
    { id: "script", label: "Script", type: "code" }
];
const unpackSmartQuery$3 = memoize(query => {
    return map(query, e => {
        if (Array.isArray(e) && e[0] === "FUNC" && e[1] === "Q")
            return unpack("provisions", e[2], e[3]);
        return e;
    });
});
function putActionHandler$1(action, _object, isNew) {
    return new Promise((resolve, reject) => {
        const object = Object.assign({}, _object);
        if (action === "save") {
            const id = object["_id"];
            delete object["_id"];
            if (!id)
                return void resolve({ _id: "ID can not be empty" });
            resourceExists("provisions", id)
                .then(exists => {
                if (exists && isNew) {
                    fulfill(0, Date.now());
                    return void resolve({ _id: "Provision already exists" });
                }
                if (!exists && !isNew) {
                    fulfill(0, Date.now());
                    return void resolve({ _id: "Provision does not exist" });
                }
                putResource("provisions", id, object)
                    .then(() => {
                    push("success", `Provision ${exists ? "updated" : "created"}`);
                    fulfill(0, Date.now());
                    resolve();
                })
                    .catch(reject);
            })
                .catch(reject);
        }
        else if (action === "delete") {
            deleteResource("provisions", object["_id"])
                .then(() => {
                push("success", "Provision deleted");
                fulfill(0, Date.now());
                resolve();
            })
                .catch(err => {
                fulfill(0, Date.now());
                reject(err);
            });
        }
        else {
            reject(new Error("Undefined action"));
        }
    });
}
const formData$1 = {
    resource: "provisions",
    attributes: attributes$2
};
const getDownloadUrl$3 = memoize(filter => {
    const cols = {};
    for (const attr of attributes$2)
        cols[attr.label] = attr.id;
    return `/api/provisions.csv?${M.buildQueryString({
        filter: stringify(filter),
        columns: JSON.stringify(cols)
    })}`;
});
function init$7(args) {
    if (!window.authorizer.hasAccess("provisions", 2)) {
        return Promise.reject(new Error("You are not authorized to view this page"));
    }
    const sort = args.hasOwnProperty("sort") ? "" + args["sort"] : "";
    const filter = args.hasOwnProperty("filter") ? "" + args["filter"] : "";
    return new Promise((resolve, reject) => {
        loadCodeMirror()
            .then(() => {
            resolve({ filter, sort });
        })
            .catch(reject);
    });
}
const component$w = () => {
    return {
        view: vnode => {
            document.title = "Provisions - GenieACS";
            function showMore() {
                vnode.state["showCount"] =
                    (vnode.state["showCount"] || PAGE_SIZE$3) + PAGE_SIZE$3;
                M.redraw();
            }
            function onFilterChanged(filter) {
                const ops = { filter };
                if (vnode.attrs["sort"])
                    ops["sort"] = vnode.attrs["sort"];
                M.route.set(M.route.get(), ops);
            }
            const sort = vnode.attrs["sort"]
                ? memoizedJsonParse$3(vnode.attrs["sort"])
                : {};
            const sortAttributes = {};
            for (let i = 0; i < attributes$2.length; i++)
                sortAttributes[i] = sort[attributes$2[i].id] || 0;
            function onSortChange(sortAttrs) {
                const _sort = Object.assign({}, sort);
                for (const [index, direction] of Object.entries(sortAttrs)) {
                    // Changing the priority of columns
                    delete _sort[attributes$2[index].id];
                    _sort[attributes$2[index].id] = direction;
                }
                const ops = { sort: JSON.stringify(_sort) };
                if (vnode.attrs["filter"])
                    ops["filter"] = vnode.attrs["filter"];
                M.route.set(M.route.get(), ops);
            }
            let filter = vnode.attrs["filter"]
                ? memoizedParse$4(vnode.attrs["filter"])
                : true;
            filter = unpackSmartQuery$3(filter);
            const provisions = fetch("provisions", filter, {
                limit: vnode.state["showCount"] || PAGE_SIZE$3,
                sort: sort
            });
            const count$1 = count("provisions", filter);
            const downloadUrl = getDownloadUrl$3(filter);
            const attrs = {};
            attrs["attributes"] = attributes$2;
            attrs["data"] = provisions.value;
            attrs["total"] = count$1.value;
            attrs["showMoreCallback"] = showMore;
            attrs["sortAttributes"] = sortAttributes;
            attrs["onSortChange"] = onSortChange;
            attrs["downloadUrl"] = downloadUrl;
            attrs["recordActionsCallback"] = provision => {
                return [
                    M("a", {
                        onclick: () => {
                            const cb = () => {
                                return M(component$u, Object.assign({
                                    base: provision,
                                    actionHandler: (action, object) => {
                                        return new Promise(resolve => {
                                            putActionHandler$1(action, object, false)
                                                .then(errors => {
                                                const errorList = errors
                                                    ? Object.values(errors)
                                                    : [];
                                                if (errorList.length) {
                                                    for (const err of errorList)
                                                        push("error", err);
                                                }
                                                else {
                                                    close(cb);
                                                }
                                                resolve();
                                            })
                                                .catch(err => {
                                                push("error", err.message);
                                                resolve();
                                            });
                                        });
                                    }
                                }, formData$1));
                            };
                            open(cb);
                        }
                    }, "Show")
                ];
            };
            if (window.authorizer.hasAccess("provisions", 3)) {
                attrs["actionsCallback"] = (selected) => {
                    return [
                        M("button.primary", {
                            title: "Create new provision",
                            onclick: () => {
                                const cb = () => {
                                    return M(component$u, Object.assign({
                                        actionHandler: (action, object) => {
                                            return new Promise(resolve => {
                                                putActionHandler$1(action, object, true)
                                                    .then(errors => {
                                                    const errorList = errors
                                                        ? Object.values(errors)
                                                        : [];
                                                    if (errorList.length) {
                                                        for (const err of errorList)
                                                            push("error", err);
                                                    }
                                                    else {
                                                        close(cb);
                                                    }
                                                    resolve();
                                                })
                                                    .catch(err => {
                                                    push("error", err.message);
                                                    resolve();
                                                });
                                            });
                                        }
                                    }, formData$1));
                                };
                                open(cb);
                            }
                        }, "New"),
                        M("button.primary", {
                            title: "Delete selected provisions",
                            disabled: !selected.size,
                            onclick: e => {
                                if (!confirm(`Deleting ${selected.size} provisions. Are you sure?`))
                                    return;
                                e.redraw = false;
                                e.target.disabled = true;
                                Promise.all(Array.from(selected).map(id => deleteResource("provisions", id)))
                                    .then(res => {
                                    push("success", `${res.length} provisions deleted`);
                                    fulfill(0, Date.now());
                                })
                                    .catch(err => {
                                    push("error", err.message);
                                    fulfill(0, Date.now());
                                });
                            }
                        }, "Delete")
                    ];
                };
            }
            const filterAttrs = {};
            filterAttrs["resource"] = "provisions";
            filterAttrs["filter"] = vnode.attrs["filter"];
            filterAttrs["onChange"] = onFilterChanged;
            return [
                M("h1", "Listing provisions"),
                M(component$n, filterAttrs),
                M(component$m, attrs)
            ];
        }
    };
};

const provisionsPage = /*#__PURE__*/Object.freeze({
  init: init$7,
  component: component$w
});

/**
 * Copyright 2013-2019  GenieACS Inc.
 *
 * This file is part of GenieACS.
 *
 * GenieACS is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * GenieACS is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with GenieACS.  If not, see <http://www.gnu.org/licenses/>.
 */
const PAGE_SIZE$4 = config.ui.pageSize || 10;
const memoizedParse$5 = memoize(parse);
const memoizedJsonParse$4 = memoize(JSON.parse);
const attributes$3 = [
    { id: "_id", label: "Name" },
    { id: "script", label: "Script", type: "code" }
];
const unpackSmartQuery$4 = memoize(query => {
    return map(query, e => {
        if (Array.isArray(e) && e[0] === "FUNC" && e[1] === "Q")
            return unpack("virtualParameters", e[2], e[3]);
        return e;
    });
});
function putActionHandler$2(action, _object, isNew) {
    return new Promise((resolve, reject) => {
        const object = Object.assign({}, _object);
        if (action === "save") {
            const id = object["_id"];
            delete object["_id"];
            if (!id)
                return void resolve({ _id: "ID can not be empty" });
            resourceExists("virtualParameters", id)
                .then(exists => {
                if (exists && isNew) {
                    fulfill(0, Date.now());
                    return void resolve({ _id: "Virtual parameter already exists" });
                }
                if (!exists && !isNew) {
                    fulfill(0, Date.now());
                    return void resolve({ _id: "Virtual parameter does not exist" });
                }
                putResource("virtualParameters", id, object)
                    .then(() => {
                    push("success", `Virtual parameter ${exists ? "updated" : "created"}`);
                    fulfill(0, Date.now());
                    resolve();
                })
                    .catch(reject);
            })
                .catch(reject);
        }
        else if (action === "delete") {
            deleteResource("virtualParameters", object["_id"])
                .then(() => {
                push("success", "Virtual parameter deleted");
                fulfill(0, Date.now());
                resolve();
            })
                .catch(err => {
                fulfill(0, Date.now());
                reject(err);
            });
        }
        else {
            reject(new Error("Undefined action"));
        }
    });
}
const formData$2 = {
    resource: "virtualParameters",
    attributes: attributes$3
};
const getDownloadUrl$4 = memoize(filter => {
    const cols = {};
    for (const attr of attributes$3)
        cols[attr.label] = attr.id;
    return `/api/virtualParameters.csv?${M.buildQueryString({
        filter: stringify(filter),
        columns: JSON.stringify(cols)
    })}`;
});
function init$8(args) {
    if (!window.authorizer.hasAccess("virtualParameters", 2)) {
        return Promise.reject(new Error("You are not authorized to view this page"));
    }
    const sort = args.hasOwnProperty("sort") ? "" + args["sort"] : "";
    const filter = args.hasOwnProperty("filter") ? "" + args["filter"] : "";
    return new Promise((resolve, reject) => {
        loadCodeMirror()
            .then(() => {
            resolve({ filter, sort });
        })
            .catch(reject);
    });
}
const component$x = () => {
    return {
        view: vnode => {
            document.title = "Virtual Parameters - GenieACS";
            function showMore() {
                vnode.state["showCount"] =
                    (vnode.state["showCount"] || PAGE_SIZE$4) + PAGE_SIZE$4;
                M.redraw();
            }
            function onFilterChanged(filter) {
                const ops = { filter };
                if (vnode.attrs["sort"])
                    ops["sort"] = vnode.attrs["sort"];
                M.route.set(M.route.get(), ops);
            }
            const sort = vnode.attrs["sort"]
                ? memoizedJsonParse$4(vnode.attrs["sort"])
                : {};
            const sortAttributes = {};
            for (let i = 0; i < attributes$3.length; i++)
                sortAttributes[i] = sort[attributes$3[i].id] || 0;
            function onSortChange(sortAttrs) {
                const _sort = Object.assign({}, sort);
                for (const [index, direction] of Object.entries(sortAttrs)) {
                    // Changing the priority of columns
                    delete _sort[attributes$3[index].id];
                    _sort[attributes$3[index].id] = direction;
                }
                const ops = { sort: JSON.stringify(_sort) };
                if (vnode.attrs["filter"])
                    ops["filter"] = vnode.attrs["filter"];
                M.route.set(M.route.get(), ops);
            }
            let filter = vnode.attrs["filter"]
                ? memoizedParse$5(vnode.attrs["filter"])
                : true;
            filter = unpackSmartQuery$4(filter);
            const virtualParameters = fetch("virtualParameters", filter, {
                limit: vnode.state["showCount"] || PAGE_SIZE$4,
                sort: sort
            });
            const count$1 = count("virtualParameters", filter);
            const downloadUrl = getDownloadUrl$4(filter);
            const attrs = {};
            attrs["attributes"] = attributes$3;
            attrs["data"] = virtualParameters.value;
            attrs["total"] = count$1.value;
            attrs["showMoreCallback"] = showMore;
            attrs["sortAttributes"] = sortAttributes;
            attrs["onSortChange"] = onSortChange;
            attrs["downloadUrl"] = downloadUrl;
            attrs["recordActionsCallback"] = virtualParameter => {
                return [
                    M("a", {
                        onclick: () => {
                            const cb = () => {
                                return M(component$u, Object.assign({
                                    base: virtualParameter,
                                    actionHandler: (action, object) => {
                                        return new Promise(resolve => {
                                            putActionHandler$2(action, object, false)
                                                .then(errors => {
                                                const errorList = errors
                                                    ? Object.values(errors)
                                                    : [];
                                                if (errorList.length) {
                                                    for (const err of errorList)
                                                        push("error", err);
                                                }
                                                else {
                                                    close(cb);
                                                }
                                                resolve();
                                            })
                                                .catch(err => {
                                                push("error", err.message);
                                                resolve();
                                            });
                                        });
                                    }
                                }, formData$2));
                            };
                            open(cb);
                        }
                    }, "Show")
                ];
            };
            if (window.authorizer.hasAccess("virtualParameters", 3)) {
                attrs["actionsCallback"] = (selected) => {
                    return [
                        M("button.primary", {
                            title: "Create new virtual parameter",
                            onclick: () => {
                                const cb = () => {
                                    return M(component$u, Object.assign({
                                        actionHandler: (action, object) => {
                                            return new Promise(resolve => {
                                                putActionHandler$2(action, object, true)
                                                    .then(errors => {
                                                    const errorList = errors
                                                        ? Object.values(errors)
                                                        : [];
                                                    if (errorList.length) {
                                                        for (const err of errorList)
                                                            push("error", err);
                                                    }
                                                    else {
                                                        close(cb);
                                                    }
                                                    resolve();
                                                })
                                                    .catch(err => {
                                                    push("error", err.message);
                                                    resolve();
                                                });
                                            });
                                        }
                                    }, formData$2));
                                };
                                open(cb);
                            }
                        }, "New"),
                        M("button.primary", {
                            title: "Delete selected virtual parameters",
                            disabled: !selected.size,
                            onclick: e => {
                                if (!confirm(`Deleting ${selected.size} virtual parameters. Are you sure?`))
                                    return;
                                e.redraw = false;
                                e.target.disabled = true;
                                Promise.all(Array.from(selected).map(id => deleteResource("virtualParameters", id)))
                                    .then(res => {
                                    push("success", `${res.length} virtual parameters deleted`);
                                    fulfill(0, Date.now());
                                })
                                    .catch(err => {
                                    push("error", err.message);
                                    fulfill(0, Date.now());
                                });
                            }
                        }, "Delete")
                    ];
                };
            }
            const filterAttrs = {};
            filterAttrs["resource"] = "virtualParameters";
            filterAttrs["filter"] = vnode.attrs["filter"];
            filterAttrs["onChange"] = onFilterChanged;
            return [
                M("h1", "Listing virtual parameters"),
                M(component$n, filterAttrs),
                M(component$m, attrs)
            ];
        }
    };
};

const virtualParametersPage = /*#__PURE__*/Object.freeze({
  init: init$8,
  component: component$x
});

/**
 * Copyright 2013-2019  GenieACS Inc.
 *
 * This file is part of GenieACS.
 *
 * GenieACS is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * GenieACS is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with GenieACS.  If not, see <http://www.gnu.org/licenses/>.
 */
const PAGE_SIZE$5 = config.ui.pageSize || 10;
const memoizedParse$6 = memoize(parse);
const memoizedJsonParse$5 = memoize(JSON.parse);
const attributes$4 = [
    { id: "_id", label: "Name" },
    {
        id: "metadata.fileType",
        label: "Type",
        type: "combo",
        options: [
            "1 Firmware Upgrade Image",
            "2 Web Content",
            "3 Vendor Configuration File",
            "4 Tone File",
            "5 Ringer File"
        ]
    },
    { id: "metadata.oui", label: "OUI" },
    { id: "metadata.productClass", label: "Product Class" },
    { id: "metadata.version", label: "Version" }
];
const formData$3 = {
    resource: "files",
    attributes: attributes$4
        .slice(1) // remove _id from new object form
        .concat([{ id: "file", label: "File", type: "file" }])
};
const unpackSmartQuery$5 = memoize(query => {
    return map(query, e => {
        if (Array.isArray(e) && e[0] === "FUNC" && e[1] === "Q")
            return unpack("files", e[2], e[3]);
        return e;
    });
});
function putActionHandler$3(action, _object) {
    return new Promise((resolve, reject) => {
        const object = Object.assign({}, _object);
        if (action === "save") {
            const file = object["file"] ? object["file"][0] : null;
            delete object["file"];
            if (!file)
                return void resolve({ file: "File not selected" });
            const id = file.name;
            resourceExists("files", id)
                .then(exists => {
                if (exists) {
                    fulfill(0, Date.now());
                    return void resolve({ file: "File already exists" });
                }
                const headers = Object.assign({
                    "Content-Type": "application/octet-stream",
                    Accept: "application/octet-stream"
                }, object);
                M.request({
                    method: "PUT",
                    headers: headers,
                    url: `/api/files/${encodeURIComponent(id)}`,
                    serialize: body => body,
                    data: file
                })
                    .then(() => {
                    push("success", `File ${exists ? "updated" : "created"}`);
                    fulfill(0, Date.now());
                    resolve();
                })
                    .catch(reject);
            })
                .catch(reject);
        }
        else {
            reject(new Error("Undefined action"));
        }
    });
}
const getDownloadUrl$5 = memoize(filter => {
    const cols = {};
    for (const attr of attributes$4)
        cols[attr.label] = attr.id;
    return `/api/files.csv?${M.buildQueryString({
        filter: stringify(filter),
        columns: JSON.stringify(cols)
    })}`;
});
function init$9(args) {
    if (!window.authorizer.hasAccess("files", 2)) {
        return Promise.reject(new Error("You are not authorized to view this page"));
    }
    const sort = args.hasOwnProperty("sort") ? "" + args["sort"] : "";
    const filter = args.hasOwnProperty("filter") ? "" + args["filter"] : "";
    return Promise.resolve({ filter, sort });
}
const component$y = () => {
    return {
        view: vnode => {
            document.title = "Files - GenieACS";
            function showMore() {
                vnode.state["showCount"] =
                    (vnode.state["showCount"] || PAGE_SIZE$5) + PAGE_SIZE$5;
                M.redraw();
            }
            function onFilterChanged(filter) {
                const ops = { filter };
                if (vnode.attrs["sort"])
                    ops["sort"] = vnode.attrs["sort"];
                M.route.set(M.route.get(), ops);
            }
            const sort = vnode.attrs["sort"]
                ? memoizedJsonParse$5(vnode.attrs["sort"])
                : {};
            const sortAttributes = {};
            for (let i = 0; i < attributes$4.length; i++)
                sortAttributes[i] = sort[attributes$4[i].id] || 0;
            function onSortChange(sortAttrs) {
                const _sort = Object.assign({}, sort);
                for (const [index, direction] of Object.entries(sortAttrs)) {
                    // Changing the priority of columns
                    delete _sort[attributes$4[index].id];
                    _sort[attributes$4[index].id] = direction;
                }
                const ops = { sort: JSON.stringify(_sort) };
                if (vnode.attrs["filter"])
                    ops["filter"] = vnode.attrs["filter"];
                M.route.set(M.route.get(), ops);
            }
            let filter = vnode.attrs["filter"]
                ? memoizedParse$6(vnode.attrs["filter"])
                : true;
            filter = unpackSmartQuery$5(filter);
            const files = fetch("files", filter, {
                limit: vnode.state["showCount"] || PAGE_SIZE$5,
                sort: sort
            });
            const count$1 = count("files", filter);
            const downloadUrl = getDownloadUrl$5(filter);
            const attrs = {};
            attrs["attributes"] = attributes$4;
            attrs["data"] = files.value;
            attrs["total"] = count$1.value;
            attrs["showMoreCallback"] = showMore;
            attrs["sortAttributes"] = sortAttributes;
            attrs["onSortChange"] = onSortChange;
            attrs["downloadUrl"] = downloadUrl;
            if (window.authorizer.hasAccess("files", 3)) {
                attrs["actionsCallback"] = (selected) => {
                    return [
                        M("button.primary", {
                            title: "Create new file",
                            onclick: () => {
                                const cb = () => {
                                    return M(component$u, Object.assign({
                                        actionHandler: (action, object) => {
                                            return new Promise(resolve => {
                                                putActionHandler$3(action, object)
                                                    .then(errors => {
                                                    const errorList = errors
                                                        ? Object.values(errors)
                                                        : [];
                                                    if (errorList.length) {
                                                        for (const err of errorList)
                                                            push("error", err);
                                                    }
                                                    else {
                                                        close(cb);
                                                    }
                                                    resolve();
                                                })
                                                    .catch(err => {
                                                    push("error", err.message);
                                                    resolve();
                                                });
                                            });
                                        }
                                    }, formData$3));
                                };
                                open(cb);
                            }
                        }, "New"),
                        M("button.primary", {
                            title: "Delete selected files",
                            disabled: !selected.size,
                            onclick: e => {
                                if (!confirm(`Deleting ${selected.size} files. Are you sure?`))
                                    return;
                                e.redraw = false;
                                e.target.disabled = true;
                                Promise.all(Array.from(selected).map(id => deleteResource("files", id)))
                                    .then(res => {
                                    push("success", `${res.length} files deleted`);
                                    fulfill(0, Date.now());
                                })
                                    .catch(err => {
                                    push("error", err.message);
                                    fulfill(0, Date.now());
                                });
                            }
                        }, "Delete")
                    ];
                };
            }
            const filterAttrs = {};
            filterAttrs["resource"] = "files";
            filterAttrs["filter"] = vnode.attrs["filter"];
            filterAttrs["onChange"] = onFilterChanged;
            return [
                M("h1", "Listing files"),
                M(component$n, filterAttrs),
                M(component$m, attrs)
            ];
        }
    };
};

const filesPage = /*#__PURE__*/Object.freeze({
  init: init$9,
  component: component$y
});

/**
 * Copyright 2013-2019  GenieACS Inc.
 *
 * This file is part of GenieACS.
 *
 * GenieACS is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * GenieACS is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with GenieACS.  If not, see <http://www.gnu.org/licenses/>.
 */
function flattenConfig(config) {
    const flatten = {};
    const recuresive = (obj, root) => {
        for (const [k, v] of Object.entries(obj)) {
            const key = root ? `${root}.${k}` : k;
            if (v === undefined)
                continue;
            if (v === null || typeof v !== "object")
                flatten[key] = v;
            else
                recuresive(v, key);
        }
    };
    if (config !== null && typeof config === "object")
        recuresive(config, "");
    return flatten;
}
function structureConfig(config) {
    config.sort((a, b) => (a._id > b._id ? 1 : a._id < b._id ? -1 : 0));
    const _config = {};
    for (const c of config) {
        const keys = c._id.split(".");
        let ref = _config;
        while (keys.length > 1) {
            const k = keys.shift();
            if (typeof ref[k] !== "object")
                ref[k] = {};
            ref = ref[k];
        }
        ref[keys[0]] = c.value;
    }
    const toArray = function (object) {
        const MAX_BITS = 30;
        const MAX_ARRAY_SIZE = MAX_BITS * 10;
        if (object == null || typeof object !== "object")
            return object;
        if (Object.keys(object).length <= MAX_ARRAY_SIZE) {
            let indexes = [];
            for (const key of Object.keys(object)) {
                const idx = Math.floor(+key);
                if (idx >= 0 && idx < MAX_ARRAY_SIZE && String(idx) === key) {
                    const pos = Math.floor(idx / MAX_BITS);
                    if (!indexes[pos])
                        indexes[pos] = 0;
                    indexes[pos] |= 1 << idx % MAX_BITS;
                }
                else {
                    indexes = [];
                    break;
                }
            }
            let index = 0;
            while (indexes.length && (index = indexes.shift()) === 1073741823)
                ;
            if (index && (~index & (index + 1)) === index + 1) {
                // its an array
                const array = [];
                for (let i = 0; i < Object.keys(object).length; i++)
                    array[i] = object[i];
                object = array;
            }
        }
        for (const [k, v] of Object.entries(object))
            object[k] = toArray(v);
        return object;
    };
    return toArray(_config);
}
function diffConfig(current, target) {
    const diff = {
        add: [],
        remove: []
    };
    for (const [k, v] of Object.entries(target))
        if (!current[k] || current[k] !== v)
            diff.add.push({ _id: k, value: v });
    for (const k of Object.keys(current))
        if (!target[k])
            diff.remove.push(k);
    return diff;
}

/**
 * Copyright 2013-2019  GenieACS Inc.
 *
 * This file is part of GenieACS.
 *
 * GenieACS is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * GenieACS is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with GenieACS.  If not, see <http://www.gnu.org/licenses/>.
 */
function putActionHandler$4(prefix, dataYaml) {
    return new Promise((resolve, reject) => {
        try {
            let updated = yaml.parse(dataYaml, { schema: "failsafe" });
            if (updated) {
                const config = {};
                let ref = config;
                prefix.forEach((seg, index) => {
                    if (index < prefix.length - 1) {
                        ref[seg] = {};
                        ref = ref[seg];
                    }
                    else {
                        ref[seg] = updated;
                    }
                });
                updated = flattenConfig(config);
            }
            else {
                updated = {};
            }
            // Try parse to ensure valid expressions
            for (const v of Object.values(updated))
                parse(v);
            queryConfig(`${prefix.join(".")}.%`)
                .then(res => {
                const current = {};
                for (const f of res)
                    current[f._id] = f.value;
                const diff = diffConfig(current, updated);
                if (!diff.add.length && !diff.remove.length)
                    return void resolve();
                const promises = [];
                for (const obj of diff.add)
                    promises.push(putResource("config", obj._id, obj));
                for (const id of diff.remove)
                    promises.push(deleteResource("config", id));
                Promise.all(promises)
                    .then(() => {
                    resolve();
                })
                    .catch(reject);
            })
                .catch(reject);
        }
        catch (error) {
            resolve({ config: error.message });
        }
    });
}
const component$z = () => {
    return {
        view: vnode => {
            const prefix = vnode.attrs["prefix"].split(".");
            const name = vnode.attrs["name"];
            const data = vnode.attrs["data"];
            if (prefix[prefix.length - 1] === "")
                prefix.pop();
            let config;
            if (data.length) {
                config = structureConfig(data);
                for (const seg of prefix)
                    config = config[seg];
            }
            const yamlString = config && Object.values(config).length
                ? yaml.stringify(config, {
                    schema: "failsafe",
                    tags: args => {
                        return args;
                    }
                })
                : "";
            const attrs = {
                id: `${name}-ui-config`,
                value: yamlString,
                mode: "yaml",
                focus: true,
                onSubmit: dom => {
                    dom.form.querySelector("button[type=submit]").click();
                },
                onChange: value => {
                    vnode.state["updatedYaml"] = value;
                }
            };
            const code = M(component$t, attrs);
            const submit = M("button.primary", { type: "submit" }, "Save");
            return M("div.put-form", [
                M("h1", `Editing ${name}`),
                M("form", {
                    onsubmit: e => {
                        e.redraw = false;
                        e.preventDefault();
                        if (vnode.state["updatedYaml"] == null)
                            vnode.state["updatedYaml"] = yamlString;
                        putActionHandler$4(prefix, vnode.state["updatedYaml"])
                            .then(vnode.attrs["onUpdate"])
                            .catch(vnode.attrs["onError"]);
                    }
                }, [code, M(".actions-bar", [submit])])
            ]);
        }
    };
};

/**
 * Copyright 2013-2019  GenieACS Inc.
 *
 * This file is part of GenieACS.
 *
 * GenieACS is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * GenieACS is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with GenieACS.  If not, see <http://www.gnu.org/licenses/>.
 */
const attributes$5 = [
    { id: "_id", label: "Key" },
    { id: "value", label: "Value", type: "textarea" }
];
function putActionHandler$5(action, _object, isNew) {
    return new Promise((resolve, reject) => {
        const object = Object.assign({}, _object);
        if (action === "save") {
            let id = object["_id"] || "";
            delete object["_id"];
            const regex = /^[0-9a-zA-Z_.-]+$/;
            id = id.trim();
            if (!id.match(regex))
                return void resolve({ _id: "Invalid ID" });
            try {
                object.value = stringify(parse(object.value || ""));
            }
            catch (err) {
                return void resolve({
                    value: "Config value must be valid expression"
                });
            }
            resourceExists("config", id)
                .then(exists => {
                if (exists && isNew) {
                    fulfill(0, Date.now());
                    return void resolve({ _id: "Config already exists" });
                }
                if (!exists && !isNew) {
                    fulfill(0, Date.now());
                    return void resolve({ _id: "Config does not exist" });
                }
                putResource("config", id, object)
                    .then(() => {
                    push("success", `Config ${exists ? "updated" : "created"}`);
                    fulfill(0, Date.now());
                    resolve();
                })
                    .catch(reject);
            })
                .catch(reject);
        }
        else if (action === "delete") {
            deleteResource("config", object["_id"])
                .then(() => {
                push("success", "Config deleted");
                fulfill(0, Date.now());
                resolve();
            })
                .catch(err => {
                fulfill(0, Date.now());
                reject(err);
            });
        }
        else {
            reject(new Error("Undefined action"));
        }
    });
}
const formData$4 = {
    resource: "config",
    attributes: attributes$5
};
function escapeRegExp$1(str) {
    return str.replace(/[-[\]/{}()*+?.\\^$|]/g, "\\$&");
}
function init$a() {
    if (!window.authorizer.hasAccess("config", 2)) {
        return Promise.reject(new Error("You are not authorized to view this page"));
    }
    return new Promise((resolve, reject) => {
        Promise.all([loadCodeMirror(), loadYaml()])
            .then(() => {
            resolve({});
        })
            .catch(reject);
    });
}
function renderTable$1(confsResponse, searchString) {
    const confs = confsResponse.value.sort((a, b) => {
        return a._id < b._id ? -1 : 1;
    });
    let regex;
    if (searchString) {
        const keywords = searchString.split(" ").filter(s => s);
        if (keywords.length)
            regex = new RegExp(keywords.map(s => escapeRegExp$1(s)).join(".*"), "i");
    }
    const rows = [];
    for (const conf of confs) {
        const attrs = {};
        if (regex && !regex.test(conf._id) && !regex.test(conf.value))
            attrs["style"] = "display: none;";
        const edit = M("button", {
            title: "Edit config value",
            onclick: () => {
                const cb = () => {
                    return M(component$u, Object.assign({
                        base: conf,
                        actionHandler: (action, object) => {
                            return new Promise(resolve => {
                                putActionHandler$5(action, object, false)
                                    .then(errors => {
                                    const ErrorList = errors ? Object.values(errors) : [];
                                    if (ErrorList.length) {
                                        for (const err of ErrorList)
                                            push("error", err);
                                    }
                                    else {
                                        close(cb);
                                    }
                                    resolve();
                                })
                                    .catch(err => {
                                    push("error", err.message);
                                    resolve();
                                });
                            });
                        }
                    }, formData$4));
                };
                open(cb);
            }
        }, "✎");
        const del = M("button", {
            title: "Delete config",
            onclick: () => {
                if (!confirm(`Deleting ${conf._id} config. Are you sure?`))
                    return;
                putActionHandler$5("delete", conf).catch(err => {
                    throw err;
                });
            }
        }, "✕");
        rows.push(M("tr", attrs, M("td.left", M("long-text", { text: conf._id })), M("td.right", M("span", [M("long-text", { text: `${conf.value}` }), edit, del]))));
    }
    if (!rows.length)
        rows.push(M("tr.empty", M("td", { colspan: 2 }, "No config")));
    return M("table", M("tbody", rows));
}
const component$A = () => {
    return {
        view: vnode => {
            document.title = "Config - GenieACS";
            const search = M("input", {
                type: "text",
                placeholder: "Search config",
                oninput: e => {
                    vnode.state["searchString"] = e.target.value;
                    e.redraw = false;
                    clearTimeout(vnode.state["timeout"]);
                    vnode.state["timeout"] = setTimeout(M.redraw, 250);
                }
            });
            const confs = fetch("config", true);
            let newConfig;
            const subs = [];
            if (window.authorizer.hasAccess("config", 3)) {
                newConfig = M("button.primary", {
                    title: "Create new config",
                    onclick: () => {
                        const cb = () => {
                            return M(component$u, Object.assign({
                                actionHandler: (action, object) => {
                                    return new Promise(resolve => {
                                        putActionHandler$5(action, object, true)
                                            .then(errors => {
                                            const errorList = errors
                                                ? Object.values(errors)
                                                : [];
                                            if (errorList.length) {
                                                for (const err of errorList)
                                                    push("error", err);
                                            }
                                            else {
                                                close(cb);
                                            }
                                            resolve();
                                        })
                                            .catch(err => {
                                            push("error", err.message);
                                            resolve();
                                        });
                                    });
                                }
                            }, formData$4));
                        };
                        open(cb);
                    }
                }, "New config");
                const subsData = [
                    { name: "overview", prefix: "ui.overview.groups.", data: [] },
                    { name: "charts", prefix: "ui.overview.charts.", data: [] },
                    { name: "filters", prefix: "ui.filters.", data: [] },
                    { name: "map", prefix: "ui.map.", data: [] },
                    { name: "index page", prefix: "ui.index.", data: [] },
                    { name: "device page", prefix: "ui.device.", data: [] }
                ];
                if (confs.fulfilled) {
                    for (const conf of confs.value) {
                        for (const sub of subsData) {
                            if (conf["_id"].startsWith(sub["prefix"])) {
                                sub["data"].push(conf);
                                break;
                            }
                        }
                    }
                }
                for (const sub of subsData) {
                    const attrs = { prefix: sub.prefix, name: sub.name, data: sub.data };
                    subs.push(M("button", {
                        onclick: () => {
                            const cb = () => {
                                return M(component$z, Object.assign({
                                    onUpdate: errs => {
                                        const errors = errs ? Object.values(errs) : [];
                                        if (errors.length) {
                                            for (const err of errors)
                                                push("error", err);
                                        }
                                        else {
                                            push("success", `${sub.name.replace(/^[a-z]/, sub.name[0].toUpperCase())} config updated`);
                                            close(cb);
                                        }
                                        fulfill(0, Date.now());
                                    },
                                    onError: err => {
                                        push("error", err.message);
                                        fulfill(0, Date.now());
                                        close(cb);
                                    }
                                }, attrs));
                            };
                            open(cb);
                        }
                    }, `Edit ${sub.name}`));
                }
            }
            return [
                M("h1", "Listing config"),
                M(".all-parameters", search, M(".parameter-list", { style: "height: 400px" }, renderTable$1(confs, vnode.state["searchString"])), M(".actions-bar", [newConfig].concat(subs)))
            ];
        }
    };
};

const configPage = /*#__PURE__*/Object.freeze({
  init: init$a,
  component: component$A
});

/**
 * Copyright 2013-2019  GenieACS Inc.
 *
 * This file is part of GenieACS.
 *
 * GenieACS is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * GenieACS is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with GenieACS.  If not, see <http://www.gnu.org/licenses/>.
 */
const PAGE_SIZE$6 = config.ui.pageSize || 10;
const memoizedParse$7 = memoize(parse);
const memoizedJsonParse$6 = memoize(JSON.parse);
const attributes$6 = [
    { id: "role", label: "Role" },
    {
        id: "resource",
        label: "Resource",
        type: "combo",
        options: [
            "config",
            "devices",
            "faults",
            "map",
            "files",
            "permissions",
            "presets",
            "provisions",
            "virtualParameters"
        ]
    },
    { id: "filter", label: "Filter", type: "textarea" },
    {
        id: "access",
        label: "Access",
        type: "combo",
        options: ["1: count", "2: read", "3: write"]
    },
    { id: "validate", label: "Validate", type: "textarea" }
];
const unpackSmartQuery$6 = memoize(query => {
    return map(query, e => {
        if (Array.isArray(e) && e[0] === "FUNC" && e[1] === "Q")
            return unpack("permissions", e[2], e[3]);
        return e;
    });
});
function putActionHandler$6(action, _object) {
    return new Promise((resolve, reject) => {
        const object = Object.assign({}, _object);
        if (action === "save") {
            if (!object.role)
                return void resolve({ role: "Role can not be empty" });
            if (!object.resource)
                return void resolve({ resource: "Resource can not be empty" });
            if (!object.access)
                return void resolve({ access: "Access can not be empty" });
            if (object.access === "3: write")
                object.access = 3;
            else if (object.access === "2: read")
                object.access = 2;
            else if (object.access === "1: count")
                object.access = 1;
            else
                return void resolve({ access: "Invalid access level" });
            if (object.filter) {
                try {
                    object.filter = stringify(memoizedParse$7(object.filter));
                }
                catch (err) {
                    return void resolve({
                        filter: "Filter must be valid expression"
                    });
                }
            }
            if (object.validate) {
                try {
                    object.validate = stringify(memoizedParse$7(object.validate));
                }
                catch (err) {
                    return void resolve({
                        validate: "Validate must be valid expression"
                    });
                }
            }
            const id = `${object.role}:${object.resource}:${object.access}`;
            resourceExists("permissions", id)
                .then(exists => {
                if (exists) {
                    fulfill(0, Date.now());
                    return void resolve({ _id: "Permission already exists" });
                }
                putResource("permissions", id, object)
                    .then(() => {
                    push("success", "Permission created");
                    fulfill(0, Date.now());
                    resolve();
                })
                    .catch(reject);
            })
                .catch(reject);
        }
        else if (action === "delete") {
            deleteResource("permissions", object["_id"])
                .then(() => {
                push("success", "Permission deleted");
                fulfill(0, Date.now());
                resolve();
            })
                .catch(err => {
                fulfill(0, Date.now());
                reject(err);
            });
        }
        else {
            reject(new Error("Undefined action"));
        }
    });
}
const formData$5 = {
    resource: "permissions",
    attributes: attributes$6
};
const getDownloadUrl$6 = memoize(filter => {
    const cols = {};
    for (const attr of attributes$6)
        cols[attr.label] = attr.id;
    return `/api/permissions.csv?${M.buildQueryString({
        filter: stringify(filter),
        columns: JSON.stringify(cols)
    })}`;
});
function init$b(args) {
    if (!window.authorizer.hasAccess("permissions", 2)) {
        return Promise.reject(new Error("You are not authorized to view this page"));
    }
    const sort = args.hasOwnProperty("sort") ? "" + args["sort"] : "";
    const filter = args.hasOwnProperty("filter") ? "" + args["filter"] : "";
    return Promise.resolve({ filter, sort });
}
const component$B = () => {
    return {
        view: vnode => {
            document.title = "Permissions - GenieACS";
            function showMore() {
                vnode.state["showCount"] =
                    (vnode.state["showCount"] || PAGE_SIZE$6) + PAGE_SIZE$6;
                M.redraw();
            }
            function onFilterChanged(filter) {
                const ops = { filter };
                if (vnode.attrs["sort"])
                    ops["sort"] = vnode.attrs["sort"];
                M.route.set(M.route.get(), ops);
            }
            const sort = vnode.attrs["sort"]
                ? memoizedJsonParse$6(vnode.attrs["sort"])
                : {};
            const sortAttributes = {};
            for (let i = 0; i < attributes$6.length; i++) {
                const attr = attributes$6[i];
                if (!(attr.id === "filter" || attr.id === "validate"))
                    sortAttributes[i] = sort[attr.id] || 0;
            }
            function onSortChange(sortAttrs) {
                const _sort = Object.assign({}, sort);
                for (const [index, direction] of Object.entries(sortAttrs)) {
                    // Changing the priority of columns
                    delete _sort[attributes$6[index].id];
                    _sort[attributes$6[index].id] = direction;
                }
                const ops = { sort: JSON.stringify(_sort) };
                if (vnode.attrs["filter"])
                    ops["filter"] = vnode.attrs["filter"];
                M.route.set(M.route.get(), ops);
            }
            let filter = vnode.attrs["filter"]
                ? memoizedParse$7(vnode.attrs["filter"])
                : true;
            filter = unpackSmartQuery$6(filter);
            const permissions = fetch("permissions", filter, {
                limit: vnode.state["showCount"] || PAGE_SIZE$6,
                sort: sort
            });
            const count$1 = count("permissions", filter);
            const downloadUrl = getDownloadUrl$6(filter);
            const valueCallback = (attr, permission) => {
                if (attr.id === "access") {
                    const val = permission["access"];
                    if (val === 1)
                        return "1: count";
                    else if (val === 2)
                        return "2: read";
                    else if (val === 3)
                        return "3: write";
                    return val;
                }
                return permission[attr.id];
            };
            const attrs = {};
            attrs["attributes"] = attributes$6;
            attrs["data"] = permissions.value;
            attrs["total"] = count$1.value;
            attrs["valueCallback"] = valueCallback;
            attrs["showMoreCallback"] = showMore;
            attrs["sortAttributes"] = sortAttributes;
            attrs["onSortChange"] = onSortChange;
            attrs["downloadUrl"] = downloadUrl;
            if (window.authorizer.hasAccess("permissions", 3)) {
                attrs["recordActionsCallback"] = permission => {
                    return [
                        M("button", {
                            title: "Delete permission",
                            onclick: () => {
                                if (!confirm(`Deleting ${permission._id} permission. Are you sure?`))
                                    return;
                                putActionHandler$6("delete", permission).catch(err => {
                                    push("error", err.message);
                                });
                            }
                        }, "✕")
                    ];
                };
                attrs["actionsCallback"] = (selected) => {
                    return [
                        M("button.primary", {
                            title: "Create new permission",
                            onclick: () => {
                                const cb = () => {
                                    return M(component$u, Object.assign({
                                        actionHandler: (action, object) => {
                                            return new Promise(resolve => {
                                                putActionHandler$6(action, object)
                                                    .then(errors => {
                                                    const errorList = errors
                                                        ? Object.values(errors)
                                                        : [];
                                                    if (errorList.length) {
                                                        for (const err of errorList)
                                                            push("error", err);
                                                    }
                                                    else {
                                                        close(cb);
                                                    }
                                                    resolve();
                                                })
                                                    .catch(err => {
                                                    push("error", err.message);
                                                    resolve();
                                                });
                                            });
                                        }
                                    }, formData$5));
                                };
                                open(cb);
                            }
                        }, "New"),
                        M("button.primary", {
                            title: "Delete selected permissions",
                            disabled: !selected.size,
                            onclick: e => {
                                if (!confirm(`Deleting ${selected.size} permissions. Are you sure?`))
                                    return;
                                e.redraw = false;
                                e.target.disabled = true;
                                Promise.all(Array.from(selected).map(id => deleteResource("permissions", id)))
                                    .then(res => {
                                    push("success", `${res.length} permissions deleted`);
                                    fulfill(0, Date.now());
                                })
                                    .catch(err => {
                                    push("error", err.message);
                                    fulfill(0, Date.now());
                                });
                            }
                        }, "Delete")
                    ];
                };
            }
            const filterAttrs = {};
            filterAttrs["resource"] = "permissions";
            filterAttrs["filter"] = vnode.attrs["filter"];
            filterAttrs["onChange"] = onFilterChanged;
            return [
                M("h1", "Listing permissions"),
                M(component$n, filterAttrs),
                M(component$m, attrs)
            ];
        }
    };
};

const permissionsPage = /*#__PURE__*/Object.freeze({
  init: init$b,
  component: component$B
});

/**
 * Copyright 2013-2019  GenieACS Inc.
 *
 * This file is part of GenieACS.
 *
 * GenieACS is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * GenieACS is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with GenieACS.  If not, see <http://www.gnu.org/licenses/>.
 */
const PAGE_SIZE$7 = config.ui.pageSize || 10;
const memoizedParse$8 = memoize(parse);
const memoizedJsonParse$7 = memoize(JSON.parse);
const attributes$7 = [
    { id: "_id", label: "Username" },
    { id: "roles", label: "Roles", type: "multi", options: [] }
];
const unpackSmartQuery$7 = memoize(query => {
    return map(query, e => {
        if (Array.isArray(e) && e[0] === "FUNC" && e[1] === "Q")
            return unpack("users", e[2], e[3]);
        return e;
    });
});
function putActionHandler$7(action, _object, isNew) {
    return new Promise((resolve, reject) => {
        const object = Object.assign({}, _object);
        if (action === "save") {
            const id = object["_id"];
            const password = object["password"];
            const confirm = object["confirm"];
            delete object["_id"];
            delete object["password"];
            delete object["confirm"];
            if (!id)
                return void resolve({ _id: "ID can not be empty" });
            if (isNew) {
                if (!password) {
                    return void resolve({ password: "Password can not be empty" });
                }
                else if (password !== confirm) {
                    return void resolve({
                        confirm: "Confirm password doesn't match password"
                    });
                }
            }
            if (!Array.isArray(object.roles) || !object.roles.length)
                return void resolve({ roles: "Role(s) must be selected" });
            object.roles = object.roles.join(",");
            resourceExists("users", id)
                .then(exists => {
                if (exists && isNew) {
                    fulfill(0, Date.now());
                    return void resolve({ _id: "User already exists" });
                }
                if (!exists && !isNew) {
                    fulfill(0, Date.now());
                    return void resolve({ _id: "User does not exist" });
                }
                putResource("users", id, object)
                    .then(() => {
                    if (isNew) {
                        changePassword(id, password)
                            .then(() => {
                            push("success", "User created");
                            fulfill(0, Date.now());
                            resolve();
                        })
                            .catch(reject);
                    }
                    else {
                        push("success", "User updated");
                        fulfill(0, Date.now());
                        resolve();
                    }
                })
                    .catch(reject);
            })
                .catch(reject);
        }
        else if (action === "delete") {
            deleteResource("users", object["_id"])
                .then(() => {
                push("success", "User deleted");
                fulfill(0, Date.now());
                resolve();
            })
                .catch(err => {
                fulfill(0, Date.now());
                reject(err);
            });
        }
        else {
            reject(new Error("Undefined action"));
        }
    });
}
const getDownloadUrl$7 = memoize(filter => {
    const cols = {};
    for (const attr of attributes$7)
        cols[attr.label] = attr.id;
    return `/api/users.csv?${M.buildQueryString({
        filter: stringify(filter),
        columns: JSON.stringify(cols)
    })}`;
});
function init$c(args) {
    if (!window.authorizer.hasAccess("users", 2)) {
        return Promise.reject(new Error("You are not authorized to view this page"));
    }
    const sort = args.hasOwnProperty("sort") ? "" + args["sort"] : "";
    const filter = args.hasOwnProperty("filter") ? "" + args["filter"] : "";
    return Promise.resolve({ filter, sort });
}
const component$C = () => {
    return {
        view: vnode => {
            document.title = "Users - GenieACS";
            function showMore() {
                vnode.state["showCount"] =
                    (vnode.state["showCount"] || PAGE_SIZE$7) + PAGE_SIZE$7;
                M.redraw();
            }
            function onFilterChanged(filter) {
                const ops = { filter };
                if (vnode.attrs["sort"])
                    ops["sort"] = vnode.attrs["sort"];
                M.route.set(M.route.get(), ops);
            }
            const sort = vnode.attrs["sort"]
                ? memoizedJsonParse$7(vnode.attrs["sort"])
                : {};
            const sortAttributes = {};
            for (let i = 0; i < attributes$7.length; i++) {
                const attr = attributes$7[i];
                if (attr.id !== "roles")
                    sortAttributes[i] = sort[attributes$7[i].id] || 0;
            }
            function onSortChange(sortAttrs) {
                const _sort = Object.assign({}, sort);
                for (const [index, direction] of Object.entries(sortAttrs)) {
                    // Changing the priority of columns
                    delete _sort[attributes$7[index].id];
                    _sort[attributes$7[index].id] = direction;
                }
                const ops = { sort: JSON.stringify(_sort) };
                if (vnode.attrs["filter"])
                    ops["filter"] = vnode.attrs["filter"];
                M.route.set(M.route.get(), ops);
            }
            let filter = vnode.attrs["filter"]
                ? memoizedParse$8(vnode.attrs["filter"])
                : true;
            filter = unpackSmartQuery$7(filter);
            const users = fetch("users", filter, {
                limit: vnode.state["showCount"] || PAGE_SIZE$7,
                sort: sort
            });
            const count$1 = count("users", filter);
            // Getting the roles
            const permissions = fetch("permissions", true);
            if (permissions.fulfilled) {
                for (const attr of attributes$7) {
                    if (attr.id === "roles")
                        attr.options = [...new Set(permissions.value.map(p => p.role))];
                }
            }
            const downloadUrl = getDownloadUrl$7(filter);
            const canWrite = window.authorizer.hasAccess("users", 3);
            const attrs = {};
            attrs["attributes"] = attributes$7;
            attrs["data"] = users.value;
            attrs["total"] = count$1.value;
            attrs["showMoreCallback"] = showMore;
            attrs["sortAttributes"] = sortAttributes;
            attrs["onSortChange"] = onSortChange;
            attrs["downloadUrl"] = downloadUrl;
            attrs["recordActionsCallback"] = user => {
                return [
                    M("a", {
                        onclick: () => {
                            const cb = () => {
                                const children = [
                                    M(component$u, Object.assign({
                                        base: {
                                            _id: user._id,
                                            roles: user.roles.split(",")
                                        },
                                        actionHandler: (action, object) => {
                                            return new Promise(resolve => {
                                                putActionHandler$7(action, object, false)
                                                    .then(errors => {
                                                    const errorList = errors
                                                        ? Object.values(errors)
                                                        : [];
                                                    if (errorList.length) {
                                                        for (const err of errorList)
                                                            push("error", err);
                                                    }
                                                    else {
                                                        close(cb);
                                                    }
                                                    resolve();
                                                })
                                                    .catch(err => {
                                                    push("error", err.message);
                                                    resolve();
                                                });
                                            });
                                        }
                                    }, {
                                        resource: "users",
                                        attributes: attributes$7
                                    }))
                                ];
                                if (canWrite) {
                                    children.push(M("hr"));
                                    const _attrs = {
                                        noAuth: true,
                                        username: user._id,
                                        onPasswordChange: () => {
                                            close(cb);
                                            M.redraw();
                                        }
                                    };
                                    children.push(M(component$i, _attrs));
                                }
                                return children;
                            };
                            open(cb);
                        }
                    }, "Show")
                ];
            };
            if (canWrite) {
                const formData = {
                    resource: "users",
                    attributes: [
                        attributes$7[0],
                        { id: "password", label: "Password", type: "password" },
                        { id: "confirm", label: "Confirm password", type: "password" },
                        attributes$7[1]
                    ]
                };
                attrs["actionsCallback"] = (selected) => {
                    return [
                        M("button.primary", {
                            title: "Create new user",
                            onclick: () => {
                                const cb = () => {
                                    return M(component$u, Object.assign({
                                        actionHandler: (action, object) => {
                                            return new Promise(resolve => {
                                                putActionHandler$7(action, object, true)
                                                    .then(errors => {
                                                    const errorList = errors
                                                        ? Object.values(errors)
                                                        : [];
                                                    if (errorList.length) {
                                                        for (const err of errorList)
                                                            push("error", err);
                                                    }
                                                    else {
                                                        close(cb);
                                                    }
                                                    resolve();
                                                })
                                                    .catch(err => {
                                                    push("error", err.message);
                                                    resolve();
                                                });
                                            });
                                        }
                                    }, formData));
                                };
                                open(cb);
                            }
                        }, "New"),
                        M("button.primary", {
                            title: "Delete selected users",
                            disabled: !selected.size,
                            onclick: e => {
                                if (!confirm(`Deleting ${selected.size} users. Are you sure?`))
                                    return;
                                e.redraw = false;
                                e.target.disabled = true;
                                Promise.all(Array.from(selected).map(id => deleteResource("users", id)))
                                    .then(res => {
                                    push("success", `${res.length} users deleted`);
                                    fulfill(0, Date.now());
                                })
                                    .catch(err => {
                                    push("error", err.message);
                                    fulfill(0, Date.now());
                                });
                            }
                        }, "Delete")
                    ];
                };
            }
            const filterAttrs = {};
            filterAttrs["resource"] = "users";
            filterAttrs["filter"] = vnode.attrs["filter"];
            filterAttrs["onChange"] = onFilterChanged;
            return [
                M("h1", "Listing users"),
                M(component$n, filterAttrs),
                M(component$m, attrs)
            ];
        }
    };
};

const usersPage = /*#__PURE__*/Object.freeze({
  init: init$c,
  component: component$C
});

/**
 * Copyright 2013-2019  GenieACS Inc.
 *
 * This file is part of GenieACS.
 *
 * GenieACS is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * GenieACS is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with GenieACS.  If not, see <http://www.gnu.org/licenses/>.
 */
class Authorizer {
    constructor(permissionSets) {
        this.permissionSets = permissionSets;
        this.validatorCache = new WeakMap();
        this.hasAccessCache = new Map();
    }
    hasAccess(resourceType, access) {
        const cacheKey = `${resourceType}-${access}`;
        if (this.hasAccessCache.has(cacheKey))
            return this.hasAccessCache.get(cacheKey);
        let has = false;
        for (const permissionSet of this.permissionSets) {
            for (const perm of permissionSet) {
                if (perm[resourceType]) {
                    if (perm[resourceType].access >= access) {
                        has = true;
                        break;
                    }
                }
            }
        }
        this.hasAccessCache.set(cacheKey, has);
        return has;
    }
    getValidator(resourceType, resource) {
        if (this.validatorCache.has(resource))
            return this.validatorCache.get(resource);
        const validators = [];
        for (const permissionSet of this.permissionSets) {
            for (const perm of permissionSet) {
                if (perm[resourceType] &&
                    perm[resourceType].access >= 3 &&
                    perm[resourceType].validate)
                    validators.push(perm[resourceType].validate);
            }
        }
        const validator = (mutationType, mutation, any) => {
            if (!validators.length)
                return false;
            const object = {
                mutationType,
                mutation,
                resourceType,
                object: resource,
                options: any
            };
            const valueFunction = (paramName) => {
                const entry = paramName.split(".", 1)[0];
                paramName = paramName.slice(entry.length + 1);
                let value = null;
                if (["mutation", "options"].includes(entry)) {
                    value = object[entry];
                    for (const seg of paramName.split(".")) {
                        // typeof null is "object"
                        if (value != null && typeof value !== "object")
                            value = null;
                        else
                            value = value[seg];
                        if (value == null)
                            break;
                    }
                }
                else if (object[entry]) {
                    value = object[entry][paramName];
                }
                return value;
            };
            const res = evaluate(validators.length > 1 ? ["OR", validators] : validators[0], valueFunction, Date.now());
            return !Array.isArray(res) && !!res;
        };
        this.validatorCache.set(resource, validator);
        return validator;
    }
}

/**
 * Copyright 2013-2019  GenieACS Inc.
 *
 * This file is part of GenieACS.
 *
 * GenieACS is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * GenieACS is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with GenieACS.  If not, see <http://www.gnu.org/licenses/>.
 */
window.authorizer = new Authorizer(window.permissionSets);
const adminPages$1 = [
    "presets",
    "provisions",
    "virtualParameters",
    "files",
    "config",
    "users",
    "permissions"
];
let state;
let lastRenderTimestamp = 0;
let pageVisitTimestamp = 0;
let fulfillTimeout = null;
function fulfill$1() {
    clearTimeout(fulfillTimeout);
    fulfillTimeout = setTimeout(() => {
        fulfill(lastRenderTimestamp, pageVisitTimestamp).then(updated => {
            if (updated)
                m.redraw();
        });
    }, 100);
}
function pagify(pageName, page) {
    const component = {
        render: () => {
            lastRenderTimestamp = Date.now();
            let p;
            if (state && state.error)
                p = m(component$q, state);
            else
                p = m(contextifyComponent(page.component), state);
            fulfill$1();
            const attrs = {};
            attrs["page"] = pageName;
            return m(component$4, attrs, p);
        },
        onmatch: null
    };
    component.onmatch = (args, requestedPath) => {
        pageVisitTimestamp = Date.now();
        if (!page.init) {
            state = null;
            fulfill$1();
            return null;
        }
        return new Promise(resolve => {
            page
                .init(args)
                .then(st => {
                if (!st)
                    return void m.route.set("/");
                state = st;
                resolve();
                fulfill$1();
            })
                .catch(err => {
                if (!window.username && err.message.indexOf("authorized") >= 0) {
                    push("error", err.message);
                    m.route.set("/login", { continue: requestedPath });
                }
                state = { error: err.message };
                resolve();
            });
        });
    };
    return component;
}
function redirectAdminPage() {
    const component = {
        onmatch: () => {
            for (const page of adminPages$1) {
                if (window.authorizer.hasAccess(page, 2)) {
                    m.route.set(`/admin/${page}`);
                    return null;
                }
            }
            return null;
        }
    };
    return component;
}
m.route(document.body, "/overview", {
    "/login": pagify("login", loginPage),
    "/overview": pagify("overview", overviewPage),
    "/devices": pagify("devices", devicesPage),
    "/devices/:id": pagify("devices", devicePage),
    "/faults": pagify("faults", faultsPage),
    "/map": pagify("map", mapPage),
    "/admin": redirectAdminPage(),
    "/admin/presets": pagify("presets", presetsPage),
    "/admin/provisions": pagify("provisions", provisionsPage),
    "/admin/virtualParameters": pagify("virtualParameters", virtualParametersPage),
    "/admin/files": pagify("files", filesPage),
    "/admin/config": pagify("config", configPage),
    "/admin/users": pagify("users", usersPage),
    "/admin/permissions": pagify("permissions", permissionsPage)
});
